// ---------------------------------------------------------------------------
// English and Arabic text.
//
// Both objects must have exactly the same keys. A test enforces this, so a
// missing Arabic string is caught before anybody sees an English word in the
// middle of an Arabic screen.
// ---------------------------------------------------------------------------

export const en = {
  common: {
    edit: 'Edit',
    remove: 'Remove',
    appName: 'Yaghi Butcher POS',
    businessName: 'Yaghi Butcher',
    loading: 'Loading…',
    retry: 'Try again',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
    /// The clear-this-field action, shared by every field that has one.
    clear: 'Clear',
    continue: 'Continue',
    search: 'Search',
    signOut: 'Sign out',
    signingOut: 'Signing out…',
    online: 'Online',
    offline: 'Offline',
    yes: 'Yes',
    no: 'No',
    none: 'None',
    never: 'Never',
    all: 'All',
    back: 'Back',
    language: 'Language',
    english: 'English',
    arabic: 'العربية',
    skipToContent: 'Skip to content',
    comingSoon: 'Not built yet',
    phaseLabel: 'Planned for {phase}',
  },

  login: {
    welcome: 'Welcome to Yaghi Butcher POS',
    subtitle: 'نظام مبيعات ملحمة ياغي',
    tagline: 'Sign in to start your shift',
    usernameLabel: 'Username',
    usernamePlaceholder: 'For example: cashier',
    usernameRequired: 'Please enter your username.',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Your password',
    passwordRequired: 'Please enter your password.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    withPassword: 'Password',
    withPin: 'Quick PIN',
    choosePerson: 'Choose your name',
    enterPin: 'Enter your PIN',
    pinFor: 'Signing in as {name}',
    changePerson: 'Choose someone else',
    clear: 'Clear',
    /// Shown ONLY while the request is in flight. The empty message below is
    /// a fact the server stated; this one is the absence of an answer, and
    /// showing the wrong one of the two is what made a shop with four
    /// employees read "nobody has a PIN".
    loadingEmployees: 'Loading employees…',
    employeesFailed: 'Employees could not be loaded. Try again.',
    noPinUsers: 'No employee has a Quick PIN yet. Please use a username and password.',
    footer: 'Secure · Fast · Reliable',
    footerAr: 'آمن · سريع · موثوق',
    offlineWarning: 'You appear to be offline. Signing in needs a connection to the shop server.',
    suspendedTitle: 'This account is suspended',
    lockedTitle: 'This account is temporarily locked',
    /// Counted down on screen. The SERVER decides when the lock actually
    /// ends; this only tells the person how long to wait.
    lockedBody: 'Too many failed attempts. Try again in {seconds} seconds.',
    lockedBodyOne: 'Too many failed attempts. Try again in 1 second.',
    lockedOver: 'You can try again now.',
  },

  nav: {
    dashboard: 'Dashboard',
    pos: 'Point of Sale',
    orders: 'Orders',
    menuOrders: 'Menu Orders',
    products: 'Products',
    inventory: 'Inventory',
    recipes: 'Recipes',
    production: 'Production',
    waste: 'Waste',
    purchases: 'Purchases',
    suppliers: 'Suppliers',
    customers: 'Customers',
    customerOrders: 'Delivery Accounts',
    customerCredit: 'Customer Credit',
    shifts: 'Shifts',
    myShift: 'My Shift',
    expenses: 'Expenses',
    reports: 'Reports',
    employees: 'Employees',
    auditLog: 'Audit Log',
    settings: 'Settings',
    offlineQueue: 'Waiting to Send',
    labels: 'Labels',
    importData: 'Import Data',
    backups: 'Backups',
    quickPricing: 'Quick Pricing',
    offers: 'Offers & Combos',
    groupPricing: 'Pricing & Offers',
    groupMenu: 'Digital menu',
    menuManagement: 'Menu management',
    groupSelling: 'Selling',
    groupStock: 'Stock',
    groupBuying: 'Buying',
    groupMoney: 'Money',
    groupAdmin: 'Administration',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
  },

  pricing: {
    title: 'Quick Pricing',
    subtitle: 'Change selling prices straight from the list. Enter or leaving the field saves.',
    searchPlaceholder: 'Search by name, code or barcode',
    allCategories: 'All categories',
    onlyWithOptions: 'Only products with options',
    onlyDiscounted: 'Only discounted',
    product: 'Product',
    code: 'Code',
    category: 'Category',
    unit: 'Unit',
    basePrice: 'Base price',
    effectivePrice: 'Effective',
    optionCount: '{count} options',
    optionCount_one: '1 option',
    parentNotSellable: 'Priced by its options',
    saving: 'Saving…',
    saved: 'Saved',
    failed: 'Not saved',
    retry: 'Retry',
    invalid: 'Whole lira above zero',
    conflict: 'This price changed elsewhere. The row has been refreshed - check it and try again.',
    readOnly: 'You may look at prices here, but not change them.',
    empty: 'No products match.',
    fromProduct: 'product discount',
    fromCategory: 'from the category',
    discountBadge: '{percent}% off',
    discountBadgeSource: '{percent}% off · {source}',
  },

  discountRule: {
    title: 'Discount',
    hint: 'A percentage off the base price. The base price itself never changes.',
    enabled: 'Discount enabled',
    percent: 'Percentage off',
    startsAt: 'Starts (optional)',
    endsAt: 'Ends (optional)',
    effective: 'Effective price',
    stateINACTIVE: 'Off',
    stateSCHEDULED: 'Scheduled',
    stateACTIVE: 'Active',
    stateEXPIRED: 'Expired',
    windowError: 'The discount must start before it ends.',
    percentError: 'Enter a percentage above 0 and below 100.',
    categoryHint: 'Applies to every product on this shelf that has no discount of its own. Discounts never add up.',
    noPermission: 'You do not have permission to change discounts.',
  },

  media: {
    choose: 'Choose an image',
    replace: 'Replace image',
    remove: 'Remove image',
    uploading: 'Uploading…',
    hint: 'JPG, PNG or WebP, up to 5 MB.',
    typeError: 'That file type is not supported. Use a JPG, PNG or WebP image.',
    sizeError: 'That image is larger than 5 MB. Please resize it and try again.',
    uploadFailed: 'The image could not be uploaded.',
  },

  overlay: {
    unsavedTitle: 'Unsaved changes',
    unsavedBody: 'You have unsaved changes. Leave without saving?',
    keepEditing: 'Keep editing',
    discard: 'Discard changes',
  },

  menu: {
    title: 'Menu management',
    subtitle: 'What the digital menu shows, what may be ordered, and the orders that come in. One product, one price - the menu only presents.',
    boxes: {
      status: 'Menu status',
      products: 'Menu products',
      categories: 'Menu Catalogue Management',
      promotions: 'Offers & home hero',
      orders: 'Menu orders',
      settings: 'Menu settings',
    },
    summary: {
      products: '{total} products · {visible} shown',
      categories: '{main} main · {sub} linked',
      promotions: '{active} offers live · {live} hero slides',
      offers: '{active} active · {scheduled} scheduled',
      newOrders: '{count} new orders',
      noNewOrders: 'No new orders',
      settings: 'Hours · ordering · WhatsApp',
    },
    status: {
      menu: 'Menu',
      store: 'Store',
      ordering: 'Ordering',
      whatsapp: 'WhatsApp',
      enabled: 'Enabled',
      disabled: 'Disabled',
      open: 'Open',
      closed: 'Closed',
      until: 'until {time}',
      opensAt: 'opens {day} {time}',
      configured: 'Configured',
      notConfigured: 'Not configured',
      accepting: 'Accepting orders',
      notAccepting: 'Not accepting orders',
      manualOff: 'switched off by hand',
      delivery: 'Delivery',
      pickup: 'Pickup',
      timezone: 'Business time',
      refreshed: 'As of',
    },
    products: {
      search: 'Search by name, code or barcode',
      allShelves: 'All menu categories',
      filters: { all: 'All', visible: 'Shown', hidden: 'Hidden' },
      product: 'Product',
      code: 'Code',
      shelf: 'Menu category',
      price: 'Current price',
      visibility: 'Shown on the menu',
      showOnMenu: 'Show on menu',
      visible: 'Shown',
      hidden: 'Hidden',
      status: 'On the menu',
      noShelf: 'Not linked to the menu',
      options: '{count} options',
      optionRetired: 'retired',
      saveFailed: 'Not saved',
      derived: {
        onMenu: 'On the menu',
        hidden: 'Hidden by you',
        unlinked: 'Not shown: its POS category is on no menu category',
        linkedHidden: 'Not shown: its menu category is hidden',
        inactive: 'Not shown: the product is retired on the till',
        noOptions: 'Shown as unavailable: every option is retired',
      },
      linkHint: 'Link this POS category from Menu categories.',
      pageOf: '{from}-{to} of {total}',
      previous: 'Previous',
      next: 'Next',
      hint: 'The picture and the price are the product\'s own; change them in Products or Quick Pricing. Nothing here affects the till, the stock or the printers.',
      empty: 'No products match.',
    },
    categories: {
      hint: 'How a customer browses: a main category you name, and under it the REAL POS categories you link to it - their products follow automatically. Nothing here touches the till, the stock or the printers.',
      add: 'Add category',
      addMain: 'Add main category',
      addSub: 'Link POS categories',
      linkTitle: 'Link POS categories under «{name}»',
      linkHint: 'Tick the POS categories this main category shows. A POS category is on the menu in one place; one already linked elsewhere says where.',
      linkedElsewhere: 'Linked under «{name}»',
      linkedHere: 'Linked here',
      linkSearch: 'Search POS categories',
      posCategory: 'POS category',
      displayName: 'Customer-facing name',
      unlink: 'Unlink',
      unlinkHint: 'Unlinking takes its products off the menu until it is linked again; nothing changes on the till.',
      noLinks: 'No POS categories linked yet - link some so products appear.',
      edit: 'Edit category',
      nameAr: 'Name (Arabic)',
      nameEn: 'Name (English)',
      visible: 'Shown on the menu',
      sortOrder: 'Order',
      moveUp: 'Move up',
      moveDown: 'Move down',
      products: '{count} products',
      subcategories: '{count} subcategories',
      empty: 'No menu categories yet.',
      saved: 'The menu category has been saved.',
      level: 'Level',
      mainLevel: 'Main category',
      subLevel: 'Subcategory',
      parent: 'Under main category',
      source: 'Shows the POS category',
      cover: 'Cover picture',
      coverHint: 'Shown on the menu home. Without one, the menu draws a tasteful placeholder.',
      hiddenByParent: 'Hidden with its main category',
      hidden: 'Hidden',
      retired: 'Retired',
      retire: 'Retire',
      restore: 'Restore',
      catalogueSearch: 'Search products, SKU, barcode or category',
      deleteParent: 'Delete empty parent',
      deleteBlocked: 'Move linked categories before deleting',
      deleteExplanation: 'Delete this empty Menu presentation category? POS categories and products will not be changed.',
      retireHint: 'Retiring hides a category. Only an empty parent can be deleted; POS products and categories are never deleted here.',
      linkedCount: '{count} linked',
      linkedOne: '1 linked',
      productOne: '1 product',
      linkPos: 'Link a POS category',
      actions: 'Actions',
      changeCover: 'Change cover',
      hide: 'Hide',
      show: 'Show',
      editDisplayName: 'Edit display name',
      move: 'Move to another main category',
      expand: 'Expand',
      collapse: 'Collapse',
      reorderHandle: 'Drag to reorder - or use the arrow keys',
      reorderHint: 'Hold ≡ and drag to reorder. With the keyboard: focus ≡ and press the arrow keys.',
      reorderFailed: 'The order was not saved - the previous order is back.',
      reordered: 'The order has been saved.',
      shown: 'Shown',
      hiddenState: 'Hidden',
      posProducts: '{count} products on the till',
      hasChildren: 'Has subcategories - move them first to make it a subcategory.',
      uncategorised: 'Products with no category: {count}',
    },
    hero: {
      hint: 'The rotating hero at the top of the menu home: the brand, a discounted product, an offer. Every price, picture and discount is read from the product or the offer itself - nothing is copied here, so a quick-pricing change is on the hero at once.',
      add: 'Add slide',
      addBrand: 'Brand slide',
      addProduct: 'Discounted product',
      addCombo: 'Offer / combo',
      kind: { BRAND: 'Brand', PRODUCT: 'Product', COMBO: 'Offer' },
      titleAr: 'Title (Arabic)',
      titleEn: 'Title (English)',
      subtitleAr: 'Subtitle (Arabic)',
      subtitleEn: 'Subtitle (English)',
      ctaTextAr: 'Button (Arabic)',
      ctaTextEn: 'Button (English)',
      ctaTarget: 'The button opens',
      ctaNone: 'Nothing - just the words',
      ctaCategory: 'A menu category',
      ctaProduct: 'A product',
      ctaCombo: 'An offer',
      image: 'Picture',
      enabled: 'Enabled',
      startsAt: 'From',
      endsAt: 'Until',
      sortOrder: 'Order',
      pickProduct: 'Choose a discounted product',
      pickCombo: 'Choose an active offer',
      noCandidates: 'No discounted, orderable products right now - set a discount in Products first.',
      noCombos: 'No active offers right now.',
      readOnlyProduct: 'Read from the product. Change its price or discount in Products.',
      readOnlyCombo: 'Read from the offer. Change it in Offers & combos.',
      preview: 'Preview',
      remove: 'Remove from hero',
      removed: 'The slide has been removed.',
      saved: 'The slide has been saved.',
      empty: 'No slides yet: the menu shows the built-in brand hero.',
      limits: 'Up to {brand} brand slides and {promo} promotions can be live at once.',
      lifecycle: { ACTIVE: 'Live', SCHEDULED: 'Scheduled', EXPIRED: 'Ended', DISABLED: 'Off' },
      exclusion: {
        PRODUCT_MISSING: 'the product no longer exists',
        PRODUCT_HIDDEN: 'the product is hidden from the menu',
        PRODUCT_UNAVAILABLE: 'the product is unavailable',
        NOT_DISCOUNTED: 'the product has no active discount',
        COMBO_MISSING: 'the offer no longer exists',
        COMBO_NOT_ACTIVE: 'the offer is not active',
        COMBO_HIDDEN: 'the offer is hidden from the menu',
      },
      notShowing: 'Not showing: {reason}',
      confirmRemove: 'Remove this slide from the hero?',
      window: 'Window',
      always: 'Always',
    },
    offers: {
      hint: 'The same combos the till sells - price, dates, picture and contents are edited in Offers & Combos. Here you only choose where the menu shows them.',
      showOnMenu: 'Show on menu',
      showInHero: 'Show in hero',
      shown: 'Shown',
      inHero: 'In hero',
      notInHero: 'Not in hero',
      heroOrder: 'Hero order',
      open: 'Open Offers & Combos',
      empty: 'No offers yet.',
      sections: { combos: 'Offers & combos', products: 'Discounted products', brand: 'Brand slides' },
      productsHint: 'Products the engine discounts right now. Put one on the hero; its price, picture and discount are read from the product.',
      brandHint: "The shop's own slide: words and a picture, a button, a window.",
      noDiscounted: 'No discounted, shown products right now - set a discount in Products first.',
      state: { working: 'Live', paused: 'Paused', scheduled: 'Scheduled', expired: 'Ended', draft: 'Draft', componentRetired: 'A component is retired' },
      preview: 'Preview',
      previewMenu: 'Menu card',
      previewHero: 'Hero slide',
      previewState: { onMenu: 'On the menu now', hidden: 'Not on the menu: hidden', paused: 'Not on the menu: paused', scheduled: 'Not on the menu yet: scheduled', expired: 'Not on the menu: ended', unavailable: 'Shown as unavailable: a component is retired', inactive: 'Not on the menu: retired', unlinked: 'Not on the menu: POS category not linked', notDiscounted: 'Not on the hero: no active discount', notShowing: 'Not on the hero: {reason}' },
    },
    orders: {
      tabs: { pending: 'New', confirmed: 'Confirmed', rejected: 'Rejected', all: 'All' },
      pageTitle: 'Menu Orders',
      pageSubtitle: 'Orders from the digital menu. Confirming one opens it in the till.',
      searchPlaceholder: 'Search by order number, name or phone',
      noSearchResults: 'No order matches that search.',
      selectPrompt: 'Choose an order to see it.',
      empty: 'No orders here.',
      emptyPending: 'No new orders. Orders from the digital menu appear here the moment they arrive.',
      delivery: 'Delivery',
      pickup: 'Pickup',
      items: '{count} items',
      view: 'View order',
      confirm: 'Confirm order',
      reject: 'Reject order',
      rejectTitle: 'Reject this order?',
      rejectReason: 'Reason (optional)',
      reasons: { unavailable: 'Item unavailable', area: 'Outside the delivery area', unreachable: 'Customer unreachable', invalid: 'Invalid order', other: 'Other' },
      customer: 'Customer',
      phone: 'Phone',
      address: 'Address',
      area: 'Area',
      street: 'Street',
      building: 'Building',
      floor: 'Floor',
      landmark: 'Landmark',
      notes: 'Customer notes',
      subtotal: 'Subtotal',
      deliveryFee: 'Delivery fee',
      total: 'Total',
      submittedAt: 'Received',
      confirmedAt: 'Confirmed',
      confirmedBy: 'Confirmed by',
      rejectedBy: 'Rejected by',
      status: { PENDING_CONFIRMATION: 'Awaiting confirmation', CONFIRMED: 'Confirmed', REJECTED: 'Rejected', CANCELLED: 'Cancelled' },
      posOrder: 'POS order',
      currentSaleOrigin: 'Menu order',
      frozenSale: 'Accepted items and prices are fixed. Take payment when ready.',
      preserveFirst: 'Finish or hold the current sale before switching. Its items and customer details are still here.',
      leaveSale: 'Set aside — keep in Orders',
      leaveSaleShort: 'Set aside',
      collect: 'Collect payment',
      confirmed: 'The order has been confirmed.',
      alreadyConfirmed: 'This order was already confirmed.',
      newArrival: 'New menu order',
      feePending: 'Delivery fee will be confirmed in POS and added to the final bill.',
      confirmFee: 'Confirm fee and dispatch',
      feeSentToAccounts: 'Delivery fee confirmed. The order is now in Delivery Accounts, to be collected when the driver returns.',
      usdChange: 'Please bring change for {amount}$ (customer request, not payment).',
      rejected: 'The order has been rejected.',
      printing: 'Printing the receipt and the preparation tickets…',
      whatsapp: 'Open WhatsApp chat',
      discount: '{percent}% off',
      combo: 'Combo',
      menuNumber: 'Menu order',
      openInPos: 'Open in POS',
      reviewTitle: 'Order from the menu',
      reviewHint: 'Items and prices are frozen as the customer sent them. Review, then confirm or reject.',
      frozen: 'This is a menu order under review: items cannot be added or changed. Confirm it or reject it.',
      exitReview: 'Leave review',
      notPending: 'This order is no longer awaiting confirmation.',
    },
    settings: {
      business: 'Business',
      businessNameAr: 'Business name (Arabic)',
      businessNameEn: 'Business name (English)',
      phone: 'Phone',
      address: 'Address',
      locationUrl: 'Map link (https)',
      whatsapp: 'WhatsApp',
      whatsappNumber: 'Ordering WhatsApp number',
      whatsappHint: 'Read by the future menu from the database - change it here, never in code.',
      menu: 'Menu',
      menuEnabled: 'Menu browsing enabled',
      orderingEnabled: 'Online ordering enabled',
      orderingHint: 'A manual switch on top of the ordering hours. Off means browsing only.',
      defaultLanguage: 'Default language',
      arabicEnabled: 'Arabic',
      englishEnabled: 'English',
      showUsd: 'Show USD beside LBP',
      orderTypes: 'Order types',
      deliveryEnabled: 'Delivery',
      pickupEnabled: 'Pickup',
      deliveryFeeLbp: 'Delivery fee for online orders (LBP)',
      deliveryFeeHint: 'Frozen onto every online delivery when it is placed.',
      timezone: 'Business timezone',
      businessHours: 'Business hours',
      orderingHours: 'Online ordering hours',
      hoursHint: 'The store may be open while ordering is not. Times are in the business timezone.',
      closed: 'Closed',
      addInterval: 'Add hours',
      removeInterval: 'Remove',
      from: 'From',
      to: 'To',
      days: { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' },
      saved: 'The menu settings have been saved.',
      languagesError: 'At least one language must stay enabled.',
    },
  },

  offers: {
    title: 'Offers & Combos',
    subtitle: 'Bundles sold at one price. Stock and preparation still come from the real products.',
    create: 'Create offer / combo',
    edit: 'Edit offer',
    tabAll: 'All',
    tabActive: 'Working',
    tabScheduled: 'Scheduled',
    tabExpired: 'Expired',
    tabDraft: 'Draft',
    tabDisabled: 'Paused',
    browse: 'Browse by POS category',
    browseHint: "Tick the products the bundle includes - by the till's own categories. Search is a shortcut to the same list.",
    picked: 'In the bundle',
    menuLine: 'Menu',
    menuShown: 'shown',
    menuHidden: 'hidden',
    quoteLoading: 'Working out the regular total…',
    empty: 'No offers yet.',
    emptyHint: 'Create a combo to sell a bundle at one price.',
    nameAr: 'Name (Arabic)',
    nameEn: 'Name (English)',
    image: 'Photograph',
    offerPrice: 'Offer price',
    regularTotal: 'Regular total',
    savings: 'Savings',
    startsAt: 'Starts (optional)',
    endsAt: 'Ends (optional)',
    components: 'Components',
    addComponent: 'Add product',
    componentSearch: 'Search products by name, code or barcode',
    quantity: 'Quantity',
    chooseOption: 'Choose the option',
    remove: 'Remove',
    noComponents: 'Add at least one product.',
    priceTooHigh: 'The offer price must be below the regular total to go live.',
    saveDraft: 'Save as draft',
    publish: 'Publish',
    disable: 'Disable',
    enable: 'Publish',
    lifecycleDRAFT: 'Draft',
    lifecycleSCHEDULED: 'Scheduled',
    lifecycleACTIVE: 'Working',
    lifecycleEXPIRED: 'Ended',
    lifecycleDISABLED: 'Paused',
    unavailable: 'A component is retired on the till',
    created: 'The offer has been created.',
    saved: 'The offer has been saved.',
    stateChanged: 'The offer has been updated.',
    comboBadge: 'Combo',
    offersTab: 'Offers',
    inCombo: 'in combo',
    comboLine: 'Combo',
    noPermission: 'You may look at offers here, but not change them.',
    required: 'Required',
  },

  offerPoster: {
    open: 'Poster',
    title: 'Offer poster',
    subtitle: 'Copy the description into your image AI, then upload the picture it makes - the till writes the name and the prices on it, in Arabic.',
    step1: '1. Describe the picture',
    step2: '2. Add the words and prices',
    style: 'Style',
    styleslate: 'Dark slate & marble',
    stylewood: 'Rustic wood',
    stylestudio: 'Clean studio',
    stylefestive: 'Ramadan & Eid',
    look: 'Food',
    lookraw: 'Fresh, raw',
    lookserved: 'Cooked & served',
    target: 'Image AI',
    targetchatgpt: 'ChatGPT / Gemini',
    targetmidjourney: 'Midjourney',
    format: 'Size',
    formatsquare: 'Post 1:1',
    formatstory: 'Story 9:16',
    prompt: 'Description for the image AI',
    copy: 'Copy description',
    copied: 'Description copied.',
    copyFailed: 'Could not copy - select the text and copy it by hand.',
    references: 'Attach these photos with the description - the AI is told to keep your products exactly as they are and change only the setting around them:',
    referenceDownload: 'Download photo: {name}',
    noText: 'The description asks for a picture with no writing at all - image AIs misspell Arabic and prices. The till adds the words in the next step.',
    upload: 'Upload the AI picture',
    replace: 'Change picture',
    removePicture: 'Remove picture',
    noPicture: 'No picture yet - the preview shows the layout on a dark background.',
    phone: 'How to order (shown at the bottom)',
    preview: 'Poster preview',
    printed: 'Written on the poster',
    download: 'Download poster',
    share: 'Share',
    shareFailed: 'Could not share - download it instead.',
    noRate: 'No exchange rate is set, so the poster shows lira only.',
    productsTitle: 'Discounted products',
    productsHint: 'Products and options on discount right now.',
    productsEmpty: 'No product is on discount right now.',
    posterSave: 'Save {percent}%',
    posterUntil: 'Offer valid until {date}',
    posterPhone: 'Order on WhatsApp: {phone}',
  },

  header: {
    currentUser: 'Signed in as',
    role: 'Role',
    shiftOpen: 'Shift open',
    shiftClosed: 'No open shift',
    shiftNumber: 'Shift {number}',
    exchangeRate: 'Rate',
    exchangeRateFull: '1 USD = {rate} LBP',
    noRate: 'No rate set',
    openMenu: 'Open the user menu',
    notifications: 'Notifications',
    noNotifications: 'Nothing new',
  },

  dashboard: {
    title: 'Dashboard',
    greeting: 'Good day, {name}',
    activeProducts: 'Active products',
    activeCategories: 'Categories',
    activeEmployees: 'Active employees',
    openShifts: 'Open shifts',
    lowStockItems: 'Low stock items',
    stockValue: 'Stock value at cost',
    lowStockTitle: 'Products at or below their minimum',
    lowStockEmpty: 'Every tracked product is above its minimum level.',
    recentActivity: 'Recent activity',
    recentActivityEmpty: 'Nothing has been recorded yet.',
    notBuiltTitle: 'What is not built yet',
    notBuiltIntro:
      'These panels are deliberately absent rather than showing zeros that could be mistaken for real figures.',
    minimum: 'Minimum',
    current: 'Current',
  },

  employees: {
    title: 'Employees',
    subtitle: 'Everyone who can sign in to the system.',
    name: 'Name',
    username: 'Username',
    role: 'Role',
    status: 'Status',
    pin: 'Quick PIN',
    lastLogin: 'Last signed in',
    overrides: 'Permission exceptions',
    active: 'Active',
    suspended: 'Suspended',
    readOnlyNotice:
      'This list is read-only in this phase. Adding, editing and suspending employees, and setting per-person permission exceptions, comes in a later phase.',
  },

  permissions: {
    title: 'Roles and permissions',
    subtitle: 'Exactly what each role is allowed to do.',
    role: 'Role',
    people: '{count} people',
    sensitive: 'Sensitive',
    sensitiveHint: 'Shows money, cost or profit information.',
    granted: 'Granted',
    notGranted: 'Not granted',

    effectiveTitle: 'What this person can do',
    effectiveBody:
      'Ticked means they can do it. Their role decides the starting point; anything you change from it is remembered against them, and a role change later still moves everybody following it.',
    roleSummary: 'Role: {role}',

    ownerAlwaysFull: 'Owners always have full access to the system.',

    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    selectAll: 'Select all',
    clearAll: 'Clear all',
    resetToRole: 'Reset to role defaults',

    save: 'Save changes',
    saved: 'Permissions saved.',
    unsaved: 'Unsaved changes',

    /// Said beside a read the till cannot work without. Shown rather than
    /// hidden: an administrator who cannot see why Products is 1/5 will try
    /// to "fix" it.
    requiredByPos: 'Required by Point of Sale',
    /// Named for whichever module owes it, so the same badge serves any
    /// module that gains a dependency later.
    requiredBy: 'Required by {module}',
    dependencyLocked:
      'This is needed for the module above to work. Turn the module off to remove it.',
    actionsLabel: 'What they can do inside',
    /// The whole-group checkbox. It needs a name of its own because a group
    /// and one of its modules can share a label - Stock holds Inventory, and
    /// both are "المخزون" in Arabic - and two checkboxes reading the same
    /// leaves a screen reader with no way to tell them apart.
    groupAll: 'All of {group}',
    removeModuleTitle: 'Turn off {module}?',
    removeModuleBody: 'These go with it, and will have to be given back one by one:',
    removeModuleConfirm: 'Turn it off',

    fromRole: 'from role',
    customAllowed: 'custom',
    customDenied: 'removed',
  },

  audit: {
    title: 'Audit log',
    subtitle: 'A permanent record of important actions. Entries can never be edited or deleted.',
    when: 'When',
    who: 'Who',
    action: 'Action',
    entity: 'Item',
    reason: 'Reason',
    ip: 'Address',
    empty: 'No activity has been recorded yet.',
    system: 'System',
  },

  errors: {
    genericTitle: 'Something went wrong',
    networkTitle: 'The server could not be reached',
    networkBody: 'Check that the shop server is running, then try again. Nothing was saved.',
    notFoundTitle: 'Page not found',
    notFoundBody: 'That page does not exist. It may have been moved or removed.',
    forbiddenTitle: 'You do not have permission to view this page',
    forbiddenBody:
      'Your account does not include access to this part of the system. If you believe you need it, ask the owner or a manager.',
    goToStart: 'Go to my start page',
    signInAgain: 'Sign in again',
    sessionExpiredTitle: 'Your session has ended',
    sessionExpiredBody: 'For security, sessions end after a period of inactivity. Please sign in again.',
    unexpectedTitle: 'The screen could not be displayed',
    unexpectedBody: 'Something unexpected happened. Reloading the page usually fixes it.',
    reload: 'Reload the page',
  },

  status: {
    inStock: 'In stock',
    lowStock: 'Low',
    outOfStock: 'Out of stock',
    notTracked: 'Not tracked',
  },

  phases: {
    phase2: 'Phase 2',
    phase3: 'Phase 3',
    phase4: 'Phase 4',
    phase5: 'Phase 5',
    phase6: 'Phase 6',
    phase7: 'Phase 7',
    phase8: 'Phase 8',
    phase9: 'Phase 9',
    notBuiltHeading: 'This screen is not built yet',
    notBuiltBody:
      'It is planned for {phase}. It appears in the menu only because your account will have access once it exists.',
    whatWillBeHere: 'What will be here',
  },

  products: {
    title: 'Products',
    subtitle: 'Everything the shop sells.',
    tabProducts: 'Products',
    tabCategories: 'Categories',
    add: 'Add product',
    edit: 'Edit product',
    searchPlaceholder: 'Search by name, code or barcode',
    /// Said under a chosen product. A quantity means nothing without it: 2 is
    /// two kilos of lamb or two bottles of water.
    unit: 'Unit',
    allCategories: 'All categories',
    showInactive: 'Include hidden',
    noProducts: 'No products match what you are looking for.',
    noProductsHint: 'Try a different search, or clear the category filter.',
    empty: 'No products yet.',
    emptyHint: 'Add your first product to start selling.',

    nameEn: 'Name (English)',
    nameAr: 'Name (Arabic)',
    sku: 'Product code',
    skuHint: 'Numbered automatically from the category. It never changes.',
    numbersOnSave: 'Assigned automatically when you save.',
    chooseCategoryFirst: 'Choose a category to see the next number.',
    barcode: 'Barcode',
    barcodeHint: 'Numbered automatically across the shop, six digits.',
    category: 'Category',
    image: 'Photograph',
    imageHint: 'JPG, PNG or WebP, up to 5 MB.',
    uploadImage: 'Choose an image',
    uploading: 'Uploading…',
    removeImage: 'Remove image',
    sellingUnit: 'Sold by',

    // --- where a product sits on the till, inside its category ------------
    displayOrder: 'Display order',
    displayOrderHint: 'Controls where this product appears inside its category.',

    // --- the choice the cashier must make before selling --------------------
    //
    // The KEYS still say "variant", because the table, the API and every id in
    // the system do, and renaming those would be a migration for the sake of a
    // word. What a person READS is generic: tawook comes in types, chips come
    // in sizes, sauce comes in flavours, and the shop decides which of those
    // it is per product.
    variants: 'Options',
    variantsHint:
      'Optional. Use this when the cashier must choose - a size, a type, a flavour - before selling.',
    hasVariants: 'This product has options',
    variantAdd: 'Add option',
    variantRemove: 'Remove option',
    variantNameAr: 'Arabic name',
    variantNameEn: 'English name',
    variantActive: 'Active',
    variantNeedOne: 'Add at least one active option, or turn options off.',
    variantDuplicate: 'This name is already used by another option.',
    /// Shown when a LIVING option of the same product already has the name.
    /// A retired one does not reserve it.
    optionNameTaken: 'An active option with this name already exists for this product.',
    variantNameRequired: 'Required.',
    variantPriceNote: 'Each option has its own selling price. The price above is not used for sales.',
    /// Shown INSTEAD of the parent price box once options are switched on.
    priceFromOptions: 'Set by the options below.',
    variantRequired: 'Please choose an option.',
    variantUnavailable: 'This option is no longer available.',
    variantLabel: 'Option',
    variantCount_one: '1 option',
    variantCount_other: '{count} options',

    // --- what the choice IS -------------------------------------------------
    //
    // Stored on the product in both languages, so the till and the paper can
    // say "Choose size" and "Size: Large" rather than assuming everything is a
    // type. `{label}` is filled in with whatever that product uses.
    optionLabelField: 'What is the choice?',
    optionLabelHint: 'Shown to the cashier as "Choose size", "Choose type", and printed on the receipt.',
    /// The preset WORDS live in `utils/optionLabel`, not here: they are the
    /// values a product stores, not interface text, and the dropdown must
    /// offer exactly what will be printed.
    optionPresetCustom: 'Something else',
    optionLabelAr: 'Arabic wording',
    optionLabelEn: 'English wording',
    optionLabelRequired: 'Required.',
    chooseOption: 'Choose {label}',
    displayOrderInvalid: 'Enter a whole number of 1 or more.',
    displayOrderDuplicate: 'This display order is used more than once.',
    reorderProducts: 'Arrange products',
    reorderHint: 'Number the products the way they should appear on the till. Leaving gaps — 10, 20, 30 — makes it easy to slot something in later.',
    reorderSaved: 'The order has been saved.',
    reorderEmpty: 'This category has no products to arrange yet.',
    costPrice: 'Cost price',
    costPriceHint: 'What the shop pays. Never shown to a cashier.',
    sellingPrice: 'Selling price',
    wholesalePrice: 'Wholesale price',
    minSellingPrice: 'Minimum selling price',
    minSellingPriceHint: 'Optional. Selling below this will need approval.',
    trackStock: 'Track stock for this product',
    currentStock: 'Opening stock',
    currentStockHint: 'Set once, when the product is created.',
    stockLockedHint:
      'Stock is changed by recording an inventory movement, not by editing this form. That arrives in Phase 4.',
    minStock: 'Low-stock warning level',
    preparationNotes: 'Preparation notes',
    preparationNotesHint: 'Printed on the preparation ticket, for example: trim the fat cap.',
    active: 'Available for sale',
    priceChangeReason: 'Reason for the price change',
    priceChangeReasonHint: 'Recorded in the price history and the audit log.',
    priceHistory: 'Price history',
    noPriceHistory: 'No price changes recorded yet.',
    from: 'From',
    until: 'Until',
    current: 'Current',
    changedBy: 'Changed by',

    perUnit: 'per {unit}',
    status: 'Status',
    hidden: 'Hidden',
    onSale: 'On sale',
    price: 'Price',
    cost: 'Cost',
    margin: 'Margin',
    stock: 'Stock',
    costHidden: 'You do not have permission to see cost prices.',

    deleteTitle: 'Remove this product?',
    deleteMessage:
      'It will disappear from the shop and the POS. Past records that mention it stay readable. This can be undone only by a developer.',
    deleteConfirm: 'Remove product',
    created: 'The product has been created.',
    saved: 'The product has been saved.',
    deleted: 'The product has been removed.',
  },

  categories: {
    title: 'Categories',
    add: 'Add category',
    edit: 'Edit category',
    nameEn: 'Name (English)',
    nameAr: 'Name (Arabic)',
    slug: 'Code',
    slugHint: 'Numbered automatically: cat-001, cat-002... It never changes.',
    codePrefix: 'Product code prefix',
    codePrefixHint: '2 to 5 letters every product code here starts with: BEF gives BEF-001. Leave empty to choose one from the name. A change applies to products added afterwards.',
    color: 'Colour',
    sortOrder: 'Order',
    productCount: 'Products',
    empty: 'No categories yet.',
    deleteTitle: 'Remove this category?',
    deleteMessage: 'Only a category with no products can be removed.',
    deleteConfirm: 'Remove category',
    created: 'The category has been created.',
    saved: 'The category has been saved.',
    deleted: 'The category has been removed.',
    stillHasProducts: 'Move its products to another category first.',
  },

  pos: {
    title: 'Point of Sale',
    // --- customers and credit, Phase 7 ---
    chooseCustomer: 'Choose a customer',
    changeCustomer: 'Change',
    customerSearch: 'Search by name, phone or code',
    noCustomerChosen: 'Walk-in customer',
    creditNeedsCustomer: 'Choose the customer whose account this goes on.',
    creditNotEnough: 'They only have {available} of credit left.',
    creditNoChange: 'A sale on account cannot give change. Put only what is still owed on it.',
    goingOnAccount: '{amount} is going on {name}\'s account.',
    customerBalance: 'On account',
    searchPlaceholder: 'Search or scan a barcode',
    /// Said beside the counter-sale fee field, so nobody thinks it is owed.
    feeOptional: '(optional)',
    feeTooLarge: 'That fee looks wrong. Check the amount.',
    feeNegative: 'A fee cannot be negative.',
    allProducts: 'All',
    noResults: 'Nothing matches that search.',
    noResultsHint: 'Check the spelling, or choose another category.',
    outOfStock: 'Out of stock',
    lowStock: 'Low',

    cart: 'Current order',
    cartEmpty: 'The order is empty.',
    cartEmptyHint: 'Choose a product on the left to begin.',
    items: '{count} items',
    oneItem: '1 item',
    subtotal: 'Subtotal',
    totalWeight: 'Total weight',
    clearCart: 'Clear the order',
    clearCartTitle: 'Clear the whole order?',
    clearCartMessage: 'Every line will be removed. This cannot be undone.',
    clearCartConfirm: 'Clear order',
    removeLine: 'Remove this line',
    editLine: 'Edit',
    addNote: 'Add a note',
    lineNote: 'Note for this item',
    lineNotePlaceholder: 'For example: thin slices, no fat',
    orderNote: 'Note for the whole order',
    orderNotePlaceholder: 'For example: deliver after 6pm',

    enterWeight: 'Enter the weight',
    enterQuantity: 'Enter the quantity',
    enterAmount: 'Enter the amount',
    weightKg: 'Kilograms',
    weightG: 'Grams',
    quantity: 'Quantity',
    amountUsd: 'Amount in USD',
    addToOrder: 'Add to the order',
    saveChanges: 'Save changes',
    keypadClear: 'Clear',
    convertedTo: 'That is {kg} kg',
    lineTotalPreview: 'Line total',
    weightBasePrice: 'Base price',
    weightEffectivePrice: 'After discount',

    errorEmpty: 'Please enter a number.',
    errorNotANumber: 'That is not a valid number.',
    errorZero: 'The amount must be greater than zero.',
    errorWholeNumber: 'This product is sold by the piece, so enter a whole number.',
    errorTooLarge: 'That looks too large. Please check the entry.',

    printPreview: 'Print',

    // --- responsive layout ---
    scrollCategoriesBack: 'Show earlier categories',
    scrollCategoriesForward: 'Show later categories',
    openCart: 'Open the current order',
    closeCart: 'Close the current order',
    cartSummary: '{count} items · {total}',
    cartSummaryOne: '1 item · {total}',
    cartEmptySummary: 'The order is empty',
  },

  receipt: {
    onlineSource: 'Online menu order',
    onlineCustomer: 'Name',
    onlinePhone: 'Phone',
    onlineAddress: 'Address',
    onlineBuilding: 'Building / floor',
    cashier: 'Cashier',
    date: 'Date',
    item: 'Item',
    total: 'Total',
    grandTotal: 'TOTAL',
    inLbp: 'Total in LBP',
    rate: 'Exchange rate',
    discount: 'Discount',
    // The thank-you line is not here: it comes from the shop's own
    // `business.receipt_footer` setting, so the owner can word it themselves
    // in whichever languages they want.
    noRate: 'No exchange rate has been set, so the LBP total cannot be shown.',
  },

  units: {
    KG: 'Kilogram',
    GRAM: 'Gram',
    PIECE: 'Piece',
    TRAY: 'Tray',
    PACKAGE: 'Package',
    BOX: 'Box',
    MEAL: 'Fixed-price item',
    CUSTOM_AMOUNT: 'Custom amount',
  },

  shift: {
    myShiftTab: 'My shift',
    allUsersTab: 'All users',
    allUsersTitle: 'Shifts',
    allUsersSubtitle: 'Every drawer in the shop, live. Only the owner and managers see this view.',
    overviewOpen: 'Open drawers',
    overviewOpenHint: '{today} working today · {old} left open from earlier days',
    overviewDeliveriesOut: 'Deliveries still out',
    overviewSales: 'Sales in open drawers',
    overviewAttention: 'Needs attention',
    overviewAttentionHint: 'business day ended - close them',
    noOpenShifts: 'No drawer is open right now.',
    you: '(you)',
    openedOn: 'opened {date}',
    openToday: 'Open',
    dayEndedBadge: 'Business day ended',
    expectedUsd: 'Expected cash (USD)',
    expectedLbp: 'Expected cash (LBP)',
    deliveriesOut: 'Deliveries out',
    deliveriesOutTitle: '{name} sent these out, not yet paid - the drawer cannot close until they are received',
    leftAt: 'left {time}',
    receiveFor: 'Receive for {name}',
    feePending: 'Fee to confirm in the POS',
    closeOnBehalf: 'Close on behalf of {name}',
    closeOnBehalfBlocked: 'Close on behalf - waits for {count} deliveries',
    closeMine: 'Close my shift',
    closedShifts: 'Closed shifts',
    allCashiers: 'All users',
    closedBy: 'Closed by',
    onBehalf: 'on behalf',
    unassignedTitle: 'Older delivery accounts no open drawer owns',
    unassignedHint: 'Sent out before deliveries were tied to a shift. Receive them into your own drawer.',
    onBehalfDialogTitle: "Close {name}'s shift",
    onBehalfWarningTitle: "You are closing somebody else's drawer",
    onBehalfWarning: 'Shift {shift}, opened {date} by {name}. Count the cash that is physically in their drawer. The report will say it was closed by you on their behalf.',
    onBehalfNote: 'Note (required when closing for someone else)',
    onBehalfConfirm: "Close {name}'s shift",
    onBehalfClosed: '{shift} closed on behalf of {name}.',
    onBehalfDeliveriesOut: 'This shift still has deliveries out. Receive them for {name} first.',
    expectedInDrawer: 'Expected in the drawer',
    drawerMatches: 'The drawer matches',
    syncBeforeClose: 'Reconnect and sync all pending operations before closing the shift.',
    title: 'My Shift',
    subtitle: 'The drawer you are responsible for.',
    noShift: 'You do not have a shift open.',
    noShiftHint: 'Open one before you start selling, so the cash can be counted against it.',
    open: 'Open a shift',
    opening: 'Opening…',
    openTitle: 'Open a shift',
    openingCashUsd: 'Cash in the drawer (USD)',
    openingCashLbp: 'Cash in the drawer (LBP)',
    openingCashHint: 'Count what is physically there before you start.',
    openNote: 'Note',
    close: 'Close the shift',
    closing: 'Closing…',
    closeTitle: 'Close the shift',
    closeIntro: 'Count the drawer now and enter what you actually have.',
    countedUsd: 'Counted cash (USD)',
    countedLbp: 'Counted cash (LBP)',
    closeNote: 'Note about any difference',
    expected: 'Expected',
    counted: 'Counted',
    difference: 'Difference',
    over: 'Over',
    short: 'Short',
    exact: 'Exact',
    shiftNumber: 'Shift',
    openedAt: 'Opened',
    closedAt: 'Closed',
    status: 'Status',
    statusOpen: 'Open',
    closeBlockedDeliveries: 'The shift cannot be closed',
    closeBlockedDeliveriesCount: '{count} delivery account(s) are still awaiting receipt.',
    closeBlockedDeliveriesBody:
      'Receive each account here - the same payment window as the till. Nothing is ever marked paid automatically; the close button unlocks once the last one is received.',
    moreDeliveryAccounts: '+{count} more - they appear here as these are received.',
    statusClosed: 'Closed',
    salesCount: 'Sales',
    salesTotal: 'Sales total',
    cashTaken: 'Cash taken',
    changeGiven: 'Change given',
    byMethod: 'Taken by method',
    movements: 'Cash movements',
    noMovements: 'No cash has moved yet.',
    history: 'Past shifts',
    noHistory: 'No shifts yet.',
    opened: 'Your shift is open. You can start selling.',
    closed: 'Your shift is closed.',
    cashier: 'Cashier',
    confirmCloseTitle: 'Close this shift?',
    confirmCloseMessage:
      'Once closed you cannot sell against it again. Make sure the drawer has been counted.',
  },

  payment: {
    title: 'Take payment',
    dueNow: 'Total due',
    paidSoFar: 'Paid so far',
    remaining: 'Still owed',
    change: 'Change',
    addPayment: 'Add a payment',
    method: 'Method',
    cash: 'Cash',
    visa: 'Visa',
    whish: 'Whish',
    /// Phase 7. Puts the sale on the customer's account instead of taking
    /// money now.
    credit: 'On account',
    currency: 'Currency',
    amount: 'Amount',
    reference: 'Reference number',
    referenceHint: 'Optional. The approval code from the card machine or app.',
    quickExact: 'Exact amount',
    remove: 'Remove',
    changeCurrency: 'Give change in',
    complete: 'Complete the sale',
    completing: 'Completing…',
    /// How the order reaches the customer - decided here, at payment.
    fulfilmentLabel: 'Order type',
    fulfilmentPickup: 'Collection',
    fulfilmentOnTable: 'On table',
    fulfilmentDelivery: 'Delivery',
    tableLabel: 'Table number',
    tablePlaceholder: '4',
    tableRequired: 'Say which table this order is for.',
    deliveryLocked: 'This sale has a delivery fee, so it goes out as a delivery.',
    /// The stage-change line: the money is in; what remains is how the order
    /// leaves the shop.
    paidInFull: 'Paid in full',
    /// The order-level preparation exception.
    skipPreparation: 'No preparation',
    skipPreparationHint: 'This order will not be sent to the preparation printers.',
    skipPreparationWarning: 'No preparation ticket will be sent for this order.',
    skipReceipt: 'No receipt',
    skipReceiptHint: 'The customer receipt will not print on the cashier printer. You can still reprint it later from Orders.',
    skipReceiptWarning: 'No receipt will print for this sale. The sale is still recorded, and any kitchen tickets still print.',
    preparationAlreadySent: 'Preparation was already sent for this order.',
    completed: 'Sale completed.',
    noPayments: 'Add at least one payment.',
    underpaid: 'The payment does not cover the total yet.',
    overpaidNote: 'The customer is owed change.',
    noRate: 'No exchange rate has been set. An owner or manager must set one before selling.',
    shiftRequired: 'Your shift is not open',
    shiftRequiredHint: 'Open a shift before selling, so the cash can be counted against it.',
    goToShift: 'Go to My Shift',

    // --- the business day ended while this drawer was still open ---------
    dayEndedTitle: 'The business day has ended',
    dayEndedBody:
      'You must close the current shift before continuing to sell in the new business day.',
    /// Says which drawer and which day, so the cashier knows what they are
    /// being asked to count.
    dayEndedShift: 'Shift {number}, opened {day}',
    dayEndedWhy: 'A shift holds one day’s takings. The day ended at 12:00 AM.',
    dayEndedCart: 'Your current order has been kept. Nothing has been lost.',
    dayEndedAction: 'Close shift',
    /// The banner over the payment panel, once the block is showing.
    dayEndedShort: 'The business day has ended — close the shift to carry on.',
    failedTitle: 'The sale was not completed',
    failedRetry:
      'Nothing was saved and no stock was deducted. You can safely try again - a repeat of the same order will not be charged twice.',
  },

  orders: {
    title: 'Orders',
    subtitle: 'Completed sales.',
    subtitleOwn: 'Your completed sales.',
    // ONE box, THREE handles. Whoever is at the counter has whichever piece
    // of paper they kept: the receipt, the number the shop quoted, or the one
    // the menu showed them when they ordered.
    searchPlaceholder: 'Search receipt, Menu Order, or order number',
    empty: 'No sales yet.',
    emptyHint: 'Completed sales appear here.',
    noResults: 'No sale matches that number.',
    dateToday: 'Today',
    dateYesterday: 'Yesterday',
    dateLast7: 'Last 7 days',
    dateCustom: 'Custom range',
    dateFilterLabel: 'Which days to show',
    dateLockedHint: 'Today only. Ask the owner for access to previous days.',
    customFrom: 'From',
    customTo: 'To',
    customApply: 'Apply',
    customClear: 'Clear',
    customReversed: 'The start date must not be after the end date.',
    emptyForRange: 'No sales in the days you chose.',
    receipt: 'Receipt',
    when: 'When',
    cashier: 'Cashier',
    items: 'Items',
    total: 'Total',
    paidWith: 'Paid with',
    viewReceipt: 'View receipt',
    scopeOwn: 'You are seeing your own sales only.',
  },

  reports: {
    title: 'Reports',
    dailyTitle: 'Daily sales',
    day: 'Day',
    today: 'Today',
    salesCount: 'Sales',
    itemsSold: 'Items sold',
    revenue: 'Revenue',
    averageSale: 'Average sale',
    shiftsOpened: 'Shifts opened',
    costOfGoods: 'Cost of goods',
    grossProfit: 'Gross profit',
    grossMargin: 'Gross margin',
    byMethod: 'By payment method',
    byCashier: 'By cashier',
    topProducts: 'Best sellers',
    method: 'Method',
    count: 'Count',
    amount: 'Amount',
    inUsd: 'In USD',
    product: 'Product',
    quantity: 'Quantity',
    noSales: 'No sales were recorded on this day.',
    notIncludedTitle: 'Not in this report yet',
    notIncludedBody:
      'These are deliberately absent rather than shown as zero. The full report suite arrives in Phase 8.',
    profitHidden: 'Profit figures need the profit-reports permission.',
  },

  approval: {
    title: 'Manager approval needed',
    whoApproves: 'Which manager is approving?',
    noApprovers: 'No manager with a PIN can approve this. Ask the owner to set one up.',
    reason: 'Reason',
    reasonPlaceholder: 'For example: customer returned the meat unopened',
    reasonHint: 'This is kept permanently and shown in the audit log.',
    pin: "The manager's PIN",
    approve: 'Approve',
    checking: 'Checking…',
    footnote:
      'The manager must type this themselves. An approval covers one action, expires after ten minutes, and is recorded against their name.',
    granted: 'Approved by {name}.',
    required: 'A manager must approve this.',
  },

  inventory: {
    title: 'Inventory & stock count',
    subtitle: 'Every product, its stock, and a box to correct it.',
    trackedOf: '{count} of {total} products tracked',
    all: 'All',
    low: 'Low',
    out: 'Out',
    notTracked: 'Not tracked',
    inStock: 'In stock',
    allCategories: 'All categories',
    category: 'Category',
    search: 'Search a product by name or barcode',
    popular: 'Best sellers',
    popularWindow: 'last 30 days',
    popularEmpty: 'Nothing sold in the last 30 days.',
    timesOrdered: 'Ordered {count} times',
    sold: 'Sold',
    items: '{count} items',
    trackedCount: '{count} tracked',
    actual: 'Actual quantity',
    decrease: 'Less',
    increase: 'More',
    save: 'Save',
    saved: '{name}: stock is now {quantity}',
    reasonLabel: 'Reason (optional)',
    reasonCount: 'Count',
    reasonSpoiled: 'Spoiled',
    reasonDelivery: 'Delivery received',
    reasonMistake: 'Earlier mistake',
    notTrackedHint: 'Turn on «Track stock» in the product to follow it here.',
    readOnly: 'You can see the stock here, but not correct it.',
    empty: 'No product matches.',
    unchanged: 'That is already the recorded quantity.',
  },


  refund: {
    title: 'Refund',
    void: 'Void the sale',
    voidTitle: 'Void this sale',
    voidIntro:
      'Use this when the sale should not have happened at all. Everything goes back: all goods return to stock and all cash comes out of the drawer. The sale itself is kept exactly as it was and marked voided.',
    voidReason: 'Why is it being voided?',
    voidConfirm: 'Void the sale',
    voided: 'The sale has been voided.',
    voidedLabel: 'Voided',

    refundTitle: 'Refund items',
    refundIntro: 'Choose what the customer is bringing back. You can refund part of a sale.',
    chooseItems: 'What is coming back?',
    soldQuantity: 'Sold',
    alreadyRefunded: 'Already returned',
    availableToRefund: 'Left to refund',
    refundQuantity: 'Returning',
    refundReason: 'Why is the money being given back?',
    refundTotal: 'Refund total',
    howRefunded: 'How is the money going back?',
    restock: 'Put the goods back on the shelf',
    restockHint: 'Leave this off if the meat is no longer fit to sell.',
    refundConfirm: 'Give the refund',
    refunding: 'Refunding…',
    refunded: 'Refund {number} recorded.',
    nothingSelected: 'Choose at least one item.',
    fullyRefunded: 'Fully refunded',
    partiallyRefunded: 'Partly refunded',
    cannotRefund: 'Nothing is left to refund on this sale.',
    history: 'Refunds',
    refundNumber: 'Refund',
    approvedBy: 'Approved by',
    restocked: 'Back in stock',
    notRestocked: 'Not restocked',
  },

  expenses: {
    title: 'Expenses',
    subtitle: 'Money the shop has spent.',
    add: 'Record an expense',
    addTitle: 'Record an expense',
    category: 'Category',
    description: 'What was it for?',
    amount: 'Amount',
    currency: 'Currency',
    method: 'Paid by',
    cashWarning:
      'A cash expense comes straight out of the till, so your shift must be open and the expected cash will drop by this amount.',
    /// An expense is not a sale. A sale is money the shop HAS - it must be
    /// recordable whatever the network is doing, which is what the offline
    /// queue is for. An expense is a decision that needs a manager, a shift
    /// and an audit entry, and inventing one on a till that cannot reach the
    /// server would mean a cash total that disagrees with the drawer.
    offlineBlocked: 'Expenses require a connection to the POS server.',

    submit: 'Record it',
    submitting: 'Recording…',
    created: 'The expense has been recorded.',
    status: 'Status',
    statusPENDING: 'Waiting',
    statusAPPROVED: 'Approved',
    statusREJECTED: 'Rejected',
    approve: 'Approve',
    reject: 'Reject',
    rejectTitle: 'Reject this expense?',
    rejectReason: 'Why is it being rejected?',
    rejectNote:
      'Rejecting does not put the money back in the drawer. It has already been spent; this records that it should not have been.',
    approved: 'The expense has been approved.',
    rejected: 'The expense has been rejected.',
    recordedBy: 'Recorded by',
    spentAt: 'When',
    total: 'Total',
    empty: 'No expenses yet.',
    emptyHint: 'Anything the shop spends money on is recorded here.',
    allStatuses: 'All',
    needsApproval: 'This expense is above the limit, so a manager must approve it.',
    approvalTitle: 'Approve this expense',

    categoryDELIVERY: 'Delivery',
    categoryFUEL: 'Fuel',
    categoryPACKAGING: 'Packaging',
    categoryCLEANING: 'Cleaning',
    categoryMAINTENANCE: 'Maintenance',
    categoryELECTRICITY: 'Electricity',
    categoryEMPLOYEE_MEALS: 'Employee meals',
    categorySUPPLIER_RELATED: 'Supplier related',
    categoryMISCELLANEOUS: 'Miscellaneous',

    // --- managing the category list --------------------------------------
    //
    // The nine keys above are the SHIPPED categories, looked up by key so
    // they keep their Arabic. A category the owner types has no key and is
    // shown exactly as typed, in both languages: translating somebody's own
    // words for them is a guess, and "Generator" is not improved by one.
    allCategories: 'All categories',
    manageCategories: 'Manage categories',
    categoriesTitle: 'Manage expense categories',
    activeCategories: 'Active categories',
    archivedCategories: 'Archived categories',
    addCategory: 'Add category',
    editCategory: 'Edit category',
    categoryName: 'Category name',
    archive: 'Archive',
    restore: 'Restore',
    categoryInUse: 'This category is already in use.',
    categoryAdded: 'Category added.',
    categoryRenamed: 'Category renamed.',
    categoryArchived: 'Category archived. Past expenses keep it.',
    categoryRestored: 'Category restored.',
    categoryRemoved: 'Category removed.',
    categoryUsedCount: 'Used by {count}',
    categoryNeverUsed: 'Never used',
    categoryArchiveNote: 'Archiving keeps this category on every expense that already uses it.',
    categoryDeleteUnused: 'Delete',
    categoryDeleteConfirm: 'Delete this category? It has never been used, so nothing is lost.',
    noCategories: 'No expense categories available.',
    noCategoriesHint: 'Add one to start recording expenses.',
    categoryArchivedMark: 'Archived',
  },

  discount: {
    title: 'Discount',
    add: 'Add a discount',
    amount: 'Amount off',
    reason: 'Why?',
    reasonPlaceholder: 'For example: damaged packaging, regular customer',
    remove: 'Remove the discount',
    needsApproval: 'That discount is above the limit, so a manager must approve it.',
    approvalTitle: 'Approve this discount',
    applied: 'Discount applied.',
    subtotal: 'Before discount',
  },

  suppliers: {
    title: 'Suppliers',
    subtitle: 'The businesses the shop buys from.',
    add: 'Add supplier',
    edit: 'Edit supplier',
    name: 'Name',
    contactPerson: 'Contact person',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    notes: 'Notes',
    paymentTerms: 'Payment terms',
    paymentTermsDays: 'Days to pay',
    paymentTermsHint: 'Used to work out an invoice due date when none is given. Zero means due at once.',
    active: 'Currently used',
    searchPlaceholder: 'Search by name, contact or phone',
    showInactive: 'Include inactive',
    orders: 'Orders',
    invoices: 'Invoices',
    balance: 'Balance',
    owed: 'Owed to them',
    overdue: 'Overdue',
    invoiced: 'Invoiced',
    paid: 'Paid',
    settled: 'Nothing owed',
    balanceHidden: 'You do not have permission to see supplier balances.',
    empty: 'No suppliers yet.',
    emptyHint: 'Add the businesses you buy your meat from.',
    created: 'The supplier has been added.',
    saved: 'The supplier has been saved.',
    deleted: 'The supplier has been removed.',
    deleteTitle: 'Remove this supplier?',
    deleteMessage: 'Only a supplier with no open orders and nothing owing can be removed.',
    history: 'Purchase history',
    noHistory: 'Nothing has been bought from them yet.',
    tabSuppliers: 'Suppliers',
    tabInvoices: 'Invoices',
    tabPayments: 'Payments',
    moneyHidden: 'You do not have permission to see supplier invoices or payments.',
  },

  purchases: {
    title: 'Purchases',
    subtitle: 'What has been ordered, and what has actually arrived.',
    add: 'New order',
    addTitle: 'New purchase order',
    orderNumber: 'Order',
    supplier: 'Supplier',
    status: 'Status',
    expectedDate: 'Expected',
    expectedTotal: 'Expected total',
    items: 'Products',
    deliveries: 'Deliveries',
    createdBy: 'Raised by',
    notes: 'Notes',

    statusDRAFT: 'Draft',
    statusORDERED: 'Ordered',
    statusPARTIALLY_RECEIVED: 'Partly received',
    statusRECEIVED: 'Received',
    statusCANCELLED: 'Cancelled',

    /// The single most important sentence on the screen.
    noStockYet:
      'Nothing has been added to stock. Stock only changes when the goods physically arrive and the delivery is recorded.',

    addProduct: 'Add a product',
    product: 'Product',
    quantity: 'Quantity',
    expectedCost: 'Expected cost',
    lineTotal: 'Line total',
    removeLine: 'Remove',
    createOrder: 'Save as draft',
    creating: 'Saving…',
    created: 'Order {number} saved as a draft.',
    place: 'Send to the supplier',
    placing: 'Sending…',
    placed: 'The order has been placed.',
    cancel: 'Cancel the order',
    cancelTitle: 'Cancel this order?',
    cancelMessage: 'Anything already received stays on the shelf. Only the outstanding part is abandoned.',
    cancelReason: 'Why is it being cancelled?',
    cancelled: 'The order has been cancelled.',

    receive: 'Record a delivery',
    receiveTitle: 'Record what arrived',
    receiveIntro:
      'Enter what physically turned up. Only the good quantity is added to stock; damaged goods are recorded but never put on the shelf.',
    ordered: 'Ordered',
    received: 'Received so far',
    outstanding: 'Still to come',
    receivingNow: 'Arrived now',
    damaged: 'Damaged',
    damagedHint: 'Arrived but not fit to sell.',
    missing: 'Missing',
    missingHint: 'Ordered but not in the delivery.',
    actualCost: 'Actual cost',
    actualCostHint: 'What the supplier really charged. This updates the cost price.',
    batchNumber: 'Batch number',
    batchNumberHint: 'Leave empty and one will be generated.',
    productionDate: 'Production date',
    expiryDate: 'Expiry date',
    storageLocation: 'Stored in',
    deliveryNote: "Supplier's delivery note",
    confirmReceive: 'Add to stock',
    confirmPendingReceipt: 'The previous receipt is not yet confirmed. Try again to check the same delivery.',
    receiving: 'Recording…',
    receivedMessage: 'Delivery {number} recorded and the stock updated.',
    nothingEntered: 'Enter at least one quantity.',
    tooMuch: 'Only {outstanding} of {product} is still outstanding on this order.',
    costHidden: 'Costs are recorded from the order. You are not permitted to see or change them.',
    empty: 'No purchase orders yet.',
    emptyHint: 'Raise one when you order from a supplier.',
    allStatuses: 'All',
    fullyReceived: 'Everything on this order has arrived.',
  },

  invoices: {
    title: 'Supplier invoices',
    add: 'Record an invoice',
    addTitle: 'Record a supplier invoice',
    invoiceNumber: "Supplier's invoice number",
    invoiceDate: 'Invoice date',
    dueDate: 'Due date',
    dueDateHint: 'Leave empty to work it out from the supplier\'s agreed terms.',
    amount: 'Amount',
    outstanding: 'Outstanding',
    paid: 'Paid',
    linkedOrder: 'Against order',
    statusUNPAID: 'Unpaid',
    statusPARTIALLY_PAID: 'Part paid',
    statusPAID: 'Paid',
    statusOVERDUE: 'Overdue',
    empty: 'No invoices recorded.',
    emptyHint: 'Record a supplier bill when it arrives.',
    created: 'The invoice has been recorded.',
    allStatuses: 'All',
    overdueWarning: '{count} overdue',
  },

  supplierPayments: {
    title: 'Payments to suppliers',
    add: 'Record a payment',
    addTitle: 'Pay a supplier',
    paymentNumber: 'Payment',
    againstInvoice: 'Against invoice',
    onAccount: 'On account',
    amount: 'Amount',
    // "Method" for how, "Paid by" for who. Both appear as columns on the same
    // table, so they must not read the same.
    method: 'Method',
    reference: 'Reference',
    note: 'Note',
    paidAt: 'When',
    paidBy: 'Paid by',
    cashWarning:
      'Cash paid to a supplier comes out of the till, so your shift must be open and the expected cash will drop by this amount.',
    submit: 'Record the payment',
    submitting: 'Recording…',
    created: 'The payment has been recorded.',
    empty: 'No payments recorded.',
  },

  batches: {
    title: 'Batches',
    subtitle: 'What arrived, when, and when it goes off.',
    batchNumber: 'Batch',
    product: 'Product',
    supplier: 'Supplier',
    received: 'Received',
    production: 'Made',
    expiry: 'Expires',
    initial: 'Arrived',
    remaining: 'Left',
    location: 'Stored in',
    value: 'Value',
    state: 'State',
    stateACTIVE: 'Good',
    stateNEAR_EXPIRY: 'Near expiry',
    stateEXPIRED: 'Expired',
    stateDEPLETED: 'Used up',
    daysLeft: '{days} days left',
    expiredDaysAgo: 'Expired {days} days ago',
    expiresToday: 'Expires today',
    allStates: 'All',
    empty: 'No batches yet.',
    emptyHint: 'Batches are created when a delivery is recorded with an expiry date.',
    alertsTitle: 'Expiry alerts',
    nearExpiry: 'Near expiry',
    expired: 'Expired',
    noAlerts: 'Nothing is close to expiring.',
    warningWindow: 'Warning set at {days} days before expiry.',
    deductionNote:
      'Batches record what arrived and when it expires. A sale currently reduces the product total rather than a specific batch - taking stock from the oldest batch first is the next step.',
  },

  recipes: {
    title: 'Recipes',
    subtitle: 'What goes into the things the shop makes.',
    add: 'New recipe',
    addTitle: 'New recipe',
    edit: 'Edit recipe',
    name: 'Name',
    nameEn: 'Name (English)',
    nameAr: 'Name (Arabic)',
    makes: 'Makes',
    output: 'Finished product',
    outputHint: 'The product this recipe makes. Producing it adds stock of exactly this product.',
    batchSize: 'One batch makes',
    batchSizeHint: 'The ingredient quantities below are written for one batch of this size.',
    shelfLife: 'Keeps for',
    shelfLifeDays: 'Days',
    shelfLifeHint: 'Used to date the batch when the run is finished. Leave empty if it does not expire.',
    instructions: 'Method',
    notes: 'Notes',
    ingredients: 'Ingredients',
    addIngredient: 'Add an ingredient',
    ingredient: 'Ingredient',
    quantity: 'Quantity',
    optional: 'Optional',
    optionalHint: 'An optional ingredient missing from stock will not stop production.',
    removeIngredient: 'Remove',
    inStock: 'In stock',
    needed: 'Needed',
    notEnough: 'Not enough',
    estimatedCost: 'Estimated cost',
    estimatedUnitCost: 'Cost per unit',
    costHidden: 'You do not have permission to see what things cost.',
    active: 'Currently in use',
    used: 'Used {count} times',
    empty: 'No recipes yet.',
    emptyHint: 'Write one for anything the shop makes from raw meat.',
    created: 'The recipe has been saved.',
    saved: 'The recipe has been saved.',
    deleted: 'The recipe has been removed.',
    retired: 'That recipe has production history, so it has been retired rather than removed.',
    deleteTitle: 'Remove this recipe?',
    deleteMessage:
      'A recipe that has been used is retired instead of removed, so past production still makes sense.',
    /// Said on the recipe screen, because it is the thing people assume wrongly.
    planOnly: 'A recipe is a plan. Writing one changes no stock.',
    outputIsIngredient: 'A recipe cannot use the product it makes as one of its own ingredients.',
    searchPlaceholder: 'Search recipes',
    showInactive: 'Include retired',
  },

  production: {
    title: 'Production',
    subtitle: 'What was made, from what, and how well it turned out.',
    add: 'Plan a run',
    addTitle: 'Plan a production run',
    productionNumber: 'Run',
    recipe: 'Recipe',
    status: 'Status',
    statusDRAFT: 'Planned',
    statusCOMPLETED: 'Finished',
    statusCANCELLED: 'Cancelled',
    allStatuses: 'All',
    planned: 'Planned',
    produced: 'Made',
    wanted: 'How much do you want to make?',
    wantedHint: 'The ingredients are scaled from the recipe to match.',
    createdBy: 'Planned by',
    completedBy: 'Finished by',
    notes: 'Notes',

    /// The single most important sentence on this screen.
    noStockYet:
      'Nothing has moved yet. Ingredients come off the shelf and the finished product goes on only when the run is finished.',

    plan: 'Save the plan',
    planning: 'Saving…',
    plannedMessage: 'Run {number} planned. No stock has moved yet.',

    finalise: 'Finish the run',
    finaliseTitle: 'Finish this production run',
    finaliseIntro:
      'Enter what you actually used and what actually came out. The ingredients are deducted and the finished product is added together, in one step — if anything is short, nothing moves at all.',
    actuallyUsed: 'Actually used',
    plannedUse: 'Planned',
    available: 'In stock',
    producedQuantity: 'How much came out?',
    producedHint: 'Weigh it. This is what goes on the shelf, and what the yield is worked out from.',
    wasteDuring: 'Wasted during the run',
    wasteDuringHint: 'Trimmings and losses. Recorded, never put on the shelf.',
    addWaste: 'Record some waste',
    confirmFinalise: 'Deduct and add to stock',
    finalising: 'Recording…',
    finishedMessage: 'Run {number} finished. {produced} added to stock.',
    shortOf: 'There is not enough {product}: {available} in stock, {needed} needed.',
    nothingUsed: 'Enter at least one ingredient quantity.',

    cancel: 'Cancel the run',
    cancelTitle: 'Cancel this run?',
    cancelMessage: 'Nothing has been consumed, so no stock changes. The plan is simply abandoned.',
    cancelReason: 'Why is it being cancelled?',
    cancelled: 'The run has been cancelled.',

    ingredientsUsed: 'Ingredients used',
    fromBatches: 'Taken from',
    yield: 'Yield',
    yieldHint: 'What came out, as a share of what went in.',
    ingredientCost: 'Ingredient cost',
    unitCost: 'Cost per unit',
    outputBatch: 'Batch made',
    expires: 'Expires',
    empty: 'No production runs yet.',
    emptyHint: 'Plan one when you are about to make something.',
    completedAt: 'Finished',
  },

  waste: {
    title: 'Waste',
    subtitle: 'Stock that left without being sold.',
    add: 'Record waste',
    addTitle: 'Record waste',
    wasteNumber: 'Record',
    product: 'Product',
    quantity: 'Quantity',
    reason: 'Reason',
    note: 'What happened?',
    batch: 'Batch',
    batchHint: 'Choose a batch if you are throwing away one specific lot.',
    value: 'Value lost',
    recordedBy: 'Recorded by',
    when: 'When',
    fromProduction: 'During run',
    allReasons: 'All reasons',

    reasonPRODUCTION_TRIM: 'Trimmings',
    reasonEXPIRED: 'Past its date',
    reasonSPOILED: 'Spoiled',
    reasonDAMAGED: 'Damaged',
    reasonCUSTOMER_RETURN: 'Returned by a customer',
    reasonPREPARATION_ERROR: 'Preparation mistake',
    reasonOTHER: 'Something else',

    submit: 'Record it',
    submitting: 'Recording…',
    created: 'Waste {number} recorded and the stock reduced.',
    needsApproval: 'That is worth more than the limit, so a manager must approve it.',
    approvalTitle: 'Approve this waste',
    tooMuch: 'There is only {available} in stock.',
    empty: 'Nothing has been thrown away.',
    emptyHint: 'That is a good thing.',
    totalLost: 'Total lost',
    costHidden: 'You do not have permission to see what the waste was worth.',
  },

  productionReport: {
    title: 'Production and waste',
    subtitle: 'What was made and what was lost.',
    tabProduction: 'Production',
    tabWaste: 'Waste',
    from: 'From',
    to: 'To',
    runCount: 'Runs',
    producedTotal: 'Made',
    consumedTotal: 'Raw material used',
    wasteTotal: 'Wasted',
    overallYield: 'Overall yield',
    totalCost: 'Ingredient cost',
    byProduct: 'By product',
    byReason: 'By reason',
    recentRuns: 'Runs',
    noRuns: 'Nothing was made in this period.',
    noWaste: 'Nothing was thrown away in this period.',
    records: 'Records',
  },

  customers: {
    title: 'Customers',
    subtitle: 'The people the shop sells to by name.',
    add: 'New customer',
    addTitle: 'New customer',
    edit: 'Edit customer',
    code: 'Code',
    name: 'Name',
    phone: 'Phone',
    altPhone: 'Second number',
    email: 'Email',
    type: 'Type',
    notes: 'Notes',
    active: 'Currently a customer',
    searchPlaceholder: 'Search by name, phone or code',
    showInactive: 'Include former customers',
    allTypes: 'All types',

    typeRETAIL: 'Walk-in',
    typeWHOLESALE: 'Wholesale',
    typeRESTAURANT: 'Restaurant',
    typeSTAFF: 'Staff',

    orders: 'Orders',
    purchases: 'Purchases',
    empty: 'No customers yet.',
    emptyHint: 'Add the people who order by name or buy on account.',
    created: '{name} has been added.',
    saved: 'The customer has been saved.',
    deleted: 'The customer has been removed.',
    deleteTitle: 'Remove this customer?',
    deleteMessage: 'Only a customer with nothing owing can be removed. Their past sales are kept.',

    /// Shown to anybody without `customers.credit`.
    creditHidden: 'You do not have permission to see customer balances or credit.',

    addresses: 'Addresses',
    addAddress: 'Add an address',
    addressLabel: 'Name it',
    addressLabelHint: 'For example: Home, The restaurant, Warehouse.',
    area: 'Area',
    street: 'Street',
    addressDetails: 'Building, floor, flat',
    deliveryInstructions: 'Delivery instructions',
    deliveryInstructionsHint: 'For example: ring twice, leave with the doorman, call on arrival.',
    isDefault: 'Use this one first',
    noAddresses: 'No addresses yet.',
    addressSaved: 'The address has been saved.',
    addressRemoved: 'The address has been removed.',

    tabDetails: 'Details',
    tabStatement: 'Statement',
    tabHistory: 'Purchases',
  },

  credit: {
    title: 'Credit',
    settings: 'Credit settings',
    allowed: 'May buy on credit',
    allowedHint: 'Off by default. Credit is granted deliberately, never by accident.',
    limit: 'Credit limit',
    limitHint: 'The most they may owe at any moment.',
    terms: 'Days to pay',
    termsHint: 'How long after a credit sale before it counts as overdue.',
    balance: 'Owes',
    available: 'Credit left',
    overdue: 'Overdue',
    overdueSince: 'Oldest unpaid since {date}',
    settled: 'Nothing owed',
    limitSaved: 'The credit settings have been saved.',

    blocked: 'Credit blocked',
    block: 'Block further credit',
    unblock: 'Allow credit again',
    blockTitle: 'Block further credit?',
    blockMessage:
      'They keep their limit, but no new sale can go on the account until this is lifted.',
    blockReason: 'Why?',
    blocked_: 'Credit is blocked: {reason}',
    blockedShort: 'Blocked',
    blockSaved: 'The credit block has been updated.',

    statement: 'Statement',
    when: 'When',
    detail: 'What',
    charge: 'Charged',
    paid: 'Paid',
    runningBalance: 'Balance',
    due: 'Due',
    typeSALE: 'Sale on account',
    typePAYMENT: 'Payment received',
    typeREFUND: 'Refund',
    typeADJUSTMENT: 'Correction',
    noTransactions: 'Nothing on the account yet.',
    /// Said on the statement, because it is what makes the ledger trustworthy.
    appendOnly:
      'Every line here is permanent. A mistake is corrected by adding the opposite entry, never by changing what is already written.',

    adjust: 'Correct the balance',
    adjustTitle: 'Correct this balance',
    adjustMessage:
      'This adds a correcting entry. The original lines are left exactly as they are.',
    adjustAmount: 'By how much?',
    adjustAmountHint: 'A positive figure adds to what they owe, a negative one reduces it.',
    adjustReason: 'Why?',
    adjusted: 'The balance has been corrected.',
    approvalTitle: 'Approve this correction',

    outstanding: 'Money owed',
    outstandingSubtitle: 'Everybody who owes, worst first.',
    totalOwed: 'Total owed',
    totalOverdue: 'Total overdue',
    onlyOverdue: 'Only overdue',
    noneOwing: 'Nobody owes anything.',
  },

  customerPayments: {
    title: 'Customer payments',
    add: 'Take a payment',
    addTitle: 'Take a payment',
    paymentNumber: 'Payment',
    customer: 'Customer',
    amount: 'Amount',
    method: 'Method',
    reference: 'Reference',
    note: 'Note',
    when: 'When',
    receivedBy: 'Taken by',
    owes: 'Currently owes',
    payAll: 'Pay it all',
    cashWarning:
      'Cash from a customer goes into the till, so your shift must be open and the expected cash will rise by this amount.',
    tooMuch: 'A customer cannot pay more than they owe.',
    submit: 'Record the payment',
    submitting: 'Recording…',
    created: 'Payment {number} recorded. The balance is now {balance}.',
    empty: 'No payments yet.',
    totalTaken: 'Total taken',
  },

  // Named `customerOrders` because `orders` is already the Phase 3 screen
  // showing completed SALES. These are orders to be prepared and delivered,
  // which is a different thing entirely.
  customerOrders: {
    title: 'Orders',
    subtitle: 'What to cut, and where it goes.',
    add: 'Take an order',
    addTitle: 'Take an order',
    orderNumber: 'Order',
    customer: 'Customer',
    status: 'Status',
    fulfilment: 'How',
    fulfilmentPICKUP: 'Collecting',
    fulfilmentDELIVERY: 'Delivering',
    address: 'Address',
    requestedFor: 'Wanted for',
    estimate: 'Estimate',
    /// Said wherever the estimate appears, because meat is sold by weight.
    estimateHint:
      'An estimate from today\'s prices. The real figure comes from the sale, because meat is weighed.',
    items: 'Items',
    addItem: 'Add an item',
    quantity: 'Quantity',
    preparationNotes: 'How to cut it',
    preparationNotesHint: 'For example: cubes, minced twice, no fat.',
    orderNotes: 'Notes for the whole order',
    deliveryNotes: 'Notes for the driver',
    pickupOnlyHint:
      'This dialog takes PICKUP orders. A delivery is created from the POS Current Order, with a delivery fee.',
    driver: 'Driver',
    takenBy: 'Taken by',
    billedAs: 'Rung up as',
    notBilledYet: 'Not rung up yet',

    statusNEW: 'New',
    statusPREPARING: 'Preparing',
    statusREADY: 'Ready',
    statusOUT_FOR_DELIVERY: 'Out for delivery',
    statusDELIVERED: 'Delivered',
    statusCANCELLED: 'Cancelled',
    allStatuses: 'All',
    onlyOpen: 'Still to do',

    /// The sentence this whole screen turns on.
    noStockYet:
      'An order is a work list. It moves no stock and no money — the sale at the till does both.',

    start: 'Start preparing',
    markReady: 'Mark as ready',
    markDelivered: 'Mark as delivered',
    cancel: 'Cancel the order',
    cancelTitle: 'Cancel this order?',
    cancelMessage: 'Nothing has been sold, so no money or stock changes. The order is called off.',
    cancelReason: 'Why?',
    cancelled: 'The order has been cancelled.',
    statusChanged: 'The order has been updated.',
    created: 'Order {number} taken.',

    prepared: 'Cut',
    notPrepared: 'Not cut yet',
    allPrepared: 'Everything on this order has been cut.',
    ticket: 'Preparation ticket',
    printTicket: 'Print the ticket',

    empty: 'No orders yet.',
    emptyHint: 'Take one when a customer phones or asks for something to be prepared.',
    noOpen: 'Nothing waiting.',
  },

  // --- Phase 8 -------------------------------------------------------------

  reportSuite: {
    title: 'Reports',
    subtitle: 'Everything the shop has done, in whatever slice you need.',

    tabs: {
      sales: 'Sales',
      profit: 'Profit',
      inventory: 'Stock value',
      purchases: 'Purchases',
      expenses: 'Expenses',
      cashiers: 'Cashiers',
      customers: 'Customers',
      production: 'Production & waste',
    },

    filters: {
      title: 'Filters',
      from: 'From',
      to: 'To',
      product: 'Product',
      category: 'Category',
      cashier: 'Cashier',
      customer: 'Customer',
      supplier: 'Supplier',
      paymentMethod: 'Paid by',
      status: 'Status',
      apply: 'Apply',
      clear: 'Clear filters',
      anyProduct: 'Any product',
      anyCategory: 'Any category',
      anyCashier: 'Any cashier',
      anyCustomer: 'Any customer',
      anySupplier: 'Any supplier',
      anyMethod: 'Any method',
      anyStatus: 'Any status',
      today: 'Today',
      last7: 'Last 7 days',
      last30: 'Last 30 days',
      thisMonth: 'This month',
      showing: 'Showing {from} to {to}, Beirut time',
      backwards: 'The start date is after the end date.',
    },

    export: {
      title: 'Download',
      csv: 'CSV',
      excel: 'Excel',
      pdf: 'PDF',
      working: 'Preparing…',
      failed: 'That download did not work.',
      note: 'The file contains exactly what you can see here — nothing more.',
    },

    summary: {
      sales: 'Sales',
      itemsSold: 'Items sold',
      revenue: 'Revenue',
      netRevenue: 'Net revenue',
      discounts: 'Discounts',
      refunded: 'Refunded',
      averageSale: 'Average sale',
      cost: 'Cost of goods',
      grossProfit: 'Gross profit',
      netProfit: 'Net profit',
      margin: 'Margin',
      grossMargin: 'Gross margin',
      netMargin: 'Net margin',
      expenses: 'Expenses',
      waste: 'Waste',
      products: 'Products',
      lowStock: 'Low stock',
      outOfStock: 'Out of stock',
      batches: 'Active batches',
      stockAtCost: 'Stock at cost',
      stockAtRetail: 'Stock at retail',
      potentialMargin: 'Potential margin',
      orders: 'Orders',
      deliveries: 'Deliveries',
      invoices: 'Invoices',
      payments: 'Payments',
      received: 'Goods received',
      invoiced: 'Invoiced',
      paid: 'Paid',
      outstanding: 'Outstanding',
      cashiers: 'Cashiers',
      shifts: 'Shifts',
      shiftsOver: 'Drawers over',
      shiftsShort: 'Drawers short',
      netDifference: 'Net difference',
      customersWhoBought: 'Customers who bought',
      totalSpend: 'Total spend',
      customersOwing: 'Customers owing',
      totalOwed: 'Total owed',
      paymentsTaken: 'Payments taken',
      runs: 'Production runs',
      produced: 'Produced',
      consumed: 'Raw material used',
      yield: 'Yield',
      wasteRecords: 'Waste records',
      wasted: 'Wasted',
      ingredientCost: 'Ingredient cost',
      wasteCost: 'Waste cost',
    },

    tables: {
      byDay: 'By day',
      byHour: 'By hour',
      byProduct: 'By product',
      byCategory: 'By category',
      byMethod: 'By payment method',
      bySupplier: 'By supplier',
      byCashier: 'By cashier',
      byCustomer: 'By customer',
      byStatus: 'By status',
      byReason: 'By reason',
      shifts: 'Shifts',
      overdue: 'Overdue accounts',
      stockOnHand: 'Stock on hand',
      movements: 'Stock movements',
      runs: 'Production runs',
      wasteByReason: 'Waste by reason',
      wasteByProduct: 'Waste by product',
      expensesByCategory: 'Expenses by category',
      howProfitIsWorkedOut: 'How the profit is worked out',
    },

    columns: {
      day: 'Day',
      hour: 'Hour',
      count: 'Count',
      sales: 'Sales',
      quantity: 'Quantity',
      revenue: 'Revenue',
      cost: 'Cost',
      profit: 'Profit',
      margin: 'Margin',
      amount: 'Amount',
      product: 'Product',
      category: 'Category',
      method: 'Method',
      currency: 'Currency',
      supplier: 'Supplier',
      customer: 'Customer',
      code: 'Code',
      type: 'Type',
      balance: 'Balance',
      spend: 'Spend',
      phone: 'Phone',
      since: 'Overdue since',
      cashier: 'Cashier',
      shift: 'Shift',
      opened: 'Opened',
      closed: 'Closed',
      expected: 'Expected',
      counted: 'Counted',
      difference: 'Difference',
      refunds: 'Refunds',
      voids: 'Voids',
      discounts: 'Discounts',
      average: 'Average',
      status: 'Status',
      inStock: 'In stock',
      minimum: 'Minimum',
      unitCost: 'Unit cost',
      valueAtCost: 'Value at cost',
      valueAtRetail: 'Value at retail',
      reason: 'Reason',
      netChange: 'Net change',
      run: 'Run',
      finished: 'Finished',
      made: 'Made',
      yield: 'Yield',
      deliveries: 'Deliveries',
      invoiced: 'Invoiced',
      paid: 'Paid',
      outstanding: 'Outstanding',
      line: 'Line',
      records: 'Records',
      open: 'Open',
    },

    profitLines: {
      revenue: 'Revenue',
      lessCost: 'Less: cost of goods sold',
      grossProfit: 'Gross profit',
      lessRefunds: 'Less: refunds',
      lessWaste: 'Less: waste',
      lessExpenses: 'Less: expenses',
      netProfit: 'Net profit',
    },

    stockStatus: {
      OK: 'In stock',
      LOW: 'Low',
      OUT: 'Out',
    },

    empty: 'Nothing happened in this period.',
    emptyHint: 'Try a wider date range, or clear the filters.',
    profitHidden: 'Cost and profit are not shown for your role.',
    valueHidden: 'Stock values are not shown for your role.',
    creditHidden: 'Customer balances are not shown for your role.',
  },

  imports: {
    title: 'Import data',
    subtitle: 'Load categories, products, prices, stock, suppliers or customers from a spreadsheet.',

    kinds: {
      CATEGORIES: 'Categories',
      PRODUCTS: 'Products',
      PRICES: 'Prices',
      OPENING_INVENTORY: 'Opening stock',
      SUPPLIERS: 'Suppliers',
      CUSTOMERS: 'Customers',
    },

    steps: {
      choose: 'Choose what to import',
      upload: 'Upload the file',
      map: 'Check the columns',
      review: 'Review',
      done: 'Finished',
    },

    template: {
      title: 'Not sure of the format?',
      body: 'Download a template with the right headings and one example row.',
      csv: 'Template (CSV)',
      excel: 'Template (Excel)',
    },

    upload: {
      choose: 'Choose a file',
      chooseAgain: 'Choose a different file',
      accepted: 'CSV or Excel (.xlsx), up to 8 MB.',
      preview: 'Check the file',
      previewing: 'Reading the file…',
      noFile: 'Choose a CSV or Excel file first.',
    },

    mapping: {
      title: 'Columns',
      body: 'Each field below is matched to a column in your file. Correct anything that is wrong.',
      field: 'Field',
      column: 'Column in your file',
      notMatched: 'Not matched',
      required: 'Required',
      example: 'For example: {example}',
      unmatchedHeadings: 'Columns in your file that were not used:',
      missingRequired: 'These required columns are still missing:',
      recheck: 'Check again with these columns',
    },

    review: {
      title: 'What will happen',
      totalRows: 'Rows in the file',
      validRows: 'Rows that are ready',
      rejectedRows: 'Rows with a problem',
      willCreate: 'Will be added',
      willUpdate: 'Will be updated',
      matchedOn: 'How existing records are recognised',
      sample: 'The first rows, as they will be saved',
      action: 'Action',
      create: 'Add',
      update: 'Update',
      line: 'Line',
      problems: 'Problems found',
      problemsBody: 'Each line below is numbered as it appears in your spreadsheet.',
      noProblems: 'Every row checks out.',
      allOrNothing: 'Nothing will be imported while there are problems.',
      allOrNothingBody:
        'A file with a mistake in it is usually a file that needs fixing, not one that should be half-loaded. Fix the lines above and check the file again.',
      validRowsOnly: 'Import only the rows that are ready, and skip the rest',
      validRowsOnlyWarning:
        'The {count} rows listed above will NOT be imported. You will have to fix and import them separately.',
      commit: 'Import now',
      committing: 'Importing…',
      cancel: 'Start again',
    },

    outcome: {
      committed: 'Imported successfully',
      rejected: 'Nothing was imported',
      created: '{count} added',
      updated: '{count} updated',
      skipped: '{count} skipped',
      again: 'Import another file',
    },

    history: {
      title: 'Import history',
      empty: 'Nothing has been imported yet.',
      file: 'File',
      kind: 'Type',
      result: 'Result',
      rows: 'Rows',
      by: 'By',
      when: 'When',
      showPreviews: 'Include previews',
      statusPREVIEWED: 'Preview only',
      statusCOMMITTED: 'Imported',
      statusREJECTED: 'Refused',
      partial: 'valid rows only',
    },
  },

  notifications: {
    title: 'Notifications',
    open: 'Notifications',
    empty: 'Nothing needs your attention.',
    emptyHint: 'Warnings about stock, expiry, money owed and cash differences appear here.',
    acknowledge: 'Mark as seen',
    acknowledgeAll: 'Mark all as seen',
    refresh: 'Check now',
    refreshing: 'Checking…',
    showAcknowledged: 'Include ones already seen',
    seen: 'Seen',
    seenBy: 'Seen by {name}',
    resolved: 'Resolved',
    viewAll: 'See all',

    severity: {
      INFO: 'For information',
      WARNING: 'Needs attention',
      CRITICAL: 'Urgent',
    },

    kinds: {
      LOW_STOCK: 'Low stock',
      NEAR_EXPIRY: 'Expiring soon',
      EXPIRED_STOCK: 'Expired',
      SUPPLIER_INVOICE_OVERDUE: 'Overdue supplier invoice',
      CUSTOMER_CREDIT_OVERDUE: 'Overdue customer balance',
      CUSTOMER_CREDIT_LIMIT: 'Credit limit',
      SHIFT_DIFFERENCE: 'Cash difference',
      LARGE_WASTE: 'Large waste',
      BACKUP_FAILED: 'Backup failed',
    },
  },

  backups: {
    title: 'Backups',
    subtitle: 'Whether the shop is protected, and how to keep it that way.',

    status: {
      protected: 'Backed up',
      overdue: 'No recent backup',
      never: 'Never backed up',
      lastSuccess: 'Last good backup',
      hoursAgo: '{hours} hours ago',
      justNow: 'Less than an hour ago',
      warnAfter: 'A warning appears after {hours} hours.',
      failures: '{count} failed attempts recorded',
      lastAttemptFailed: 'The last attempt failed.',
      neverBody:
        'Nothing has been backed up yet. If this machine fails today, everything in it is gone.',
    },

    run: {
      title: 'Back up now',
      body: 'The server will write a dump file into its backup folder.',
      button: 'Back up now',
      working: 'Backing up…',
      disabled: 'Automatic backups are switched off on this server.',
      disabledBody:
        'Take a backup using the command below, then record it here. This is the safer setup: the server never holds the ability to read the whole database out to a file on request.',
    },

    manual: {
      title: 'Take a backup by hand',
      body: 'Backups run on their own every 15 minutes. To take one at the terminal, or to restore, follow the written procedure.',
      seeDocs: 'The exact commands are in docs/backup-and-restore.md. They are kept there rather than on this screen so that the page stays about whether the shop is protected — and so that a restore, which replaces everything recorded since the backup was taken, stays a deliberate act.',
      record: 'Record a backup I took',
      recordBody: 'So the screen stops warning you when you are already backing up properly.',
      fileName: 'File name',
      fileNamePlaceholder: 'yaghi-backup-2026-07-30.dump',
      note: 'Note',
      save: 'Record it',
      saving: 'Recording…',
    },

    restore: {
      title: 'Restoring',
      body: 'Restoring replaces everything and cannot be undone, so it is done at the terminal by a person who has decided to do it — never by a button.',
      link: 'Read the restore instructions',
    },

    history: {
      title: 'History',
      empty: 'No backups recorded yet.',
      outcome: 'Result',
      file: 'File',
      size: 'Size',
      duration: 'Took',
      by: 'By',
      when: 'When',
      succeeded: 'Succeeded',
      failed: 'Failed',
      seconds: '{count}s',
      automatic: 'Scheduled',
    },

    security: {
      title: 'A note on safety',
      body: 'Backup files are never downloadable through this website, and the database password is never shown. Keep your backups on a different disk from the one PostgreSQL runs on.',
    },
  },

  ownerDashboard: {
    title: 'Today',
    subtitle: 'How the shop is doing.',
    ranges: {
      today: 'Today',
      week: 'Last 7 days',
      month: 'Last 30 days',
    },
    takings: 'Takings',
    attention: 'Needs attention',
    topProducts: 'Best sellers',
    noSales: 'No sales in this period.',
    lowStockCount: '{count} products low or out',
    expiringCount: '{count} batches expiring soon',
    supplierOverdue: 'Owed to suppliers',
    customerOverdue: 'Owed by customers',
    allClear: 'Nothing needs attention.',
    openReports: 'Open the full reports',
    takingsHidden: 'Sales figures are not shown for your role.',
  },

  // --- Phase 9 -------------------------------------------------------------


  // --- operational changes -------------------------------------------------

  held: {
    hold: 'Hold order',
    holdTitle: 'Hold this order',
    holdBody: 'The cart is saved and the till is cleared, so you can serve the next customer.',
    heldOrders: 'Held orders',
    heldCount: 'Held orders ({count})',
    label: 'Label',
    labelHint: 'Anything you will recognise: Ahmad, waiting.',
    customerName: 'Customer name',
    phone: 'Phone',
    note: 'Note',
    chooseCustomer: 'Choose a customer',
    walkIn: 'Walk-in',
    confirmHold: 'Hold it',
    holding: 'Holding...',
    heldState: 'Held',

    nothingHeld: 'No held orders.',
    nothingHeldHint: 'Hold a cart when a customer is not ready, and it will wait here.',
    restore: 'Restore',
    restoring: 'Restoring...',
    cancel: 'Cancel cart',
    cancelTitle: 'Cancel this held cart?',
    cancelBody: 'Nothing has been sold, so nothing is reversed. The cart is simply removed.',
    cancelReason: 'Why is it being cancelled?',
    cancelConfirm: 'Cancel it',
    items: '{count} items',
    heldBy: 'Held by {name}',
    heldAt: 'Held {when}',
    mine: 'Mine',
    everyone: 'Everyone',
    cartNotEmpty: 'Finish or clear the current order before restoring another.',
    restored: 'Cart restored',
    restoredBody: 'The order is back on the till.',

    warningsTitle: 'This cart has changed since it was held',
    warningsBody: 'Check these before taking payment.',
    priceChanged: '{name}: was {was}, now {now}',
    unavailable: '{name} is no longer available and has not been added.',
    insufficient: '{name}: only {available} left, {requested} was wanted.',
    heldTotal: 'Held total',
    currentTotal: 'Total today',
    acknowledge: 'I have told the customer',
    acknowledgeHint: 'Tick to continue with the prices as they are today.',
  },

  delivery: {
    receivingForTitle: 'Receiving for {name}',
    receivingForBody: '{name} sent this delivery out, so the money goes into their drawer ({shift}), not yours.',
    receiveFor: 'Receive for {name}',
    accountsAllCashiers: 'All cashiers ({count})',
    accountsScopeHint: 'Owner and manager view. A cashier sees only the deliveries they sent out.',
    fee: 'Delivery fee',
    feeNoRate: 'No exchange rate has been set, so a fee in lira cannot be converted.',
    amountConverted: '{name}: {amount} becomes {quantity} on the preparation ticket.',
    itemsSubtotal: 'Items',
    finalTotal: 'Total to pay',

    stockReserved: 'Stock reserved, not deducted. It comes off when the order is paid.',
    newOrder: 'Next order',

    openInPos: 'Open POS in delivery mode',
    collectPayment: 'Collect payment',
    reopened: 'Order {number} reopened',
    reopenedBody: 'Take payment to complete it. The items are already on the till.',
    alreadyPaid: 'That order has already been paid for.',
    unpaid: 'Unpaid',
    paid: 'Paid',

    // --- dispatching a delivery from the Current Order ---
    dispatchTitle: 'Create delivery order',
    dispatchConfirm: 'Create delivery order',
    dispatching: 'Creating...',
    dispatchHint:
      'The customer receipt prints now and goes with the driver. No payment is recorded until the account is received.',
    dispatchFailed: 'The delivery could not be created',
    dispatched: 'Delivery created',
    dispatchedTitle: 'Delivery on its way',
    dispatchedBody: 'Order {number} was created and its receipt printed for the driver.',
    awaitingAccountHint:
      'No payment has been recorded. The delivery waits in POS Delivery Accounts until its account is received.',
    viewDeliveries: 'Go to Delivery Accounts',
    feeMakesDelivery: 'The delivery fee makes this order a delivery order.',
    useStandingFee: 'Usual fee: {amount}',
    noPermission: 'You do not have permission to create delivery orders.',
    offlineBlocked: 'A delivery cannot be created without a connection to the server.',
    collectInView: 'Collect this delivery\u2019s account from POS Delivery Accounts.',

    // --- the account, while it is out and when it returns ---
    awaitingAccount: 'Awaiting account',
    accountReceived: 'Account received',

    // --- the POS order column's two views ---
    posViewLabel: 'Order column view',
    currentOrderTab: 'Current order',
    accountsTab: 'Delivery accounts',
    pendingCountLine: '{count} awaiting account',
    viewAllOrders: 'View all orders',
    collectAccount: 'Receive account',
    leftAt: 'Left:',
    orderRef: 'Delivery order',
    receiptRef: 'Receipt:',
    noneAwaiting: 'No accounts pending',
    noneAwaitingHint: 'Every dispatched delivery has brought its money back.',
  },

  kitchen: {
    ticket: 'Preparation ticket',
    ticketNumber: 'Ticket {number}',
    print: 'Print ticket',
    reprint: 'Print another copy',
    reprinting: 'Printing...',
    copyNumber: 'Copy {number}',
    noTicket: 'Nothing on this order needs cutting.',
    noTicketHint: 'A preparation ticket is only produced for categories routed to the kitchen.',
    forPreparation: 'FOR PREPARATION',
    deliveryHeading: 'DELIVERY',
    urgentHeading: 'URGENT',
    printedAt: 'Printed',
    notABill: 'This is not a receipt. No prices are shown.',
    orderTypeLabel: 'Type',
    wantedBy: 'Wanted by',

    // --- the ticket's own labels ---------------------------------------
    // Owned here rather than borrowed from the orders screen and the receipt.
    // A label shared with another screen follows THAT screen's language, which
    // is how an Arabic ticket ended up printing "Customer" and "Cashier".
    customerLabel: 'Customer',
    onlineOrderLabel: 'Online order',
    phoneLabel: 'Phone',
    addressLabel: 'Address',
    cashierLabel: 'Cashier',
    orderNumberLabel: 'Order number',
    saleNumberLabel: 'Sale number',
    preparationStatusLabel: 'Preparation status',
    categoryLabel: 'Category',
    weightLabel: 'Weight',
    quantityLabel: 'Quantity',
    notesLabel: 'Notes',
    cuttingInstructionsLabel: 'Cutting instructions',
    orderNotesLabel: 'Order notes',

    // --- the sample ticket the designer previews with -------------------
    // The order type as a WORD, not as the system's enum. The boxed heading
    // above stays "DELIVERY" in every language; this row is prose and reads in
    // the language of the paper.
    typeCOUNTER: 'Counter',
    typeDELIVERY: 'Delivery',
    typePICKUP: 'Collection',
    typeON_TABLE: 'On table',
    /// Under the big Latin banner word, in the paper's own language.
    fulfilmentDelivery: 'Delivery',
    fulfilmentPickup: 'Collection',
    fulfilmentOnTable: 'On table',
    tableWord: 'Table',

    sampleCustomer: 'Sample Customer',
    sampleCashier: 'Sample Cashier',
    sampleOrderNote: 'Ring the bell twice',
    printedBy: 'Printed by {name}',
    reprintDenied: 'You do not have permission to print another copy. Ask a manager.',
  },

  printRoutingSection: {
    title: 'Print routing',
    intro: 'Which printer receives each kind of paper.',
    /// Under the receipt selector, saying what the choice governs.
    receiptExplain: 'The full sale receipt prints on this printer.',
    destination: 'Destination printer',
    choose: 'Choose a printer',
    notSet: 'No printer has been chosen for customer receipts yet.',
    /// Preparation is routed per category; this section explains that instead
    /// of pretending there is one preparation printer to pick.
    prepExplain: 'The preparation printer is chosen per category, in category settings.',
    manageCategories: 'Manage categories',
  },

  printerManager: {
    title: 'Printers',
    /// Describes the system as it now is: any number of printers, each on its
    /// own kind of cable. The copy this replaces said "the two printers are on
    /// the shop network", which stopped being true in both halves.
    intro: 'Add the shop’s printers and say how each one is connected and what it is used for.',
    refreshPrinters: 'Refresh list',
    noPrintersFound: 'Windows has no printers installed on this machine.',
    shopPcNotReported: 'The shop PC has not sent its printer list yet. Make sure the print agent is running on the till PC, then press Refresh list.',
    shopPcReported: 'Printers installed on the shop PC · updated {time}',
    add: 'Add printer',
    edit: 'Edit printer',
    empty: 'No printers have been added yet.',
    name: 'Printer name',
    namePlaceholder: 'Grill kitchen',
    nameHint: 'What you call this printer in the shop. You can change it at any time.',
    connection: 'Connection',
    usb: 'USB / Windows',
    usbHint: 'Plugged into this computer and installed in Windows.',
    network: 'Network / IP',
    networkHint: 'On the shop network, at its own address.',
    windowsPrinter: 'Installed printer',
    choosePrinter: 'Choose an installed printer',
    windowsUnavailable: 'Windows printers cannot be listed on this machine.',
    address: 'Address',
    port: 'Port',
    receiptPrinter: 'Receipts',
    receiptSet: 'Receipts will print here.',
    off: 'Off',
    switchOn: 'Switch on',
    switchOff: 'Switch off',
    switchedOn: 'Printer switched on.',
    switchedOff: 'Printer switched off.',
    /// The derived usage block. Every line of it is computed from the real
    /// routes - the receipt setting and the categories' own printer choices -
    /// never stored as separate state that could disagree with them.
    usage: 'Used for:',
    usageReceipt: 'Customer Receipt',
    usagePrepOne: 'Preparation: 1 category',
    usagePrepTwo: 'Preparation: 2 categories',
    usagePrepFew: 'Preparation: {count} categories',
    usagePrepMany: 'Preparation: {count} categories',
    unused: 'Not currently used',
    retire: 'Remove',
    retired: 'The printer is no longer in use.',
    retireTitle: 'Stop using this printer?',
    retireMessage:
      'It stays on everything it has already printed. If categories still send preparation here, move them first.',
    saved: 'The printer has been saved.',
    added: 'The printer has been added.',
    testConnection: 'Test connection',
    testing: 'Testing…',
    /// TRUTHFUL WORDING. A socket that answered, and a queue Windows knows
    /// about, are both a long way short of paper coming out - so neither of
    /// these sentences says it did.
    testConnected: 'Connection successful.',
    testAvailable: 'Available in Windows.',
    testFailed: 'Failed to connect.',
    testNotFound: 'Windows does not have a printer with that name.',
    testNotConfigured: 'This printer has not been set up yet.',
    testDisabled: 'This printer is switched off.',
    testUnsupported: 'Windows printing is only available on the shop machine.',
    // This server cannot reach a printer in the shop, so the only honest
    // thing it can report is whether the shop's agent is alive.
    testAgentRunning: 'The shop print agent is running. Print a receipt to test the printer itself.',
    testAgentSilent: 'The shop print agent is not running, so nothing can print.',
    /// Shown on the Category form.
    preparationPrinter: 'Preparation printer',
    preparationPrinterHint: 'Which printer receives this category’s preparation tickets.',
    choosePreparation: 'Choose a printer',
    preparationRequired: 'Choose which printer prepares this category’s items.',
    notAssigned: 'Preparation printer not assigned',
    /// A route that names a printer which is switched off or gone. NOT the
    /// same state as "not assigned": something was chosen, and it stopped
    /// being usable - the fix is a person choosing again, never a guess.
    printerUnavailable: 'The selected printer is unavailable',
    printerUnavailableFix: 'The selected printer is unavailable. Choose another one.',
    /// Suffix on the dead option kept visible in the dropdown.
    unavailableSuffix: 'unavailable',
    preparationGoesTo: 'Preparation: {name}',
    noReceiptPrinter: 'No receipt printer chosen yet',
  },

  printRouting: {
    label: 'Print destination',
    hint: 'Where products in this category are printed when they are sold.',
    CASHIER_ONLY: 'Receipt only',
    KITCHEN_AND_CASHIER: 'Preparation + Receipt',
    cashierOnlyHint: 'Receipt only. For drinks and packaged goods.',
    kitchenHint: 'Receipt and a preparation ticket for the butcher.',
    cannotChange: 'Only a manager can change where a category prints.',
    /// The line above the two cards that states, in words, what is currently
    /// chosen. Colour alone cannot say this.
    currentStatus: 'Current status',
    /// On the chosen card. Deliberately a word, not only a tick.
    active: 'Active',
    /// Nobody has chosen yet. Never shown as "Cashier only".
    unset: 'Not set',
    unsetHint: 'This category has no print destination yet. Choose one below.',
    /// What the owner reads when they try to save without choosing.
    required: 'Please select a print destination for this category.',
    /// Informational only, never persisted per category: the customer receipt
    /// destination is one shop-wide setting, shown here so the owner sees the
    /// whole picture while routing a category.
    receiptInfo: 'Customer receipt: prints at {name} (a shop-wide setting)',
    receiptInfoUnset: 'Customer receipt: no printer chosen yet (a shop-wide setting)',
  },

  reservations: {
    title: 'Reserved stock',
    subtitle: 'Meat promised to orders that have not been paid for.',
    empty: 'Nothing is reserved.',
    emptyHint: 'Stock is reserved when an order is sent to preparation, and released when it is paid or cancelled.',
    product: 'Product',
    quantity: 'Reserved',
    order: 'Order',
    customer: 'Customer',
    since: 'Since',
    onHand: 'On hand',
    available: 'Available',
    reservedShort: 'reserved',
  },

  designer: {
    // --- the receipt designer -------------------------------------------
    receiptTitle: 'Receipt designer',
    receiptBody:
      'Arrange the customer receipt and watch it change as you go. What you see is the receipt itself, not a picture of one.',

    whichDesign: 'Design',
    designName: 'Name',
    duplicate: 'Make a copy',
    copied: 'Copied. The copy is not active until you make it active.',
    activeMark: 'in use',
    thisIsActive: 'This is the design the till prints with.',
    notActive: 'This design is not in use. The till still prints with the active one.',
    makeActive: 'Use this design',
    nowActive: 'The till will print with this design from now on.',
    saveFirst: 'Save your changes first.',

    livePreview: 'Live preview',
    zoomLabel: 'Preview size',
    zoomFit: 'Fit',
    actualSize: 'Shown at {zoom}. The paper is {width} mm wide whatever the zoom says.',
    previewIsReal:
      'This is the same receipt the till prints, filled with a sample sale. Arrange it here and the paper matches.',
    printSample: 'Print this sample',
    printA4: 'Print A4 test sheet',
    printThermal: 'Print thermal test ({width} mm)',
    printTestNote:
      'Both print exactly what is on screen above, including changes you have not saved. The A4 sheet places the receipt at its real {width} mm width on an ordinary page - it is not stretched to fill it. For the thermal test, pick the matching roll size in the printer dialog.',

    unsaved: 'Unsaved layout changes',
    saveVersion: 'Save as new version',
    savedVersion: 'Saved. Receipts already printed keep the layout they were printed with.',
    resetDefaults: 'Back to the standard layout',

    versionsTitle: 'Versions',
    versionsBody:
      'Every save keeps the old layout. A receipt reprinted next year comes out exactly as the customer first received it.',
    versionN: 'Version {n}',
    inUse: 'printing now',

    // --- groups ----------------------------------------------------------
    groupPaper: 'Paper and text',
    groupHeader: 'Top of the receipt',
    groupIdentity: 'Which sale this is',
    groupLines: 'The items',
    groupMoney: 'The money',
    groupFooter: 'Bottom of the receipt',
    groupPrinting: 'Printing',

    template: 'Starting layout',
    templateClassic: 'Classic',
    templateModern: 'Modern',
    templateCompact: 'Compact (saves paper)',
    templateBilingual: 'Arabic and English',
    paperWidth: 'Roll width',
    language: 'Printed language',
    bothLanguages: 'Both',
    fontSize: 'Text size',
    sizeSmall: 'Small',
    sizeMedium: 'Medium',
    sizeLarge: 'Large',
    density: 'Spacing',
    densityComfortable: 'Comfortable',
    densityCompact: 'Tight',
    divider: 'Divider lines',
    dividerSolid: 'Solid',
    dividerDashed: 'Dashed',
    dividerDotted: 'Dotted',
    dividerNone: 'None',
    alignLeft: 'Left',
    alignCenter: 'Centre',
    alignRight: 'Right',

    showLogo: 'Print the logo',
    logoSize: 'Logo size',
    logoAlignment: 'Logo position',
    showNameEn: 'Shop name in English',
    showNameAr: 'Shop name in Arabic',
    showAddress: 'Address',
    showPhone: 'Phone number',
    headerText: 'Line above the sale',
    headerTextHint: 'Printed under the shop details. Leave empty for none.',

    showReceiptNumber: 'Receipt number',
    showReceiptNumberHint:
      'How a customer finds this sale again. Turning it off makes a refund harder to trace.',
    showDateTime: 'Date and time',
    showCashierName: 'Who served the customer',
    showCustomerName: 'Customer name, when there is one',
    showShiftNumber: 'Shift number',
    showOrderNumber: 'Delivery order number',

    showWeight: 'Weight',
    showQuantity: 'Quantity',
    showUnitPrice: 'Price per kilo or piece',
    showSku: 'Product code',
    showItemNotes: 'Notes on a line',
    showItemNotesHint: 'Such as "cut into cubes".',

    showSubtotal: 'Subtotal',
    showDiscount: 'Discount',
    showDeliveryFee: 'Delivery fee',
    showTotalUsd: 'Total in dollars',
    noTotalWarning: 'The receipt will not show what the customer paid.',
    showTotalLbp: 'Total in lira',
    showExchangeRate: 'Exchange rate used',
    showExchangeRateHint:
      'Lets the customer check the arithmetic, and a later rate change cannot make the receipt look wrong.',
    showPaymentMethods: 'How it was paid',
    showChange: 'Change given',

    thankYouText: 'Thank-you line',
    footerText: 'Footer',
    footerTextHint: 'Opening hours, a phone number, a returns note.',
    footerAlignment: 'Footer position',
    showReceiptBarcode: 'Receipt number at the bottom',

    copies: 'Copies',
    copiesHint: 'How many times the print dialog is asked to print.',
    autoPrint: 'Open the print dialog automatically',
    autoPrintHint:
      'A web page cannot print without a dialog. This opens it for you; it does not skip it.',
  },

  kitchenDesigner: {
    title: 'Preparation ticket designer',
    body: 'Arrange the ticket the butcher works from. It carries no money, by design.',

    noMoneyPromise:
      'A preparation ticket never shows a price, a total or a currency. There is no switch for it on this screen because there is no such field on the ticket: it is a cutting instruction, and a price on it invites somebody to hand it to a customer as a bill.',

    thisIsActive: 'This is the design preparation tickets print with.',
    notActive: 'This design is not in use. Tickets still print with the active one.',
    nowActive: 'Preparation tickets will print with this design from now on.',
    savedVersion: 'Saved. Tickets already printed keep the layout they were printed with.',
    previewIsReal:
      'This is the same ticket the printer produces, filled with a sample order. Arrange it here and the paper matches.',
    versionsBody:
      'Every save keeps the old layout, so a ticket reprinted for a disputed order shows what the butcher was actually handed.',

    templateLarge: 'Large, for the block',

    groupReadability: 'Reading it across the shop',
    groupWho: 'Whose order this is',
    groupWhatToCut: 'What to cut',
    groupShouted: 'What to shout about',

    productNameSize: 'Product name size',
    boldProductName: 'Product name in bold',
    boldQuantity: 'Quantity in bold',
    boldQuantityHint: 'The number somebody checks twice before cutting.',

    showOrderNumber: 'Order number',
    showSaleNumber: 'Sale number',
    showOrderType: 'Counter, delivery or collection',
    showCustomerName: 'Customer name',
    showRequestedTime: 'When it is wanted',
    showRequestedTimeHint: 'The difference between preparing it now and preparing it at four.',
    showCashierName: 'Who took the order',

    showWeightOrQuantity: 'Weight or quantity',
    noQuantityWarning: 'The butcher will not know how much to cut.',
    showCuttingInstructions: 'Cutting instructions',
    showCuttingInstructionsHint: 'Such as "cut into 3 cm cubes, trim the fat".',
    showOrderNotes: 'Notes on the whole order',
    showCategory: 'Which section it comes from',

    showDeliveryHeading: 'Show order type',
    showDeliveryHeadingHint: 'Prints the order type prominently: delivery, pickup, or on table.',
    showUrgentHeading: 'Large URGENT heading',
    handwritingSpace: 'Blank lines to write on',
    handwritingSpaceHint:
      'Room for the butcher to write the finished weight. Zero for none.',

    copiesHint: 'One for the block, one for the bag, if that is how the shop works.',
  },

  printers: {
    limitTitle: 'A web page cannot choose a printer for you.',
    limitBody:
      'Whatever is set here, printing opens the browser dialog and somebody picks. That is how every browser works, on purpose. The settings below say which printer to pick and what paper it holds; they do not send anything anywhere on their own.',

    roleReceipt: 'Cashier receipts',
    roleKitchen: 'Preparation tickets',
    roleLabel: 'Product labels',

    name: 'What you call it',
    connection: 'How it is reached',
    connectionBrowser: 'Browser print dialog',
    connectionConnector: 'A small program on this computer',
    connectionKiosk: 'Browser started in kiosk mode',

    printerName: 'Printer name',
    printerNameNote: 'Printer to pick in the dialog (a note, not routing)',
    printerNamePlaceholder: 'e.g. EPSON TM-T20',
    paperWidth: 'Roll',
    copies: 'Copies',

    explainBrowser:
      'Works today, with nothing installed. The dialog opens and a person presses Print. The name above is only a reminder of which one to choose.',
    explainConnector:
      'This system does not include that program. Choosing this records what you intend to set up; nothing is installed and nothing changes until a real print reports back.',
    explainKiosk:
      'Needs the browser started with printing pre-approved, which is a change to how the till is launched, not a setting here. Until that is done, printing still opens the dialog.',

    statusUntested: 'never tested',
    statusWorked: 'printed',
    statusFailed: 'did not print',

    test: 'Print a test page',
    saveFirst: 'Save this printer first.',
    didItPrint: 'Did the test page come out of the printer?',
    testPassed: 'Recorded: this printer works.',
    testRecorded: 'Recorded: the page did not come out.',
    reportedNotPrinted: 'The person testing said nothing came out of the printer.',
    lastSuccess: 'Last printed successfully on {when}.',
    lastFailure: 'Last failed on {when}: {note}',

    saved: 'Printer saved.',
  },

  users: {
    intro:
      'Everybody who signs in, and what each of them may do. Roles cover the usual cases; an individual permission is an exception to a role and needs a reason.',
    listLabel: 'Employees',
    addPerson: 'Add somebody',
    createAccount: 'Create the account',
    created: 'Account created. Tell them their username and password.',

    fullName: 'Full name',
    username: 'Username',
    usernameHint: 'Lowercase letters, numbers, dot, dash or underscore. This is what they type to sign in.',
    firstPassword: 'First password',
    firstPasswordHint:
      'You are choosing it, so tell them what it is. They can change it themselves afterwards.',

    role: 'Role',
    roleChanged: 'Role changed.',
    status: 'Account',
    statusActive: 'Can sign in',
    statusSuspended: 'Cannot sign in',
    statusChanged: 'Account updated.',
    suspendedMark: 'suspended',

    /// Deletion is deliberately ONE dialogue. Only an owner sees the button,
    /// only an owner may call the endpoint, and only an account with no
    /// history at all is accepted - somebody created by mistake, or who never
    /// worked a shift. Ceremony on top of that protects nothing; it trains
    /// people to click through warnings.
    deleteUser: 'Delete user',
    deleteTitle: 'Delete user?',
    deleteMessage: 'Are you sure you want to permanently delete this user?',
    deleteConfirm: 'Yes, delete',
    deleteDone: 'The account has been deleted.',
    deleteHasHistory:
      'This account is part of the shop\u2019s records and cannot be deleted. Suspend it instead - they will not be able to sign in, and everything they did stays theirs.',
    deleteLastOwner:
      'This is the last active owner. The shop would be left with none, so this account cannot be deleted.',

    noDeleteBody:
      'Accounts are never deleted. Somebody who has left is suspended instead: they cannot sign in, and every sale, shift and approval they made stays theirs. Deleting the account would take that history with it.',

    cannotChangeSelf:
      'This is your own account. You cannot change your own role or permissions - ask another owner. Everything else here is yours to edit.',

    lastSignedIn: 'Last signed in {when}.',
    neverSignedIn: 'Has never signed in.',

    signInTitle: 'Signing in',
    newPassword: 'New password',
    setPassword: 'Set password',
    passwordChanged: 'Password changed. Tell them what it is.',
    passwordNote:
      'There is no email on this system, so there is no reset link. You set the password and tell the person.',
    pinSet: 'Manager PIN (one is set)',
    pinNotSet: 'Manager PIN (none set)',
    setPinButton: 'Set PIN',
    removePin: 'Remove PIN',
    pinChanged: 'PIN set.',
    pinRemoved: 'PIN removed.',
    pinNote:
      'The PIN approves a discount or a void at the till without signing out. Only give one to somebody who should be approving those.',

    exceptionsTitle: 'Individual permissions',
    exceptionsBody:
      'These are exceptions to the {role} role. Use them sparingly: a role everybody has exceptions to is a role that no longer describes anything.',
    noExceptions: 'No exceptions. This person can do exactly what the {role} role allows.',
    exceptionCount: '{count} exception(s)',
    addException: 'Add an exception',
    whichPermission: 'Which permission',
    pickPermission: 'Pick one',
    alreadyInRole: 'already in the role',
    whatKind: 'Add it or take it away',
    effectAllow: 'added',
    effectDeny: 'removed',
    effectAllowLong: 'Let them do this, even though the role does not',
    effectDenyLong: 'Stop them doing this, even though the role allows it',
    reason: 'Why',
    reasonPlaceholder: 'e.g. covers the counter alone on Sundays',
    reasonWhy:
      'Somebody will ask about this in a year. Write what you would want to read then.',
    exceptionSaved: 'Exception saved.',
    exceptionRemoved: 'Back to what the role allows.',
    backToRole: 'back to the role',
    denyWins:
      'If a permission is both allowed by the role and removed here, it stays removed. Taking something away always wins.',
  },

  backupTargets: {
    intro:
      'A complete copy of the shop\u2019s data, taken every 15 minutes. Choosing a folder does not prove it works, so test it.',
    add: 'Add a destination',
    noneYet:
      'No destination is set. Backups have nowhere to go, and you will find that out on the day you need one.',

    name: 'Destination name',
    kind: 'Backup destination',
    kindLocal: 'This computer',
    kindSynced: 'OneDrive Sync',

    explainLocal:
      'A folder on this computer, or on a drive plugged into it. Worth having against a mistake, and worth nothing against a fire \u2014 it is in the same room as the till.',
    explainSynced:
      'A folder inside the OneDrive that Windows already keeps in step with the cloud. This system writes the file; the OneDrive application uploads it. That is why it needs no Microsoft account, no password and no setup beyond being signed in to OneDrive.',

    folder: 'Folder',
    folderPlaceholder: 'D:\\Yaghi Backups',
    folderHint: 'Start from the drive letter. The folder is created if it is not there yet.',

    oneDriveDetected: 'OneDrive detected',
    oneDriveChooseRoot: 'Which OneDrive?',
    oneDriveMissing:
      'OneDrive is not configured on this computer. Sign in to the Windows OneDrive application, then try again.',
    oneDriveUseSuggested: 'Use {folder}',
    oneDriveOutside:
      'That folder is not inside OneDrive, so nothing written there would be uploaded.',
    oneDriveSyncNote:
      'Windows OneDrive uploads these files when it is connected. This system writes them and does not talk to Microsoft, so it cannot say when an upload has finished \u2014 and does not pretend to.',
    oneDriveDeletionNote:
      'When an old backup is removed here, Windows OneDrive may remove it from the cloud too. That is how a synchronised folder works.',

    automatic: 'Automatic backups',
    frequency: 'Frequency',
    everyFifteen: 'Every 15 minutes',
    keep: 'Backups kept',
    keepLatestFour: 'Latest 4 successful backups',
    keepHint:
      'An older backup is removed only after a new one has been written and checked. A backup that fails removes nothing.',
    makeActive: 'Write backups here from now on',

    inUse: 'in use',
    useThis: 'Use this one',
    nowActive: 'Backups will be written here.',
    saved: 'Destination saved.',
    removed: 'Removed. Backups already written were not touched.',

    check: 'Test destination',
    checkRecorded: 'Tested. The result is recorded either way.',
    checkReadyOneDrive:
      'Backup folder is ready. OneDrive will synchronize backup files when the Windows OneDrive client is connected.',
    statusUnchecked: 'never tested',
    statusOk: 'ready',
    statusFailed: 'cannot write there',
    lastCheck: 'Tested {when}: {message}',

    summary: 'Every 15 minutes. Keeps the latest 4 successful backups.',
    sameDiskWarning:
      'This is on the same computer as the database. If the disk fails, this copy fails with it.',

    noRestoreHere:
      'There is no restore button here, on purpose. Restoring replaces everything the shop has recorded since the backup was taken, so it is done by a person at the keyboard following the written procedure \u2014 not by a button that can be pressed by mistake.',
  },

shiftReport: {
    title: 'SHIFT CLOSING REPORT',
    settingsTitle: 'Shift Closing Report',
    settingsBody: 'What prints when a shift is closed, and whether it prints on its own.',

    shift: 'Shift',
    cashier: 'Cashier',
    opened: 'Opened',
    closed: 'Closed',
    duration: 'Duration',

    salesSummary: 'SALES SUMMARY',
    grossSales: 'Gross Sales',
    discounts: 'Discounts',
    netSales: 'Net Sales',
    saleCount: 'Sales',
    averageSale: 'Average Sale',

    payments: 'PAYMENTS',
    cash: 'Cash',
    credit: 'On account',
    transactions: 'Transactions',
    totalPayments: 'Total Payments',

    cashDrawer: 'CASH DRAWER',
    openingCash: 'Opening',
    expectedCash: 'Expected',
    countedCash: 'Counted',
    difference: 'Difference',
    result: 'Status',
    resultExact: 'EXACT',
    resultOver: 'OVER',
    resultShort: 'SHORT',

    voids: 'Voided sales',
    expenses: 'EXPENSES',
    expenseCount: 'Number of expenses',
    closingNote: 'Closing note',
    deliveryHeading: 'Deliveries',
    deliveriesDispatched: 'Deliveries sent out today',
    deliveriesCollected: 'Accounts received',
    deliveriesPending: 'Awaiting account',
    deliveryFees: 'Delivery fees',

    closedBy: 'Closed by',
    printedAt: 'Printed',
    reprintMark: 'REPRINT',

    viewReport: 'View Shift Report',
    reprintReport: 'Reprint Shift Report',
    reportForShift: 'Shift report',
    livePreviewNote: 'This shift is still open — these figures are a live preview, not the final report.',
    frozenNote: 'These figures were frozen when the shift closed. They never change.',

    autoPrint: 'Automatically print when shift closes',
    copies: 'Copies',
    copiesHint: 'One to three. The report is for the drawer and maybe the office.',
    sectionHeader: 'Header',
    sectionSales: 'Sales',
    sectionPayments: 'Payments',
    sectionDrawer: 'Cash drawer',
    sectionOptional: 'Optional sections',
    sectionFooter: 'Footer',
    footerTextLabel: 'Footer text',

    printQueued: 'The shift report was sent to the receipt printer.',
    printFailed:
      'Shift closed successfully. The Shift Report could not be printed and is available for retry.',
    reprintQueued: 'The report copy was sent to the receipt printer.',
    reprintFailed: 'The report copy could not be printed. It is saved and can be retried.',
  },


  networkPrint: {
    toCashier: 'Print this draft to the receipt printer',
    toKitchen: 'Print this draft to a preparation printer',
    /// The designer's bench picker. A choice, never a default: with several
    /// preparation printers there is nothing honest to preselect.
    testPrinterLabel: 'Test printer',
    chooseTestPrinter: 'Choose a printer for the test',
    preparing: 'Preparing the document...',
    queued: 'Queued for the printer.',
    sending: 'Sending to the printer...',
    sent: 'Sent to printer.',
    sentMeaning: 'The bytes reached the printer. Check that the paper came out.',
    failed: 'Failed to send.',
  },

  /// The held-cart sheet. Its OWN block, not borrowed from the receipt: every
  /// string here exists to say this is not a receipt, and a word shared with
  /// the receipt could be changed there and quietly weaken this.
  heldSheet: {
    banner: 'HELD ORDER',
    notPaid: 'NOT PAID',
    reference: 'Hold number',
    printed: 'Printed',
    cashier: 'Cashier',
    customer: 'Customer',
    subtotal: 'Subtotal',
    deliveryFee: 'Delivery fee',
    amountDue: 'Amount due',
    notes: 'Notes',
    notASale: 'This is not a sale and not a receipt.',
    stockNotDeducted: 'Nothing has been paid and no stock has been taken.',

    /// The action, on screen.
    print: 'Print held order',
    printing: 'Sending',
    sent: 'Held order sent to the printer.',
    failed: 'Held order was not sent to the printer.',
    retry: 'Retry',
    noPrinter: 'No receipt printer has been chosen yet.',
    unchanged: 'The held order has not changed. It is still waiting.',
  },

  /// The one connectivity badge and the details behind it. Its own block:
  /// the badge speaks about the SERVER, and borrowing a word from the offline
  /// queue would let a change there quietly alter what the badge claims.
  /// Modules that are paused, not abandoned. Their tables, models,
  /// migrations, services and permissions are all still in place - only the
  /// operational buttons are withdrawn, because a half-finished action on a
  /// screen a shop uses daily is worse than an honest gap.
  /// The permission editor. It shows EFFECTIVE permissions - what the person
  /// can actually do - rather than the exceptions that produce them, because
  /// the exceptions are the one thing that is not the answer.
  /// The permission catalogue's own group keys, named for a person.
  comingSoon: {
    badge: 'Coming Soon',
    dataSafe: 'Anything already recorded is safe and will still be here.',
    backToPos: 'Back to Point of Sale',
    backToDashboard: 'Back to Dashboard',

    production: 'Production planning will be available in a future update.',
    recipes: 'Recipe management will be available in a future update.',
    labels: 'Product label printing will be available in a future update.',
    customers:
      'The customers screen is paused for now. Customer records, credit and history are all preserved, and the till can still pick a customer for a credit sale.',
  },

  connectivity: {
    title: 'Connection',
    openDetails: 'Connection details',
    serverFine: 'The till is talking to the shop server.',

    syncing: 'Syncing',
    attention: 'Attention',

    backend: 'Shop server',
    reachable: 'Reachable',
    unreachable: 'Unreachable',
    checking: 'Checking',

    device: 'This device',
    hasNetwork: 'Network up',
    noNetwork: 'No network',

    printerConnected: 'Connected',
    printerDisconnected: 'Disconnected',
    printerNotSetUp: 'Not set up',

    pending: 'Waiting to sync',
    failed: 'Refused',
    lastSync: 'Last sync',
    never: 'Not yet',
  },

  salePrint: {
    // --- the green status box that used to sit over the payment controls ---
    //
    // NOTHING RENDERS THESE ANY MORE. Printing feedback is a toast and only a
    // toast; the second surface was removed because it said the same fact
    // twice, in the corner of the till the cashier was not looking at.
    //
    // They are kept because `salePrinting.test.tsx` uses them as the
    // definition of what must not appear on the page again. Deleting them
    // would replace a dictionary reference with a dozen Arabic strings copied
    // into a test file, where nobody would recognise them for what they are.
    cashierLabel: 'Customer receipt',
    kitchenLabel: 'Preparation ticket',

    preparing: 'preparing',
    sending: 'sending to the printer',
    queuedForRetry: 'queued for retry',
    onItsWay: 'Sent to the shop printer - it will come out in a moment.',
    sent: 'sent to printer',

    cashierFailed: 'was not sent to the receipt printer. The sale is completed and recorded.',
    kitchenFailed: 'WAS NOT SENT. Nobody at the block knows about this order — tell them, or retry.',

    retry: 'Retry',
    retryStarted: 'Retry started.',
    retryFailed: 'Retry failed. The printer still cannot be reached.',

    browserFallback:
      'You can still print the receipt from the browser using the button on the receipt itself.',

    notConfiguredCashier: 'No receipt printer has been chosen yet.',

    /// A deliberate second copy, not a retry of a job that failed.
    /// Said once, as a toast, the moment a document settles. The wording
    /// stops at "sent to printer": a raw socket returns no acknowledgement,
    /// so claiming paper came out would be inventing a fact the cashier
    /// would only discover was false when the customer asked.
    cashierSent: 'Customer receipt sent to printer.',
    kitchenSent: 'Preparation ticket sent.',
    kitchenSentAt: 'Preparation ticket sent to {name}.',
    cashierQueued: 'Customer receipt queued for retry.',
    // NOT "queued for retry". Nothing is retrying: the shop's print agent is
    // the only thing that can produce this paper and it has not asked for work.
    // The cashier needs to know to go and start it, not to keep waiting.
    agentSilentToast: 'Nothing will print: the shop print agent is not running.',
    agentSilentDetail: 'The document is saved and will print as soon as the agent is started.',
    kitchenQueued: 'Preparation ticket queued for retry.',
    kitchenQueuedAt: 'Preparation ticket for {name} queued for retry.',
    cashierFailedToast: 'Customer receipt was not sent.',
    kitchenFailedToast: 'Preparation ticket was not sent.',
    kitchenFailedAt: 'Preparation ticket for {name} was not sent.',
    uncertainToast: 'Print result is uncertain. Check the printer before reprinting.',

    sendAgain: 'Send to printer',
    sendingAgain: 'Sending another copy',
    sentAgain: 'Another copy was sent to the printer.',
    sendAgainFailed: 'That copy was not sent. The printer could not be reached.',
  },

  settings: {
    title: 'Settings',
    subtitle: 'How this shop works. Every switch here is read by something.',
    noPermission:
      'Only an owner or a manager can open Settings. Ask one of them if you need a change made.',

    save: 'Save',
    saving: 'Saving...',
    saved: 'Saved.',
    revert: 'Undo changes',
    unsavedMark: 'unsaved',
    unsavedCount: '{count} unsaved',
    unsavedBanner:
      'You have {count} unsaved change(s). They are not applied until you save the section they are in.',
    mustBeNumber: 'That must be a number.',
    tooLow: 'That cannot be less than {min}.',
    tooHigh: 'That cannot be more than {max}.',
    tooLong: 'That is too long. The most it can hold is {max} characters.',
    range: '{min} to {max}',
    updatedNote: 'Showing this page in {locale}. Settings apply to the shop, not to one person.',

    navLabel: 'Settings sections',
    saveChanges: 'Save changes',
    lastSaved: 'Last changed {when}',
    savedBy: 'by {name}',
    destinationsTitle: 'Where backups are written',
    approvalsTitle: 'What needs approval',
    approvalsBody:
      'The thresholds a cashier can work within, and where a manager has to agree. These are yours to set.',
    signInPolicyTitle: 'Signing in',
    signInPolicyBody:
      'Password length, PIN length, lockout and session lifetime. Read-only here: they live in backend/.env and are read once when the server starts.',
    ownerOnlyMark: 'owner only',
    ownerOnlyNote: 'Only the owner can change this. Ask them if it needs changing.',
    noDefaultCategory: 'All products',
    categoryGone:
      'The category this was set to no longer exists. Pick another, or leave it on All products.',
    usersTitle: 'People and permissions',
    usersBody: 'Who can sign in, what their role allows, and any exceptions to it.',
    designerReceiptTitle: 'Receipt designer',
    designerKitchenTitle: 'Preparation ticket designer',
    designerKitchenBody:
      'Arrange the ticket the butcher works from. It carries no money, by design.',
    designerReceiptBody:
      'Arrange the customer receipt and watch it change as you go.',
    searchPlaceholder: 'Search settings',
    searchClear: 'Clear search',
    searchNothing: 'Nothing here matches "{query}".',

    scaleComingSoonTitle: 'Coming soon',
    scaleNeedsHardwareTitle: 'What it is waiting for',
    scaleNeedsHardwareBody:
      'A scale fitted at this counter and tested against it. The reader is written and the settings are ready; neither is switched on until real hardware has been through it, because a weight nobody has checked is worse than no weight at all.',
    scaleComingSoonBody:
      'Reading the weight straight off a scale into the cart is not in service at this shop yet. It needs the actual scale on the counter to be fitted and tested, and that has not happened.',
    scaleComingSoonWhat:
      'What already works: the settings below are saved and read, and the connection test underneath really opens a serial port and shows what a scale is sending. What is not finished is the scale being wired into the till so nobody types the weight.',
    scaleTryTitle: 'Try a scale connection now',
    scaleTryBody:
      'A real test, not a demonstration. Needs Chrome or Edge and a scale on a serial or USB-serial port.',

    businessTitle: 'Business details',
    businessBody: 'The name, address and wording printed on every receipt.',
    logoTitle: 'Shop logo',
    logoBody:
      'Printed on receipts and preparation tickets, and shown in the menu. Replacing it is a file change, not a setting.',

    currencyTitle: 'Currency and exchange rate',
    currencyBody: 'Which currency leads, and what a dollar is worth today.',
    rateTitle: 'USD to LBP rate',
    rateBody: 'Used for every lira figure from the moment you save it.',
    rateCurrent: 'Now',
    ratePerUsd: 'LBP per USD',
    rateNew: 'New rate',
    rateSave: 'Set the rate',
    rateSaved: 'The rate has been updated.',
    rateFrozenNote:
      'Sales already rung up keep the rate they were sold at. Changing this does not rewrite them.',
    rateNoPermission: 'You cannot change the rate. Ask an owner or a manager.',
    rateHistory: 'Recent rates',
    rateCurrentBadge: 'current',

    posTitle: 'Point of sale',
    posBody: 'How the till behaves before anybody touches it.',
    categoryHint: 'Categories are created on the Products screen.',
    categoryLink: 'Manage categories',

    printingTitle: 'Printing',
    printingBody: 'Paper sizes, and what a browser can honestly do with a printer.',
    printRealityTitle: 'What a browser can and cannot do',
    printRealityBody:
      'This application lays a receipt out for your paper and opens the print dialog. It cannot choose the printer for you, and it cannot print silently in the background - no web page can. For tickets that appear at the back without anybody pressing anything, you need a local print connector installed on this computer, or the browser started in kiosk-printing mode. The printer names below are notes, to remind whoever is at the till which entry to pick.',
    printTestTitle: 'Test prints',
    printTestBody:
      'Each one opens the real layout at the size configured above, so you can check the paper before a customer is waiting.',
    printTestReceipt: 'Test cashier receipt',
    printTestTicket: 'Test kitchen ticket',
    printTestLabel: 'Test product label',
    printAgain: 'Print',
    testReceiptTitle: 'TEST RECEIPT',
    testItem: 'Sample line',
    testSample: 'Test print, not a real sale',
    testLabelProduct: 'Sample product',

    scaleTitle: 'Weighing scale',
    scaleBody: 'An optional convenience. The keypad is the guarantee.',
    scaleManualTitle: 'Typing the weight always works',
    scaleManualBody:
      'Manual entry is not a setting and cannot be switched off. Whatever happens to the scale, the browser or the cable, a cashier can read the weight off the display and type it in.',
    scaleStatus: 'Connection',
    scaleConnected: 'Connected',
    scaleConnecting: 'Connecting...',
    scaleDisconnected: 'Not connected',
    scaleError: 'Connection failed',
    scaleUnsupported: 'This browser cannot open a serial port',
    scaleConnect: 'Connect a scale',
    scaleDisconnect: 'Disconnect',
    scaleReading: 'Live reading',
    scaleStable: 'settled',
    scaleReadingHint:
      'If this shows a weight that matches the scale display, the connection is working.',
    scaleHonesty:
      'The reader understands a line of text ending in kg or g, which is what most serial bench scales send. We have not tested a shelf of models and do not claim any particular one works - connect yours and watch the reading above. Anything it cannot read shows as no reading, never as a wrong weight.',

    inventoryTitle: 'Inventory rules',
    inventoryBody: 'Warnings, and how large a correction needs a second pair of eyes.',
    reservationRule:
      'Stock promised to an unpaid delivery order is reserved, not deducted: available stock is what is on hand minus what is reserved. A held cart reserves nothing. This is how the delivery feature works and is not configurable.',

    securityTitle: 'Security and approvals',
    securityBody: 'What needs a manager, and the sign-in policy this server runs.',
    policyTitle: 'Sign-in policy',
    policyPassword: 'Shortest password',
    policyPin: 'PIN length',
    policyLockout: 'Lockout',
    policyLockoutValue: '{attempts} failed attempts, then {seconds} seconds',
    policySession: 'Session lifetime',
    policyCharacters: 'characters',
    policyDigits: 'digits',
    policyMinutes: 'minutes',
    policyWhere:
      'These are read once when the server starts, from {file}. They are shown here so you know the policy; changing them means editing that file and restarting.',
    selfApprovalRule:
      'A manager cannot approve their own request. The approval asks for a PIN and refuses the person who asked.',

    shiftReportDesignerTitle: 'Shift Closing Report',
    shiftReportDesignerBody: 'What prints when a shift is closed, and whether it prints on its own.',
    backupTitle: 'Backup and system',

    // --- installing the POS as a Windows app ------------------------------
    installTitle: 'Install Yaghi Butcher POS on this computer',
    installBody:
      'Installing puts the Yaghi Butcher logo on the Desktop and Start Menu, and opens the POS in its own window without browser tabs.',
    installButton: 'Install the app',
    installAlready: 'You are already using the installed app.',
    installManually:
      'To install: open the browser menu (⋮ or …), choose "Apps", then "Install Yaghi Butcher POS".',
    backupBody: 'Whether the shop is protected, and whether everything is running.',
    backupAutomatic: 'Automatic backups',
    backupFrequency: 'Frequency',
    backupEveryFifteen: 'Every 15 minutes',
    backupDestination: 'Destination',
    backupKept: 'Backups kept',
    backupKeptValue: 'Latest 4 successful backups',
    backupLast: 'Last successful backup',
    backupNext: 'Next backup',
    backupDirectory: 'Folder',
    backupWritable: 'Ready',
    backupNotWritable: 'Not writable, backups will fail',
    backupUntested: 'Not tested yet',
    backupFailures: 'Failed backups',
    backupNone: 'No destination is set up yet, so nothing is being backed up.',
    backupNow: 'Backup Now',
    backupStarting: 'Starting...',
    backupRunning: 'Backing up...',
    backupInProgress: 'Backup already in progress',
    backupStarted: 'The backup has started.',
    backupSucceeded: 'Backup finished.',
    backupSavedOneDrive: 'Backup saved successfully to the OneDrive sync folder.',
    backupFailed: 'The backup did not finish. Backups already taken were not touched.',
    backupSkipped: 'A backup is already running. This one was not started.',
    backupCleanupWarning:
      'The backup is safe, but an older one could not be removed. It is only taking up space.',
    backupHistory: 'Backup history and restore instructions',
    backupNoPermission: 'You cannot see backup status. Ask an owner.',
    backupNoSecrets:
      'No database password or connection string is shown here or sent to this screen.',
    backupHealthTitle: 'System status',
    backupHealthNote:
      'A healthy database does not mean backups are working. These are two different questions and they are answered separately.',
    backupFolderStatus: 'Backup folder',
    healthApi: 'API',
    healthDatabase: 'Database',
    version: 'Version',
    restoreDocs: 'Restoring a backup',
    restoreDocsValue: 'See docs/backup-and-restore.md',

    notificationsTitle: 'Notifications',
    notificationsBody:
      'Which warnings the bell raises. Switching one off also clears the ones it already raised.',
  },

  offline: {
    online: 'Online',
    offline: 'Offline',
    /// The longer form, for the queue screen and the badge's tooltip, where
    /// there is room to say the thing that actually reassures a cashier.
    offlineDetail: 'No connection — sales are being saved on this till',
    syncing: 'Sending sales…',
    syncNow: 'Send now',
    waiting: '{count} waiting to be sent',
    waitingItem: 'Waiting to be sent',
    someFailed: '{count} could not be sent',
    openQueue: 'View',

    queueTitle: 'Sales waiting to be sent',
    queueSubtitle: 'Sales rung up while this till had no connection.',
    emptyQueue: 'Nothing is waiting.',
    emptyQueueHint: 'Every sale rung up on this till has reached the server.',
    allHandedOver: 'Everything has reached the server.',
    safetyNote:
      'A sale is saved on this till the moment it is completed. Sending it to the server is a separate step, and nothing is removed from this list until the server confirms it has the sale.',

    itemCount: '{count} items',
    attempts: 'Attempts: {count}',
    retry: 'Try again',
    retryAll: 'Try all {count} again',
    lastSync: 'Last sent {when} — {synced} recorded, {duplicates} already there',

    discard: 'Remove from the queue',
    discardConfirm: 'Remove this sale without recording it?',
    discardWarning:
      'The customer has already paid. Removing it here means the shop has no record of the sale at all. Do this only if it can never be recorded.',
    discardYes: 'Remove it anyway',

    /// The receipt of an offline sale, printed through the shop's print agent.
    receiptPrinted: 'Receipt {number} sent to the printer.',
    receiptNotPrinted: 'The receipt could not be sent to the printer. It can be printed from Sales once the connection is back.',
    noReceiptNumber: 'This till holds no reserved receipt number, so no receipt was printed. It can be printed from Sales once the connection is back.',

    updateTitle: 'A new version is ready',
    updateBody: 'It will be installed when you reload. Finish what you are doing first.',
    updateNow: 'Reload now',
    updateLater: 'Later',

    catalogueAge: 'Prices last updated {when}',
    catalogueMissing: 'This till has no saved product list yet, so it cannot sell offline.',
    catalogueSaved: 'Product list saved for offline use.',
    savingCatalogue: 'Saving the product list…',
    saveCatalogue: 'Save for offline use',

    newOrder: 'Next order',
    soldOffline: 'Saved on this till',
    soldOfflineBody:
      'The sale is recorded here and will be sent to the server as soon as the connection returns.',
    noShiftOffline: 'Open a shift before selling. A shift cannot be opened without a connection.',
    cannotOffline: 'This browser cannot save sales offline, so selling needs a connection.',
  },

  scale: {
    title: 'Weighing scale',
    manual: 'Type the weight',
    manualHint: 'Always available. Type the weight from the scale’s display.',
    connect: 'Connect a scale',
    connected: 'Scale connected',
    disconnect: 'Disconnect',
    reading: 'Reading…',
    stable: 'Steady',
    unstable: 'Still settling',
    lastReading: 'Last reading',
    use: 'Use this weight',
    notSupported: 'This browser cannot talk to a scale directly.',
    notSupportedBody:
      'Type the weight from the scale’s display instead. That is how most shops work, and nothing is lost by it.',
    permissionNeeded: 'Choose the scale’s port when the browser asks.',
    failed: 'The scale could not be read. Type the weight instead.',
    noScale: 'No scale connected.',
    methods: 'How scales can be connected',
  },

  labels: {
    title: 'Product labels',
    subtitle: 'Print a label with a barcode, a price and the dates.',
    product: 'Product',
    choose: 'Choose a product',
    weight: 'Weight',
    packedOn: 'Packed on',
    expiresOn: 'Expires on',
    shelfLifeDays: 'Shelf life (days)',
    price: 'Price',
    total: 'Total',
    copies: 'Copies',
    codeType: 'Code',
    barcode: 'Barcode',
    qr: 'QR code',
    size: 'Label size',
    preview: 'Preview',
    print: 'Print labels',
    noProduct: 'Choose a product to see the label.',
    noBarcode: 'This product has no barcode, so the product code is used instead.',
    printedBy: 'Printed by {name}',
  },
};
// Deliberately NOT `as const`. With `as const` every English string becomes its
// own literal type, so the Arabic dictionary below could never satisfy
// `typeof en` - "مرحباً" is not the literal type "Welcome". Leaving the values
// as plain strings still enforces exactly what matters: both dictionaries must
// have the same keys, nested the same way. A missing or misspelt Arabic key is
// a compile error.

export const ar: typeof en = {
  common: {
    edit: 'تعديل',
    remove: 'إزالة',
    appName: 'نظام مبيعات ملحمة ياغي',
    businessName: 'ملحمة ياغي',
    loading: 'جارٍ التحميل…',
    retry: 'إعادة المحاولة',
    cancel: 'إلغاء',
    save: 'حفظ',
    close: 'إغلاق',
    clear: 'مسح',
    continue: 'متابعة',
    search: 'بحث',
    signOut: 'تسجيل الخروج',
    signingOut: 'جارٍ تسجيل الخروج…',
    online: 'متصل',
    offline: 'غير متصل',
    yes: 'نعم',
    no: 'لا',
    none: 'لا يوجد',
    never: 'أبداً',
    all: 'الكل',
    back: 'رجوع',
    language: 'اللغة',
    english: 'English',
    arabic: 'العربية',
    skipToContent: 'تخطَّ إلى المحتوى',
    comingSoon: 'غير مُنجز بعد',
    phaseLabel: 'مخطط له في {phase}',
  },

  login: {
    welcome: 'مرحباً بك في نظام ملحمة ياغي',
    subtitle: 'Yaghi Butcher POS',
    tagline: 'سجّل الدخول لبدء ورديتك',
    usernameLabel: 'اسم المستخدم',
    usernamePlaceholder: 'مثال: cashier',
    usernameRequired: 'الرجاء إدخال اسم المستخدم.',
    passwordLabel: 'كلمة المرور',
    passwordPlaceholder: 'كلمة المرور',
    passwordRequired: 'الرجاء إدخال كلمة المرور.',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    submit: 'تسجيل الدخول',
    submitting: 'جارٍ تسجيل الدخول…',
    withPassword: 'كلمة المرور',
    withPin: 'رمز سريع',
    choosePerson: 'اختر اسمك',
    enterPin: 'أدخل الرمز السري',
    pinFor: 'تسجيل الدخول باسم {name}',
    changePerson: 'اختيار موظف آخر',
    clear: 'مسح',
    loadingEmployees: 'جارٍ تحميل الموظفين…',
    employeesFailed: 'تعذّر تحميل الموظفين. حاول مرة أخرى.',
    noPinUsers: 'لا يوجد موظف لديه رمز سريع. الرجاء استخدام اسم المستخدم وكلمة المرور.',
    footer: 'آمن · سريع · موثوق',
    footerAr: 'Secure · Fast · Reliable',
    offlineWarning: 'يبدو أنك غير متصل بالإنترنت. تسجيل الدخول يحتاج اتصالاً بخادم المحل.',
    suspendedTitle: 'هذا الحساب موقوف',
    lockedTitle: 'هذا الحساب مقفل مؤقتاً',
    lockedBody: 'عدد كبير من المحاولات الفاشلة. حاول مرة أخرى بعد {seconds} ثانية.',
    lockedBodyOne: 'عدد كبير من المحاولات الفاشلة. حاول مرة أخرى بعد ثانية واحدة.',
    lockedOver: 'يمكنك المحاولة مرة أخرى الآن.',
  },

  nav: {
    dashboard: 'لوحة التحكم',
    pos: 'نقطة البيع',
    orders: 'الطلبات',
    menuOrders: 'طلبات المنيو',
    products: 'المنتجات',
    inventory: 'المخزون',
    recipes: 'الوصفات',
    production: 'الإنتاج',
    waste: 'الهدر',
    purchases: 'المشتريات',
    suppliers: 'الموردون',
    customers: 'العملاء',
    customerOrders: 'حسابات التوصيل',
    customerCredit: 'ذمم العملاء',
    shifts: 'الورديات',
    myShift: 'ورديتي',
    expenses: 'المصروفات',
    reports: 'التقارير',
    employees: 'الموظفون',
    auditLog: 'سجل التدقيق',
    settings: 'الإعدادات',
    offlineQueue: 'بانتظار الإرسال',
    labels: 'الملصقات',
    importData: 'استيراد البيانات',
    backups: 'النسخ الاحتياطي',
    quickPricing: 'التسعير السريع',
    offers: 'العروض والكومبو',
    groupPricing: 'التسعير والعروض',
    groupMenu: 'المنيو الرقمي',
    menuManagement: 'إدارة المنيو',
    groupSelling: 'المبيعات',
    groupStock: 'المخزون',
    groupBuying: 'المشتريات',
    groupMoney: 'المالية',
    groupAdmin: 'الإدارة',
    collapse: 'طيّ القائمة',
    expand: 'توسيع القائمة',
  },

  pricing: {
    title: 'التسعير السريع',
    subtitle: 'عدّل أسعار البيع مباشرة من القائمة. Enter أو مغادرة الحقل يحفظ.',
    searchPlaceholder: 'ابحث بالاسم أو الرمز أو الباركود',
    allCategories: 'كل الفئات',
    onlyWithOptions: 'المنتجات ذات الخيارات فقط',
    onlyDiscounted: 'المخفّضة فقط',
    product: 'المنتج',
    code: 'الرمز',
    category: 'الفئة',
    unit: 'الوحدة',
    basePrice: 'السعر الأساسي',
    effectivePrice: 'السعر الفعلي',
    optionCount: '{count} خيارات',
    optionCount_one: 'خيار واحد',
    parentNotSellable: 'يُسعَّر من خياراته',
    saving: 'جارٍ الحفظ…',
    saved: 'تم الحفظ',
    failed: 'لم يُحفظ',
    retry: 'إعادة المحاولة',
    invalid: 'ليرة صحيحة أكبر من صفر',
    conflict: 'تغيّر هذا السعر من مكان آخر. حُدِّث الصف - تحقق منه وحاول مجدداً.',
    readOnly: 'يمكنك الاطلاع على الأسعار هنا دون تغييرها.',
    empty: 'لا منتجات مطابقة.',
    fromProduct: 'خصم المنتج',
    fromCategory: 'من الفئة',
    discountBadge: 'خصم {percent}%',
    discountBadgeSource: 'خصم {percent}% · {source}',
  },

  discountRule: {
    title: 'الخصم',
    hint: 'نسبة مئوية من السعر الأساسي. السعر الأساسي نفسه لا يتغيّر.',
    enabled: 'الخصم مفعّل',
    percent: 'نسبة الخصم',
    startsAt: 'يبدأ (اختياري)',
    endsAt: 'ينتهي (اختياري)',
    effective: 'السعر الفعلي',
    stateINACTIVE: 'متوقف',
    stateSCHEDULED: 'مجدول',
    stateACTIVE: 'نشط',
    stateEXPIRED: 'منتهٍ',
    windowError: 'يجب أن يبدأ الخصم قبل انتهائه.',
    percentError: 'أدخل نسبة أكبر من 0 وأقل من 100.',
    categoryHint: 'يُطبَّق على كل منتج في هذه الفئة ليس له خصم خاص. الخصومات لا تتراكم.',
    noPermission: 'ليست لديك صلاحية تغيير الخصومات.',
  },

  media: {
    choose: 'اختيار صورة',
    replace: 'استبدال الصورة',
    remove: 'إزالة الصورة',
    uploading: 'جارٍ الرفع…',
    hint: 'صيغة JPG أو PNG أو WebP، حتى ٥ ميغابايت.',
    typeError: 'صيغة الملف غير مدعومة. استخدم صورة JPG أو PNG أو WebP.',
    sizeError: 'الصورة أكبر من ٥ ميغابايت. صغّرها وحاول مجدداً.',
    uploadFailed: 'تعذّر رفع الصورة.',
  },

  overlay: {
    unsavedTitle: 'تغييرات غير محفوظة',
    unsavedBody: 'لديك تغييرات غير محفوظة. هل تريد الخروج بدون حفظ؟',
    keepEditing: 'متابعة التعديل',
    discard: 'تجاهل التغييرات',
  },

  menu: {
    title: 'إدارة المنيو',
    subtitle: 'ما يعرضه المنيو الرقمي، وما يمكن طلبه، والطلبات الواردة. منتج واحد وسعر واحد؛ المنيو يعرض فقط.',
    boxes: {
      status: 'حالة المنيو',
      products: 'منتجات المنيو',
      categories: 'إدارة محتوى المنيو',
      promotions: 'العروض والواجهة الرئيسية',
      orders: 'طلبات المنيو',
      settings: 'إعدادات المنيو',
    },
    summary: {
      products: '{total} منتج • {visible} ظاهر',
      categories: '{main} رئيسية • {sub} مربوطة',
      promotions: '{active} عروض شغّالة • {live} شرائح ظاهرة',
      offers: '{active} نشطة • {scheduled} مجدولة',
      newOrders: '{count} طلبات جديدة',
      noNewOrders: 'لا طلبات جديدة',
      settings: 'الدوام • الطلبات • واتساب',
    },
    status: {
      menu: 'المنيو',
      store: 'المحل',
      ordering: 'الطلبات',
      whatsapp: 'واتساب',
      enabled: 'مفعّل',
      disabled: 'متوقف',
      open: 'مفتوح',
      closed: 'مغلق',
      until: 'حتى {time}',
      opensAt: 'يفتح {day} {time}',
      configured: 'مضبوط',
      notConfigured: 'غير مضبوط',
      accepting: 'يستقبل الطلبات',
      notAccepting: 'لا يستقبل الطلبات',
      manualOff: 'موقوف يدوياً',
      delivery: 'توصيل',
      pickup: 'استلام',
      timezone: 'توقيت المحل',
      refreshed: 'بتاريخ',
    },
    products: {
      search: 'ابحث بالاسم أو الرمز أو الباركود',
      allShelves: 'كل فئات المنيو',
      filters: { all: 'الكل', visible: 'ظاهر', hidden: 'مخفي' },
      product: 'المنتج',
      code: 'الرمز',
      shelf: 'فئة المنيو',
      price: 'السعر الحالي',
      visibility: 'الظهور على المنيو',
      showOnMenu: 'يظهر على المنيو',
      visible: 'ظاهر',
      hidden: 'مخفي',
      status: 'على المنيو',
      noShelf: 'غير مربوط بفئة في المنيو',
      options: '{count} خيارات',
      optionRetired: 'موقوف',
      saveFailed: 'لم يُحفظ',
      derived: {
        onMenu: 'على المنيو',
        hidden: 'مخفي بقرارك',
        unlinked: 'لا يظهر: فئة نقطة البيع غير مربوطة بالمنيو',
        linkedHidden: 'لا يظهر: فئته في المنيو مخفية',
        inactive: 'لا يظهر: المنتج موقوف في نقطة البيع',
        noOptions: 'يظهر كغير متوفر: كل خياراته موقوفة',
      },
      linkHint: 'اربط فئة نقطة البيع هذه من «فئات المنيو».',
      pageOf: '{from}-{to} من {total}',
      previous: 'السابق',
      next: 'التالي',
      hint: 'الصورة والسعر من المنتج نفسه؛ يُغيَّران من المنتجات أو التسعير السريع. لا شيء هنا يؤثر على نقطة البيع أو المخزون أو الطابعات.',
      empty: 'لا منتجات مطابقة.',
    },
    categories: {
      hint: 'كيف يتصفح الزبون: فئة رئيسية تسمّيها أنت، وتحتها فئات نقطة البيع الحقيقية التي تربطها بها - ومنتجاتها تتبعها تلقائياً. لا شيء هنا يمسّ نقطة البيع أو المخزون أو الطابعات.',
      add: 'إضافة فئة',
      addMain: 'إضافة فئة رئيسية',
      addSub: 'ربط فئات نقطة البيع',
      linkTitle: 'ربط فئات نقطة البيع ضمن «{name}»',
      linkHint: 'علّم فئات نقطة البيع التي تعرضها هذه الفئة الرئيسية. كل فئة نقطة بيع تظهر في مكان واحد في المنيو؛ المربوطة في مكان آخر تقول أين.',
      linkedElsewhere: 'مربوطة ضمن «{name}»',
      linkedHere: 'مربوطة هنا',
      linkSearch: 'ابحث في فئات نقطة البيع',
      posCategory: 'فئة نقطة البيع',
      displayName: 'الاسم للزبون',
      unlink: 'فكّ الربط',
      unlinkHint: 'فكّ الربط يُخفي منتجاتها من المنيو حتى تُربط من جديد؛ لا يتغيّر شيء في نقطة البيع.',
      noLinks: 'لا فئات نقطة بيع مربوطة بعد - اربط بعضها ليظهر المنتجات.',
      edit: 'تعديل الفئة',
      nameAr: 'الاسم بالعربية',
      nameEn: 'الاسم بالإنجليزية',
      visible: 'تظهر على المنيو',
      sortOrder: 'الترتيب',
      moveUp: 'تحريك للأعلى',
      moveDown: 'تحريك للأسفل',
      products: '{count} منتجات',
      subcategories: '{count} فئات فرعية',
      empty: 'لا فئات منيو بعد.',
      saved: 'تم حفظ فئة المنيو.',
      level: 'المستوى',
      mainLevel: 'فئة رئيسية',
      subLevel: 'فئة فرعية',
      parent: 'ضمن الفئة الرئيسية',
      source: 'تعرض فئة نقطة البيع',
      cover: 'صورة الغلاف',
      coverHint: 'تظهر في واجهة المنيو. بدونها يعرض المنيو صورة بديلة أنيقة.',
      hiddenByParent: 'مخفية مع فئتها الرئيسية',
      hidden: 'مخفية',
      retired: 'موقوفة',
      retire: 'إيقاف',
      restore: 'استرجاع',
      catalogueSearch: 'ابحث عن منتج أو رمز أو باركود أو فئة',
      deleteParent: 'حذف الفئة الرئيسية الفارغة',
      deleteBlocked: 'انقل الفئات المرتبطة قبل الحذف',
      deleteExplanation: 'حذف هذه الفئة الرئيسية الفارغة من المنيو؟ لن تتغير فئات أو منتجات نقطة البيع.',
      retireHint: 'الإيقاف يخفي الفئة. يمكن حذف الفئة الرئيسية الفارغة فقط؛ منتجات وفئات نقطة البيع لا تُحذف هنا.',
      linkedCount: '{count} فئات مرتبطة',
      linkedOne: 'فئة واحدة مرتبطة',
      productOne: 'منتج واحد',
      linkPos: 'ربط فئة من نقطة البيع',
      actions: 'إجراءات',
      changeCover: 'تغيير صورة الغلاف',
      hide: 'إخفاء',
      show: 'إظهار',
      editDisplayName: 'تعديل الاسم الظاهر',
      move: 'نقل إلى فئة رئيسية أخرى',
      expand: 'فتح',
      collapse: 'طيّ',
      reorderHandle: 'اسحب لإعادة الترتيب - أو استخدم الأسهم',
      reorderHint: 'اضغط ≡ واسحب لإعادة الترتيب. بلوحة المفاتيح: ركّز على ≡ واضغط الأسهم.',
      reorderFailed: 'لم يُحفظ الترتيب - عاد الترتيب السابق.',
      reordered: 'تم حفظ الترتيب.',
      shown: 'ظاهر',
      hiddenState: 'مخفي',
      posProducts: '{count} منتجات في نقطة البيع',
      hasChildren: 'لها فئات فرعية - انقلها أولاً لتصبح فئة فرعية.',
      uncategorised: 'منتجات بلا فئة: {count}',
    },
    hero: {
      hint: 'الواجهة المتحركة في أعلى المنيو: العلامة، منتج عليه خصم، أو عرض. كل سعر وصورة وخصم يُقرأ من المنتج أو العرض نفسه - لا يُنسخ شيء هنا، فأي تغيير في التسعير السريع يظهر فوراً.',
      add: 'إضافة شريحة',
      addBrand: 'شريحة العلامة',
      addProduct: 'منتج عليه خصم',
      addCombo: 'عرض / كومبو',
      kind: { BRAND: 'العلامة', PRODUCT: 'منتج', COMBO: 'عرض' },
      titleAr: 'العنوان بالعربية',
      titleEn: 'العنوان بالإنجليزية',
      subtitleAr: 'العنوان الفرعي بالعربية',
      subtitleEn: 'العنوان الفرعي بالإنجليزية',
      ctaTextAr: 'نص الزر بالعربية',
      ctaTextEn: 'نص الزر بالإنجليزية',
      ctaTarget: 'الزر يفتح',
      ctaNone: 'لا شيء - الكلمات فقط',
      ctaCategory: 'فئة في المنيو',
      ctaProduct: 'منتجاً',
      ctaCombo: 'عرضاً',
      image: 'الصورة',
      enabled: 'مفعّلة',
      startsAt: 'من',
      endsAt: 'حتى',
      sortOrder: 'الترتيب',
      pickProduct: 'اختر منتجاً عليه خصم',
      pickCombo: 'اختر عرضاً نشطاً',
      noCandidates: 'لا منتجات عليها خصم ومتاحة للطلب حالياً - ضع خصماً من «المنتجات» أولاً.',
      noCombos: 'لا عروض نشطة حالياً.',
      readOnlyProduct: 'يُقرأ من المنتج. غيّر سعره أو خصمه من «المنتجات».',
      readOnlyCombo: 'يُقرأ من العرض. غيّره من «العروض والكومبو».',
      preview: 'معاينة',
      remove: 'إزالة من الواجهة',
      removed: 'تمت إزالة الشريحة.',
      saved: 'تم حفظ الشريحة.',
      empty: 'لا شرائح بعد: يعرض المنيو واجهة العلامة الافتراضية.',
      limits: 'يمكن أن تظهر حتى {brand} شريحة علامة و{promo} عروض ترويجية في الوقت نفسه.',
      lifecycle: { ACTIVE: 'ظاهرة', SCHEDULED: 'مجدولة', EXPIRED: 'انتهت', DISABLED: 'متوقفة' },
      exclusion: {
        PRODUCT_MISSING: 'المنتج لم يعد موجوداً',
        PRODUCT_HIDDEN: 'المنتج مخفي عن المنيو',
        PRODUCT_UNAVAILABLE: 'المنتج غير متوفر',
        NOT_DISCOUNTED: 'لا خصم نشطاً على المنتج',
        COMBO_MISSING: 'العرض لم يعد موجوداً',
        COMBO_NOT_ACTIVE: 'العرض غير نشط',
        COMBO_HIDDEN: 'العرض مخفي عن المنيو',
      },
      notShowing: 'لا تظهر: {reason}',
      confirmRemove: 'إزالة هذه الشريحة من الواجهة؟',
      window: 'الفترة',
      always: 'دائماً',
    },
    offers: {
      hint: 'الكومبو نفسها التي تبيعها نقطة البيع - السعر والتواريخ والصورة والمحتويات تُعدَّل في «العروض والكومبو». هنا تختار فقط أين يعرضها المنيو.',
      showOnMenu: 'يظهر على المنيو',
      showInHero: 'يظهر في الواجهة',
      shown: 'ظاهر',
      inHero: 'في الواجهة',
      notInHero: 'ليس في الواجهة',
      heroOrder: 'ترتيب الواجهة',
      open: 'فتح العروض والكومبو',
      empty: 'لا عروض بعد.',
      sections: { combos: 'العروض والكومبو', products: 'المنتجات المخفضة', brand: 'شريحة العلامة' },
      productsHint: 'المنتجات التي عليها خصم فعلي الآن. ضع واحداً في الواجهة؛ سعره وصورته وخصمه تُقرأ من المنتج نفسه.',
      brandHint: 'شريحة المحل: كلمات وصورة وزر وفترة.',
      noDiscounted: 'لا منتجات ظاهرة عليها خصم الآن - ضع خصماً من «المنتجات» أولاً.',
      state: { working: 'شغّال', paused: 'موقوف', scheduled: 'مجدول', expired: 'منتهي', draft: 'مسودة', componentRetired: 'أحد المكوّنات موقوف' },
      preview: 'معاينة',
      previewMenu: 'بطاقة المنيو',
      previewHero: 'شريحة الواجهة',
      previewState: { onMenu: 'على المنيو الآن', hidden: 'ليس على المنيو: مخفي', paused: 'ليس على المنيو: موقوف', scheduled: 'ليس على المنيو بعد: مجدول', expired: 'ليس على المنيو: منتهي', unavailable: 'يظهر كغير متوفر: أحد المكوّنات موقوف', inactive: 'ليس على المنيو: موقوف', unlinked: 'ليس على المنيو: فئة نقطة البيع غير مربوطة', notDiscounted: 'ليس في الواجهة: لا خصم فعلياً', notShowing: 'ليس في الواجهة: {reason}' },
    },
    orders: {
      tabs: { pending: 'جديدة', confirmed: 'مؤكدة', rejected: 'مرفوضة', all: 'الكل' },
      pageTitle: 'طلبات المنيو',
      pageSubtitle: 'الطلبات الواردة من المنيو الرقمي. تأكيد الطلب يفتحه في نقطة البيع.',
      searchPlaceholder: 'ابحث برقم الطلب أو الاسم أو الهاتف',
      noSearchResults: 'لا يوجد طلب مطابق للبحث.',
      selectPrompt: 'اختر طلباً لعرضه.',
      empty: 'لا طلبات هنا.',
      emptyPending: 'لا طلبات جديدة. طلبات المنيو الرقمي تظهر هنا فور وصولها.',
      delivery: 'توصيل',
      pickup: 'استلام',
      items: '{count} أصناف',
      view: 'عرض الطلب',
      confirm: 'تأكيد الطلب',
      reject: 'رفض الطلب',
      rejectTitle: 'رفض هذا الطلب؟',
      rejectReason: 'السبب (اختياري)',
      reasons: { unavailable: 'الصنف غير متوفر', area: 'خارج منطقة التوصيل', unreachable: 'تعذّر الوصول للزبون', invalid: 'طلب غير صالح', other: 'أخرى' },
      customer: 'الزبون',
      phone: 'الهاتف',
      address: 'العنوان',
      area: 'المنطقة',
      street: 'الشارع',
      building: 'البناية',
      floor: 'الطابق',
      landmark: 'علامة مميزة',
      notes: 'ملاحظات الزبون',
      subtotal: 'المجموع الفرعي',
      deliveryFee: 'رسوم التوصيل',
      total: 'الإجمالي',
      submittedAt: 'وصل في',
      confirmedAt: 'أُكّد في',
      confirmedBy: 'أكّده',
      rejectedBy: 'رفضه',
      status: { PENDING_CONFIRMATION: 'بانتظار التأكيد', CONFIRMED: 'مؤكد', REJECTED: 'مرفوض', CANCELLED: 'ملغى' },
      posOrder: 'طلب نقطة البيع',
      currentSaleOrigin: 'طلب من المنيو',
      frozenSale: 'الأصناف والأسعار المقبولة ثابتة. تابع إلى الدفع عندما تكون جاهزاً.',
      preserveFirst: 'أكمل الطلب الحالي أو علّقه قبل الانتقال. أصنافه وبيانات العميل ما زالت محفوظة هنا.',
      leaveSale: 'ضعه جانباً — يبقى في الطلبات',
      leaveSaleShort: 'ضعه جانباً',
      collect: 'تحصيل الدفع',
      confirmed: 'تم تأكيد الطلب.',
      alreadyConfirmed: 'هذا الطلب مؤكد مسبقاً.',
      newArrival: 'وصل طلب جديد من المنيو',
      feePending: 'رسوم التوصيل تُحدَّد في نقطة البيع وتُضاف إلى الفاتورة النهائية.',
      confirmFee: 'تأكيد الرسوم وإرسال الطلب',
      feeSentToAccounts: 'تم تأكيد رسوم التوصيل. الطلب الآن في حسابات التوصيل، ويُحصَّل عند عودة السائق.',
      usdChange: 'يرجى إحضار فكة لـ {amount}$ (طلب الزبون وليس دفعة).',
      rejected: 'تم رفض الطلب.',
      printing: 'تُطبع الآن فاتورة الزبون وبطاقات التحضير…',
      whatsapp: 'فتح محادثة واتساب',
      discount: 'خصم {percent}%',
      combo: 'كومبو',
      menuNumber: 'طلب المنيو',
      openInPos: 'فتح في نقطة البيع',
      reviewTitle: 'طلب من المنيو',
      reviewHint: 'الأصناف والأسعار مجمّدة كما أرسلها الزبون. راجع ثم أكّد أو ارفض.',
      frozen: 'هذا طلب من المنيو قيد المراجعة: لا يمكن إضافة أصناف أو تعديلها. أكّد الطلب أو ارفضه.',
      exitReview: 'خروج من المراجعة',
      notPending: 'هذا الطلب لم يعد بانتظار التأكيد.',
    },
    settings: {
      business: 'المحل',
      businessNameAr: 'اسم المحل بالعربية',
      businessNameEn: 'اسم المحل بالإنجليزية',
      phone: 'الهاتف',
      address: 'العنوان',
      locationUrl: 'رابط الخريطة (https)',
      whatsapp: 'واتساب',
      whatsappNumber: 'رقم واتساب الطلبات',
      whatsappHint: 'يقرأه المنيو مستقبلاً من قاعدة البيانات - غيّره هنا، لا في الكود.',
      menu: 'المنيو',
      menuEnabled: 'تفعيل تصفح المنيو',
      orderingEnabled: 'تفعيل الطلب عبر الإنترنت',
      orderingHint: 'مفتاح يدوي فوق ساعات الطلب. إيقافه يعني تصفح فقط.',
      defaultLanguage: 'اللغة الافتراضية',
      arabicEnabled: 'العربية',
      englishEnabled: 'الإنجليزية',
      showUsd: 'إظهار الدولار بجانب الليرة',
      orderTypes: 'أنواع الطلبات',
      deliveryEnabled: 'التوصيل',
      pickupEnabled: 'الاستلام',
      deliveryFeeLbp: 'رسوم التوصيل للطلبات عبر الإنترنت (LBP)',
      deliveryFeeHint: 'تُثبَّت على كل طلب توصيل عند إرساله.',
      timezone: 'المنطقة الزمنية للمحل',
      businessHours: 'ساعات العمل',
      orderingHours: 'ساعات استقبال الطلبات',
      hoursHint: 'قد يكون المحل مفتوحاً بينما الطلب مغلق. الأوقات بتوقيت المحل.',
      closed: 'مغلق',
      addInterval: 'إضافة فترة',
      removeInterval: 'إزالة',
      from: 'من',
      to: 'إلى',
      days: { mon: 'الاثنين', tue: 'الثلاثاء', wed: 'الأربعاء', thu: 'الخميس', fri: 'الجمعة', sat: 'السبت', sun: 'الأحد' },
      saved: 'تم حفظ إعدادات المنيو.',
      languagesError: 'يجب أن تبقى لغة واحدة على الأقل مفعّلة.',
    },
  },

  offers: {
    title: 'العروض والكومبو',
    subtitle: 'باقات تُباع بسعر واحد. المخزون والتحضير من المنتجات الحقيقية.',
    create: 'إنشاء عرض / كومبو',
    edit: 'تعديل العرض',
    tabAll: 'الكل',
    tabActive: 'شغّال',
    tabScheduled: 'مجدول',
    tabExpired: 'منتهي',
    tabDraft: 'مسودة',
    tabDisabled: 'موقوف',
    browse: 'تصفّح حسب فئة نقطة البيع',
    browseHint: 'علّم المنتجات التي يضمّها العرض - حسب فئات نقطة البيع نفسها. البحث اختصار إلى القائمة ذاتها.',
    picked: 'ضمن العرض',
    menuLine: 'المنيو',
    menuShown: 'ظاهر',
    menuHidden: 'مخفي',
    quoteLoading: 'يُحسب المجموع العادي…',
    empty: 'لا عروض بعد.',
    emptyHint: 'أنشئ كومبو لبيع باقة بسعر واحد.',
    nameAr: 'الاسم (عربي)',
    nameEn: 'الاسم (إنكليزي)',
    image: 'الصورة',
    offerPrice: 'سعر العرض',
    regularTotal: 'السعر الاعتيادي',
    savings: 'التوفير',
    startsAt: 'يبدأ (اختياري)',
    endsAt: 'ينتهي (اختياري)',
    components: 'المكوّنات',
    addComponent: 'إضافة منتج',
    componentSearch: 'ابحث عن منتج بالاسم أو الرمز أو الباركود',
    quantity: 'الكمية',
    chooseOption: 'اختر الخيار',
    remove: 'إزالة',
    noComponents: 'أضف منتجاً واحداً على الأقل.',
    priceTooHigh: 'يجب أن يكون سعر العرض أقل من السعر الاعتيادي ليُفعَّل.',
    saveDraft: 'حفظ كمسودة',
    publish: 'نشر',
    disable: 'إيقاف',
    enable: 'نشر',
    lifecycleDRAFT: 'مسودة',
    lifecycleSCHEDULED: 'مجدول',
    lifecycleACTIVE: 'شغّال',
    lifecycleEXPIRED: 'منتهي',
    lifecycleDISABLED: 'موقوف',
    unavailable: 'أحد المكوّنات موقوف في نقطة البيع',
    created: 'تم إنشاء العرض.',
    saved: 'تم حفظ العرض.',
    stateChanged: 'تم تحديث العرض.',
    comboBadge: 'كومبو',
    offersTab: 'العروض',
    inCombo: 'ضمن الكومبو',
    comboLine: 'كومبو',
    noPermission: 'يمكنك الاطلاع على العروض هنا دون تغييرها.',
    required: 'مطلوب',
  },

  offerPoster: {
    open: 'صورة إعلان',
    title: 'صورة إعلان للعرض',
    subtitle: 'انسخ الوصف إلى أداة الصور بالذكاء الاصطناعي، ثم ارفع الصورة التي تصنعها - والنظام يكتب عليها الاسم والأسعار بالعربية.',
    step1: '1. وصف الصورة',
    step2: '2. إضافة الكتابة والأسعار',
    style: 'الأسلوب',
    styleslate: 'حجر داكن ورخام',
    stylewood: 'خشب ريفي',
    stylestudio: 'استوديو نظيف',
    stylefestive: 'رمضان والعيد',
    look: 'شكل الطعام',
    lookraw: 'طازج نيء',
    lookserved: 'مطبوخ ومقدَّم',
    target: 'أداة الصور',
    targetchatgpt: 'ChatGPT / Gemini',
    targetmidjourney: 'Midjourney',
    format: 'المقاس',
    formatsquare: 'منشور 1:1',
    formatstory: 'ستوري 9:16',
    prompt: 'الوصف لأداة الصور',
    copy: 'نسخ الوصف',
    copied: 'تم نسخ الوصف.',
    copyFailed: 'تعذّر النسخ - حدّد النص وانسخه يدوياً.',
    references: 'أرفق هذه الصور مع الوصف - الوصف يطلب إبقاء منتجاتك كما هي تماماً وتغيير الخلفية والإضاءة فقط:',
    referenceDownload: 'تنزيل صورة: {name}',
    noText: 'الوصف يطلب صورة بلا أي كتابة - أدوات الصور تخطئ في العربية وفي الأسعار. النظام يضيف الكتابة في الخطوة التالية.',
    upload: 'ارفع صورة الذكاء الاصطناعي',
    replace: 'تغيير الصورة',
    removePicture: 'إزالة الصورة',
    noPicture: 'لا صورة بعد - المعاينة تُظهر التصميم على خلفية داكنة.',
    phone: 'طريقة الطلب (تظهر أسفل الصورة)',
    preview: 'معاينة الصورة',
    printed: 'ما سيُكتب على الصورة',
    download: 'تنزيل الصورة',
    share: 'مشاركة',
    shareFailed: 'تعذّرت المشاركة - نزّل الصورة بدلاً من ذلك.',
    noRate: 'لا يوجد سعر صرف محدد، لذلك تظهر الأسعار بالليرة فقط.',
    productsTitle: 'المنتجات المخفّضة',
    productsHint: 'المنتجات والأنواع التي عليها خصم الآن.',
    productsEmpty: 'لا يوجد منتج عليه خصم الآن.',
    posterSave: 'وفّر {percent}%',
    posterUntil: 'العرض ساري حتى {date}',
    posterPhone: 'للطلب واتساب: {phone}',
  },

  header: {
    currentUser: 'المستخدم الحالي',
    role: 'الصلاحية',
    shiftOpen: 'وردية مفتوحة',
    shiftClosed: 'لا توجد وردية مفتوحة',
    shiftNumber: 'وردية {number}',
    exchangeRate: 'سعر الصرف',
    exchangeRateFull: '١ دولار = {rate} ليرة',
    noRate: 'لم يُحدد سعر الصرف',
    openMenu: 'فتح قائمة المستخدم',
    notifications: 'التنبيهات',
    noNotifications: 'لا يوجد جديد',
  },

  dashboard: {
    title: 'لوحة التحكم',
    greeting: 'أهلاً {name}',
    activeProducts: 'المنتجات الفعّالة',
    activeCategories: 'الفئات',
    activeEmployees: 'الموظفون الفعّالون',
    openShifts: 'الورديات المفتوحة',
    lowStockItems: 'منتجات منخفضة',
    stockValue: 'قيمة المخزون بالتكلفة',
    lowStockTitle: 'منتجات عند الحد الأدنى أو دونه',
    lowStockEmpty: 'كل المنتجات المتتبَّعة فوق حدها الأدنى.',
    recentActivity: 'آخر النشاطات',
    recentActivityEmpty: 'لم يُسجَّل أي نشاط بعد.',
    notBuiltTitle: 'ما لم يُنجز بعد',
    notBuiltIntro: 'هذه اللوحات غائبة عمداً بدل عرض أصفار قد تُفهم على أنها أرقام حقيقية.',
    minimum: 'الحد الأدنى',
    current: 'الحالي',
  },

  employees: {
    title: 'الموظفون',
    subtitle: 'كل من يستطيع تسجيل الدخول إلى النظام.',
    name: 'الاسم',
    username: 'اسم المستخدم',
    role: 'الصلاحية',
    status: 'الحالة',
    pin: 'رمز سريع',
    lastLogin: 'آخر دخول',
    overrides: 'استثناءات الصلاحيات',
    active: 'فعّال',
    suspended: 'موقوف',
    readOnlyNotice:
      'هذه القائمة للعرض فقط في هذه المرحلة. إضافة الموظفين وتعديلهم وإيقافهم وتحديد استثناءات الصلاحيات يأتي في مرحلة لاحقة.',
  },

  permissions: {
    title: 'الأدوار والصلاحيات',
    subtitle: 'ما يُسمح به لكل دور بالتحديد.',
    role: 'الدور',
    people: '{count} موظف',
    sensitive: 'حسّاسة',
    sensitiveHint: 'تُظهر معلومات عن المال أو التكلفة أو الربح.',
    granted: 'ممنوحة',
    notGranted: 'غير ممنوحة',

    effectiveTitle: 'ما يمكن لهذا الشخص فعله',
    effectiveBody:
      'العلامة تعني أنه يستطيع ذلك. الدور يحدّد نقطة البداية، وأي تغيير تجريه يُحفظ لهذا الموظف وحده.',
    roleSummary: 'الدور: {role}',

    ownerAlwaysFull:
      'يمتلك المالك صلاحية كاملة للوصول إلى جميع أقسام النظام.',

    expandAll: 'فتح الكل',
    collapseAll: 'طي الكل',
    selectAll: 'تحديد الكل',
    clearAll: 'إلغاء الكل',
    resetToRole: 'استعادة صلاحيات الدور',

    save: 'حفظ التغييرات',
    saved: 'تم حفظ الصلاحيات.',
    unsaved: 'تغييرات غير محفوظة',

    requiredByPos: 'مطلوبة لنقطة البيع',
    requiredBy: 'مطلوبة لـ {module}',
    dependencyLocked:
      'مطلوبة لعمل القسم أعلاه. أوقف القسم لإزالتها.',
    actionsLabel: 'ما يمكنه فعله داخل القسم',
    groupAll: 'كل {group}',
    removeModuleTitle: 'إيقاف {module}؟',
    removeModuleBody: 'ستُزال هذه معه، وسيلزم إعادتها واحدة واحدة:',
    removeModuleConfirm: 'أوقفه',

    fromRole: 'من الدور',
    customAllowed: 'مخصص',
    customDenied: 'مسحوب',
  },

  audit: {
    title: 'سجل التدقيق',
    subtitle: 'سجل دائم للإجراءات المهمة. لا يمكن تعديل أو حذف أي قيد.',
    when: 'الوقت',
    who: 'المستخدم',
    action: 'الإجراء',
    entity: 'العنصر',
    reason: 'السبب',
    ip: 'العنوان',
    empty: 'لم يُسجَّل أي نشاط بعد.',
    system: 'النظام',
  },

  errors: {
    genericTitle: 'حدث خطأ ما',
    networkTitle: 'تعذّر الوصول إلى الخادم',
    networkBody: 'تأكد من أن خادم المحل يعمل ثم أعد المحاولة. لم يتم حفظ أي شيء.',
    notFoundTitle: 'الصفحة غير موجودة',
    notFoundBody: 'هذه الصفحة غير موجودة. ربما تم نقلها أو حذفها.',
    forbiddenTitle: 'لا تملك صلاحية لعرض هذه الصفحة',
    forbiddenBody: 'حسابك لا يشمل الوصول إلى هذا القسم. إن كنت تحتاجه، راجع المالك أو المدير.',
    goToStart: 'الذهاب إلى صفحتي الرئيسية',
    signInAgain: 'تسجيل الدخول مجدداً',
    sessionExpiredTitle: 'انتهت جلستك',
    sessionExpiredBody: 'لأسباب أمنية تنتهي الجلسة بعد فترة من عدم الاستخدام. الرجاء تسجيل الدخول مجدداً.',
    unexpectedTitle: 'تعذّر عرض الشاشة',
    unexpectedBody: 'حدث أمر غير متوقع. عادةً ما تحل إعادة تحميل الصفحة المشكلة.',
    reload: 'إعادة تحميل الصفحة',
  },

  status: {
    inStock: 'متوفر',
    lowStock: 'منخفض',
    outOfStock: 'غير متوفر',
    notTracked: 'غير متتبَّع',
  },

  phases: {
    phase2: 'المرحلة الثانية',
    phase3: 'المرحلة الثالثة',
    phase4: 'المرحلة الرابعة',
    phase5: 'المرحلة الخامسة',
    phase6: 'المرحلة السادسة',
    phase7: 'المرحلة السابعة',
    phase8: 'المرحلة الثامنة',
    phase9: 'المرحلة التاسعة',
    notBuiltHeading: 'هذه الشاشة غير مُنجزة بعد',
    notBuiltBody: 'مخطط لها في {phase}. تظهر في القائمة لأن حسابك سيملك صلاحية الوصول إليها عند إنجازها.',
    whatWillBeHere: 'ما الذي سيكون هنا',
  },

  products: {
    title: 'المنتجات',
    subtitle: 'كل ما يبيعه المحل.',
    tabProducts: 'المنتجات',
    tabCategories: 'الفئات',
    add: 'إضافة منتج',
    edit: 'تعديل المنتج',
    searchPlaceholder: 'ابحث بالاسم أو الرمز أو الباركود',
    unit: 'الوحدة',
    allCategories: 'كل الفئات',
    showInactive: 'إظهار المخفية',
    noProducts: 'لا توجد منتجات مطابقة لبحثك.',
    noProductsHint: 'جرّب بحثاً آخر، أو أزل تصفية الفئة.',
    empty: 'لا توجد منتجات بعد.',
    emptyHint: 'أضف أول منتج لتبدأ البيع.',

    nameEn: 'الاسم بالإنجليزية',
    nameAr: 'الاسم بالعربية',
    sku: 'رمز المنتج',
    skuHint: 'يُرقَّم تلقائياً حسب الفئة ولا يتغيّر أبداً.',
    numbersOnSave: 'يُعيَّن تلقائياً عند الحفظ.',
    chooseCategoryFirst: 'اختر فئة لرؤية الرقم التالي.',
    barcode: 'الباركود',
    barcodeHint: 'يُرقَّم تلقائياً على مستوى المحل، من ست خانات.',
    category: 'الفئة',
    image: 'الصورة',
    imageHint: 'صيغة JPG أو PNG أو WebP، حتى ٥ ميغابايت.',
    uploadImage: 'اختيار صورة',
    uploading: 'جارٍ الرفع…',
    removeImage: 'إزالة الصورة',
    sellingUnit: 'وحدة البيع',

    displayOrder: 'ترتيب العرض',
    displayOrderHint: 'يحدد ترتيب ظهور المنتج ضمن هذه الفئة.',

    variants: 'الخيارات',
    variantsHint: 'اختياري. استخدمه عندما يجب على الكاشير الاختيار — حجم أو نوع أو نكهة — قبل البيع.',
    hasVariants: 'هذا المنتج يحتوي على خيارات',
    variantAdd: 'إضافة خيار',
    variantRemove: 'حذف الخيار',
    variantNameAr: 'الاسم بالعربية',
    variantNameEn: 'الاسم بالإنكليزية',
    variantActive: 'فعال',
    variantNeedOne: 'أضف خياراً فعالاً واحداً على الأقل، أو أوقف خاصية الخيارات.',
    variantDuplicate: 'هذا الاسم مستخدم في خيار آخر.',
    optionNameTaken: 'يوجد خيار فعال بهذا الاسم لهذا المنتج.',
    variantNameRequired: 'مطلوب.',
    variantPriceNote: 'لكل خيار سعر بيع خاص به. السعر أعلاه لا يُستخدم في البيع.',
    priceFromOptions: 'يتم تحديد سعر البيع من أسعار الخيارات أدناه.',
    variantRequired: 'يرجى اختيار أحد الخيارات.',
    variantUnavailable: 'هذا الخيار لم يعد متاحاً.',
    variantLabel: 'الخيار',
    variantCount_one: 'خيار واحد',
    variantCount_other: '{count} خيارات',

    optionLabelField: 'ما هو الخيار؟',
    optionLabelHint: 'يظهر للكاشير هكذا: «اختر الحجم»، «اختر النوع»، ويُطبع على الفاتورة.',
    optionPresetCustom: 'شيء آخر',
    optionLabelAr: 'الصيغة بالعربية',
    optionLabelEn: 'الصيغة بالإنكليزية',
    optionLabelRequired: 'مطلوب.',
    chooseOption: 'اختر {label}',
    displayOrderInvalid: 'أدخل رقماً صحيحاً لا يقل عن ١.',
    displayOrderDuplicate: 'ترتيب العرض هذا مستخدم أكثر من مرة.',
    reorderProducts: 'ترتيب المنتجات',
    reorderHint: 'رقّم المنتجات بالترتيب الذي تريد ظهورها به على شاشة البيع. اترك فجوات — ١٠، ٢٠، ٣٠ — ليسهل إدراج منتج جديد بينها لاحقاً.',
    reorderSaved: 'تم حفظ الترتيب.',
    reorderEmpty: 'لا توجد منتجات في هذه الفئة لترتيبها بعد.',
    costPrice: 'سعر التكلفة',
    costPriceHint: 'ما يدفعه المحل. لا يظهر للكاشير أبداً.',
    sellingPrice: 'سعر البيع',
    wholesalePrice: 'سعر الجملة',
    minSellingPrice: 'أدنى سعر بيع',
    minSellingPriceHint: 'اختياري. البيع دونه سيحتاج موافقة.',
    trackStock: 'تتبّع مخزون هذا المنتج',
    currentStock: 'المخزون الافتتاحي',
    currentStockHint: 'يُحدد مرة واحدة عند إنشاء المنتج.',
    stockLockedHint: 'يتغيّر المخزون بتسجيل حركة مخزون، لا بتعديل هذا النموذج. يأتي ذلك في المرحلة الرابعة.',
    minStock: 'حد التنبيه للمخزون المنخفض',
    preparationNotes: 'ملاحظات التحضير',
    preparationNotesHint: 'تُطبع على بطاقة التحضير، مثل: إزالة طبقة الدهن.',
    active: 'متاح للبيع',
    priceChangeReason: 'سبب تغيير السعر',
    priceChangeReasonHint: 'يُسجَّل في تاريخ الأسعار وفي سجل التدقيق.',
    priceHistory: 'تاريخ الأسعار',
    noPriceHistory: 'لم يُسجَّل أي تغيير في السعر بعد.',
    from: 'من',
    until: 'حتى',
    current: 'الحالي',
    changedBy: 'غيّره',

    perUnit: 'لكل {unit}',
    status: 'الحالة',
    hidden: 'مخفي',
    onSale: 'معروض',
    price: 'السعر',
    cost: 'التكلفة',
    margin: 'هامش الربح',
    stock: 'المخزون',
    costHidden: 'لا تملك صلاحية عرض أسعار التكلفة.',

    deleteTitle: 'إزالة هذا المنتج؟',
    deleteMessage:
      'سيختفي من المحل ومن نقطة البيع. تبقى السجلات السابقة التي تذكره قابلة للقراءة. لا يمكن التراجع إلا عبر المطوّر.',
    deleteConfirm: 'إزالة المنتج',
    created: 'تم إنشاء المنتج.',
    saved: 'تم حفظ المنتج.',
    deleted: 'تمت إزالة المنتج.',
  },

  categories: {
    title: 'الفئات',
    add: 'إضافة فئة',
    edit: 'تعديل الفئة',
    nameEn: 'الاسم بالإنجليزية',
    nameAr: 'الاسم بالعربية',
    slug: 'الرمز',
    slugHint: 'يُرقَّم تلقائياً: cat-001، cat-002... ولا يتغيّر أبداً.',
    codePrefix: 'بادئة رمز المنتج',
    codePrefixHint: 'من 2 إلى 5 أحرف إنجليزية يبدأ بها رمز كل منتج هنا: BEF تعطي BEF-001. اتركها فارغة لاختيارها من الاسم. التغيير يطبَّق على المنتجات المضافة بعده.',
    color: 'اللون',
    sortOrder: 'الترتيب',
    productCount: 'المنتجات',
    empty: 'لا توجد فئات بعد.',
    deleteTitle: 'إزالة هذه الفئة؟',
    deleteMessage: 'لا يمكن إزالة فئة إلا إذا كانت خالية من المنتجات.',
    deleteConfirm: 'إزالة الفئة',
    created: 'تم إنشاء الفئة.',
    saved: 'تم حفظ الفئة.',
    deleted: 'تمت إزالة الفئة.',
    stillHasProducts: 'انقل منتجاتها إلى فئة أخرى أولاً.',
  },

  pos: {
    title: 'نقطة البيع',
    chooseCustomer: 'اختر عميلاً',
    changeCustomer: 'تغيير',
    customerSearch: 'ابحث بالاسم أو الهاتف أو الرمز',
    noCustomerChosen: 'زبون عابر',
    creditNeedsCustomer: 'اختر العميل الذي سيُسجَّل على حسابه.',
    creditNotEnough: 'لم يتبقَّ له سوى {available} من الذمة.',
    creditNoChange: 'البيع على الحساب لا يعيد فراطة. سجّل عليه ما تبقّى فقط.',
    goingOnAccount: 'سيُسجَّل {amount} على حساب {name}.',
    customerBalance: 'على الحساب',
    searchPlaceholder: 'ابحث أو امسح الباركود',
    feeOptional: '(اختياري)',
    feeTooLarge: 'الرسوم تبدو غير صحيحة. تحقق من المبلغ.',
    feeNegative: 'لا يمكن أن تكون الرسوم سالبة.',
    allProducts: 'الكل',
    noResults: 'لا نتائج مطابقة.',
    noResultsHint: 'تأكد من الكتابة، أو اختر فئة أخرى.',
    outOfStock: 'غير متوفر',
    lowStock: 'منخفض',

    cart: 'الطلب الحالي',
    cartEmpty: 'الطلب فارغ.',
    cartEmptyHint: 'اختر منتجاً للبدء.',
    items: '{count} أصناف',
    oneItem: 'صنف واحد',
    subtotal: 'المجموع الفرعي',
    totalWeight: 'الوزن الإجمالي',
    clearCart: 'إفراغ الطلب',
    clearCartTitle: 'إفراغ الطلب بالكامل؟',
    clearCartMessage: 'ستُحذف كل الأصناف. لا يمكن التراجع.',
    clearCartConfirm: 'إفراغ الطلب',
    removeLine: 'حذف هذا الصنف',
    editLine: 'تعديل',
    addNote: 'إضافة ملاحظة',
    lineNote: 'ملاحظة لهذا الصنف',
    lineNotePlaceholder: 'مثال: شرائح رفيعة، بدون دهن',
    orderNote: 'ملاحظة على الطلب كله',
    orderNotePlaceholder: 'مثال: التوصيل بعد الساعة السادسة',

    enterWeight: 'أدخل الوزن',
    enterQuantity: 'أدخل الكمية',
    enterAmount: 'أدخل المبلغ',
    weightKg: 'كيلوغرام',
    weightG: 'غرام',
    quantity: 'الكمية',
    amountUsd: 'المبلغ بالدولار',
    addToOrder: 'إضافة إلى الطلب',
    saveChanges: 'حفظ التعديلات',
    keypadClear: 'مسح',
    convertedTo: 'أي {kg} كغ',
    lineTotalPreview: 'إجمالي الصنف',
    weightBasePrice: 'السعر الأساسي',
    weightEffectivePrice: 'السعر بعد الخصم',

    errorEmpty: 'الرجاء إدخال رقم.',
    errorNotANumber: 'هذا ليس رقماً صحيحاً.',
    errorZero: 'يجب أن تكون الكمية أكبر من صفر.',
    errorWholeNumber: 'يُباع هذا المنتج بالقطعة، فأدخل رقماً صحيحاً.',
    errorTooLarge: 'الرقم يبدو كبيراً جداً. الرجاء المراجعة.',

    printPreview: 'طباعة',

    // --- تخطيط متجاوب ---
    scrollCategoriesBack: 'عرض الفئات السابقة',
    scrollCategoriesForward: 'عرض الفئات التالية',
    openCart: 'فتح الطلب الحالي',
    closeCart: 'إغلاق الطلب الحالي',
    cartSummary: '{count} أصناف · {total}',
    cartSummaryOne: 'صنف واحد · {total}',
    cartEmptySummary: 'الطلب فارغ',
  },

  receipt: {
    onlineSource: 'طلب من المنيو',
    onlineCustomer: 'الاسم',
    onlinePhone: 'الهاتف',
    onlineAddress: 'العنوان',
    onlineBuilding: 'البناية / الطابق',
    cashier: 'الكاشير',
    date: 'التاريخ',
    item: 'الصنف',
    total: 'الإجمالي',
    grandTotal: 'المجموع',
    inLbp: 'الإجمالي بالليرة',
    rate: 'سعر الصرف',
    discount: 'الحسم',
    noRate: 'لم يُحدد سعر صرف، لذلك لا يمكن عرض الإجمالي بالليرة.',
  },

  units: {
    KG: 'كيلوغرام',
    GRAM: 'غرام',
    PIECE: 'قطعة',
    TRAY: 'صينية',
    PACKAGE: 'عبوة',
    BOX: 'صندوق',
    MEAL: 'صنف بسعر ثابت',
    CUSTOM_AMOUNT: 'مبلغ مخصص',
  },

  shift: {
    myShiftTab: 'ورديتي',
    allUsersTab: 'كل المستخدمين',
    allUsersTitle: 'الورديات',
    allUsersSubtitle: 'كل أدراج المحل، مباشرة. يرى هذا العرض المالك والمدير فقط.',
    overviewOpen: 'أدراج مفتوحة',
    overviewOpenHint: '{today} تعمل اليوم · {old} متروكة مفتوحة من أيام سابقة',
    overviewDeliveriesOut: 'توصيلات لم تُستلم',
    overviewSales: 'المبيعات في الأدراج المفتوحة',
    overviewAttention: 'تحتاج متابعة',
    overviewAttentionHint: 'انتهى يوم عملها — يجب إغلاقها',
    noOpenShifts: 'لا يوجد درج مفتوح الآن.',
    you: '(أنت)',
    openedOn: 'فُتحت {date}',
    openToday: 'مفتوحة',
    dayEndedBadge: 'انتهى يوم العمل',
    expectedUsd: 'النقد المتوقع (دولار)',
    expectedLbp: 'النقد المتوقع (ليرة)',
    deliveriesOut: 'توصيلات بالخارج',
    deliveriesOutTitle: 'أرسلها {name} ولم تُدفع بعد — لا يُغلق الدرج قبل استلامها',
    leftAt: 'خرج {time}',
    receiveFor: 'استلام عن {name}',
    feePending: 'رسم التوصيل بانتظار التأكيد في نقطة البيع',
    closeOnBehalf: 'إغلاق نيابةً عن {name}',
    closeOnBehalfBlocked: 'الإغلاق نيابةً — بانتظار {count} توصيل',
    closeMine: 'إغلاق ورديتي',
    closedShifts: 'الورديات المغلقة',
    allCashiers: 'كل المستخدمين',
    closedBy: 'أغلقها',
    onBehalf: 'نيابةً',
    unassignedTitle: 'حسابات توصيل قديمة لا يملكها درج مفتوح',
    unassignedHint: 'أُرسلت قبل ربط التوصيلات بالوردية. استلمها في درجك.',
    onBehalfDialogTitle: 'إغلاق وردية {name}',
    onBehalfWarningTitle: 'أنت تغلق درج شخص آخر',
    onBehalfWarning: 'الوردية {shift}، فتحها {name} في {date}. عُدّ النقد الموجود فعلاً في درجه. سيذكر التقرير أنك أغلقتها نيابةً عنه.',
    onBehalfNote: 'ملاحظة (مطلوبة عند الإغلاق نيابةً عن غيرك)',
    onBehalfConfirm: 'إغلاق وردية {name}',
    onBehalfClosed: 'أُغلقت {shift} نيابةً عن {name}.',
    onBehalfDeliveriesOut: 'لهذه الوردية توصيلات بالخارج. استلمها عن {name} أولاً.',
    expectedInDrawer: 'المتوقع في الدرج',
    drawerMatches: 'الدرج مطابق',
    syncBeforeClose: 'أعد الاتصال وزامن جميع العمليات المعلّقة قبل إغلاق الوردية.',
    title: 'ورديتي',
    subtitle: 'الدرج الذي أنت مسؤول عنه.',
    noShift: 'لا توجد لديك وردية مفتوحة.',
    noShiftHint: 'افتح وردية قبل البدء بالبيع، ليُحتسب النقد عليها.',
    open: 'فتح وردية',
    opening: 'جارٍ الفتح…',
    openTitle: 'فتح وردية',
    openingCashUsd: 'النقد في الدرج (دولار)',
    openingCashLbp: 'النقد في الدرج (ليرة)',
    openingCashHint: 'عُدّ ما هو موجود فعلياً قبل أن تبدأ.',
    openNote: 'ملاحظة',
    close: 'إغلاق الوردية',
    closing: 'جارٍ الإغلاق…',
    closeTitle: 'إغلاق الوردية',
    closeIntro: 'عُدّ الدرج الآن وأدخل ما لديك فعلياً.',
    countedUsd: 'النقد المعدود (دولار)',
    countedLbp: 'النقد المعدود (ليرة)',
    closeNote: 'ملاحظة حول أي فرق',
    expected: 'المتوقع',
    counted: 'المعدود',
    difference: 'الفرق',
    over: 'زيادة',
    short: 'نقص',
    exact: 'مطابق',
    shiftNumber: 'وردية',
    openedAt: 'فُتحت',
    closedAt: 'أُغلقت',
    status: 'الحالة',
    statusOpen: 'مفتوحة',
    closeBlockedDeliveries: 'لا يمكن إغلاق الوردية',
    closeBlockedDeliveriesCount: 'يوجد {count} حساب توصيل بانتظار الاستلام.',
    closeBlockedDeliveriesBody: 'استلم كل حساب من هنا — نفس نافذة الدفع في نقطة البيع. لا يُسجَّل أي دفع تلقائياً، ويُفتح زر إغلاق الوردية بعد استلام آخر حساب.',
    moreDeliveryAccounts: '+{count} أخرى — تظهر هنا بعد استلام هذه.',
    statusClosed: 'مغلقة',
    salesCount: 'المبيعات',
    salesTotal: 'إجمالي المبيعات',
    cashTaken: 'النقد المقبوض',
    changeGiven: 'الباقي المُعاد',
    byMethod: 'المقبوض حسب طريقة الدفع',
    movements: 'حركات النقد',
    noMovements: 'لم تحدث أي حركة نقدية بعد.',
    history: 'الورديات السابقة',
    noHistory: 'لا توجد ورديات بعد.',
    opened: 'ورديتك مفتوحة. يمكنك البدء بالبيع.',
    closed: 'تم إغلاق ورديتك.',
    cashier: 'الكاشير',
    confirmCloseTitle: 'إغلاق هذه الوردية؟',
    confirmCloseMessage: 'بعد الإغلاق لا يمكنك البيع عليها مجدداً. تأكد من عدّ الدرج.',
  },

  payment: {
    title: 'استلام الدفع',
    dueNow: 'المطلوب',
    paidSoFar: 'المدفوع',
    remaining: 'المتبقي',
    change: 'الباقي',
    addPayment: 'إضافة دفعة',
    method: 'الطريقة',
    cash: 'نقداً',
    visa: 'فيزا',
    whish: 'ويش',
    credit: 'على الحساب',
    currency: 'العملة',
    amount: 'المبلغ',
    reference: 'رقم المرجع',
    referenceHint: 'اختياري. رمز الموافقة من جهاز البطاقة أو التطبيق.',
    quickExact: 'المبلغ بالضبط',
    remove: 'حذف',
    changeCurrency: 'إعطاء الباقي بـ',
    complete: 'إتمام البيع',
    completing: 'جارٍ الإتمام…',
    fulfilmentLabel: 'نوع الطلب',
    fulfilmentPickup: 'استلام من المحل',
    fulfilmentOnTable: 'على الطاولة',
    fulfilmentDelivery: 'توصيل',
    tableLabel: 'رقم الطاولة',
    tablePlaceholder: 'مثال: 4',
    tableRequired: 'حدد الطاولة التي يخصها هذا الطلب.',
    deliveryLocked: 'هذا البيع عليه رسوم توصيل، لذا يخرج كطلب توصيل.',
    paidInFull: 'تم استلام المبلغ بالكامل',
    skipPreparation: 'بدون تحضير',
    skipPreparationHint: 'لن يتم إرسال هذا الطلب إلى طابعات التحضير.',
    skipPreparationWarning: 'لن يتم إرسال ورقة تحضير لهذا الطلب.',
    skipReceipt: 'بدون إيصال',
    skipReceiptHint: 'لن يُطبع إيصال الزبون على طابعة الكاشير. يمكنك إعادة طباعته لاحقاً من الطلبات.',
    skipReceiptWarning: 'لن يُطبع إيصال لهذا البيع. يُسجَّل البيع كالمعتاد، وتُطبع أوراق التحضير إن وُجدت.',
    preparationAlreadySent: 'تم إرسال التحضير بالفعل لهذا الطلب.',
    completed: 'تمت عملية البيع.',
    noPayments: 'أضف دفعة واحدة على الأقل.',
    underpaid: 'الدفعات لا تغطي المبلغ المطلوب بعد.',
    overpaidNote: 'للزبون باقٍ.',
    noRate: 'لم يُحدد سعر صرف. على المالك أو المدير تحديده قبل البيع.',
    shiftRequired: 'ورديتك غير مفتوحة',
    shiftRequiredHint: 'افتح وردية قبل البيع، ليُحتسب النقد عليها.',
    goToShift: 'الذهاب إلى ورديتي',

    dayEndedTitle: 'انتهى يوم العمل',
    dayEndedBody: 'يجب إغلاق الوردية الحالية قبل متابعة عمليات البيع في اليوم الجديد.',
    dayEndedShift: 'الوردية {number}، فُتحت {day}',
    dayEndedWhy: 'الوردية تحتوي مبيعات يوم واحد. انتهى اليوم عند 12:00 صباحًا.',
    dayEndedCart: 'تم الاحتفاظ بطلبك الحالي. لم يُفقد شيء.',
    dayEndedAction: 'إغلاق الوردية',
    dayEndedShort: 'انتهى يوم العمل — أغلق الوردية للمتابعة.',
    failedTitle: 'لم تكتمل عملية البيع',
    failedRetry:
      'لم يُحفظ شيء ولم يُخصم أي مخزون. يمكنك المحاولة مجدداً بأمان — لن يُحتسب الطلب نفسه مرتين.',
  },

  orders: {
    title: 'الطلبات',
    subtitle: 'المبيعات المكتملة.',
    subtitleOwn: 'مبيعاتك المكتملة.',
    searchPlaceholder: 'ابحث برقم الإيصال أو طلب المنيو أو رقم الطلب',
    dateToday: 'اليوم',
    dateYesterday: 'أمس',
    dateLast7: 'آخر ٧ أيام',
    dateCustom: 'فترة مخصصة',
    dateFilterLabel: 'أي الأيام تُعرض',
    dateLockedHint: 'اليوم فقط. اطلب من المالك صلاحية عرض الطلبات السابقة.',
    customFrom: 'من',
    customTo: 'إلى',
    customApply: 'تطبيق',
    customClear: 'مسح',
    customReversed: 'تاريخ البداية يجب ألا يكون بعد تاريخ النهاية.',
    emptyForRange: 'لا توجد مبيعات في الأيام المختارة.',
    empty: 'لا توجد مبيعات بعد.',
    emptyHint: 'تظهر المبيعات المكتملة هنا.',
    noResults: 'لا توجد عملية بيع بهذا الرقم.',
    receipt: 'الإيصال',
    when: 'الوقت',
    cashier: 'الكاشير',
    items: 'الأصناف',
    total: 'الإجمالي',
    paidWith: 'طريقة الدفع',
    viewReceipt: 'عرض الإيصال',
    scopeOwn: 'أنت ترى مبيعاتك فقط.',
  },

  reports: {
    title: 'التقارير',
    dailyTitle: 'مبيعات اليوم',
    day: 'اليوم',
    today: 'اليوم',
    salesCount: 'عدد المبيعات',
    itemsSold: 'الأصناف المباعة',
    revenue: 'الإيرادات',
    averageSale: 'متوسط الفاتورة',
    shiftsOpened: 'الورديات المفتوحة',
    costOfGoods: 'تكلفة البضاعة',
    grossProfit: 'الربح الإجمالي',
    grossMargin: 'هامش الربح',
    byMethod: 'حسب طريقة الدفع',
    byCashier: 'حسب الكاشير',
    topProducts: 'الأكثر مبيعاً',
    method: 'الطريقة',
    count: 'العدد',
    amount: 'المبلغ',
    inUsd: 'بالدولار',
    product: 'المنتج',
    quantity: 'الكمية',
    noSales: 'لم تُسجَّل أي مبيعات في هذا اليوم.',
    notIncludedTitle: 'غير مشمول في هذا التقرير بعد',
    notIncludedBody: 'هذه غائبة عمداً بدل عرضها كصفر. مجموعة التقارير الكاملة تأتي في المرحلة الثامنة.',
    profitHidden: 'أرقام الأرباح تحتاج صلاحية تقارير الأرباح.',
  },

  approval: {
    title: 'مطلوب موافقة مدير',
    whoApproves: 'أي مدير سيوافق؟',
    noApprovers: 'لا يوجد مدير لديه رمز سري يمكنه الموافقة. اطلب من المالك إعداد رمز.',
    reason: 'السبب',
    reasonPlaceholder: 'مثال: أعاد الزبون اللحمة دون فتحها',
    reasonHint: 'يُحفظ هذا بشكل دائم ويظهر في سجل التدقيق.',
    pin: 'الرمز السري للمدير',
    approve: 'موافقة',
    checking: 'جارٍ التحقق…',
    footnote:
      'يجب أن يُدخل المدير الرمز بنفسه. الموافقة تغطي إجراءً واحداً، وتنتهي بعد عشر دقائق، وتُسجَّل باسمه.',
    granted: 'وافق عليها {name}.',
    required: 'يجب أن يوافق مدير على هذا.',
  },

  inventory: {
    title: 'المخزون والجرد',
    subtitle: 'كل الأصناف، الكمية الحالية، وتصحيحها مباشرة.',
    trackedOf: '{count} من {total} صنف متابَع',
    all: 'الكل',
    low: 'منخفض',
    out: 'نفد',
    notTracked: 'غير متابَع',
    inStock: 'متوفر',
    allCategories: 'كل الفئات',
    category: 'الفئة',
    search: 'ابحث عن صنف بالاسم أو الباركود',
    popular: 'الأكثر مبيعاً',
    popularWindow: 'آخر 30 يوماً',
    popularEmpty: 'لا مبيعات في آخر 30 يوماً.',
    timesOrdered: 'طُلب {count} مرة',
    sold: 'المباع',
    items: '{count} صنف',
    trackedCount: '{count} متابَع',
    actual: 'الكمية الفعلية',
    decrease: 'إنقاص',
    increase: 'زيادة',
    save: 'حفظ',
    saved: '{name}: أصبح المخزون {quantity}',
    reasonLabel: 'السبب (اختياري)',
    reasonCount: 'جرد',
    reasonSpoiled: 'تلف',
    reasonDelivery: 'استلام بضاعة',
    reasonMistake: 'خطأ سابق',
    notTrackedHint: 'فعّل «تتبع المخزون» في المنتج لمتابعته هنا.',
    readOnly: 'يمكنك رؤية المخزون هنا دون تصحيحه.',
    empty: 'لا أصناف مطابقة.',
    unchanged: 'هذه هي الكمية المسجلة أصلاً.',
  },


  refund: {
    title: 'إرجاع',
    void: 'إبطال البيع',
    voidTitle: 'إبطال هذه العملية',
    voidIntro:
      'استخدم هذا عندما لا يكون البيع صحيحاً أصلاً. يعود كل شيء: تعود البضاعة إلى المخزون ويخرج النقد من الدرج. تبقى عملية البيع كما هي وتُوسم بأنها مُبطلة.',
    voidReason: 'ما سبب الإبطال؟',
    voidConfirm: 'إبطال البيع',
    voided: 'تم إبطال عملية البيع.',
    voidedLabel: 'مُبطلة',

    refundTitle: 'إرجاع أصناف',
    refundIntro: 'اختر ما يعيده الزبون. يمكنك إرجاع جزء من الفاتورة.',
    chooseItems: 'ما الذي يعود؟',
    soldQuantity: 'المباع',
    alreadyRefunded: 'المُعاد سابقاً',
    availableToRefund: 'المتبقي للإرجاع',
    refundQuantity: 'المُعاد الآن',
    refundReason: 'ما سبب إعادة المبلغ؟',
    refundTotal: 'إجمالي الإرجاع',
    howRefunded: 'كيف يُعاد المبلغ؟',
    restock: 'إعادة البضاعة إلى الرف',
    restockHint: 'اترك هذا مغلقاً إن لم تعد اللحمة صالحة للبيع.',
    refundConfirm: 'تنفيذ الإرجاع',
    refunding: 'جارٍ الإرجاع…',
    refunded: 'تم تسجيل الإرجاع {number}.',
    nothingSelected: 'اختر صنفاً واحداً على الأقل.',
    fullyRefunded: 'مُعاد بالكامل',
    partiallyRefunded: 'مُعاد جزئياً',
    cannotRefund: 'لم يبق شيء للإرجاع في هذه الفاتورة.',
    history: 'المرتجعات',
    refundNumber: 'رقم الإرجاع',
    approvedBy: 'وافق عليه',
    restocked: 'أُعيد للمخزون',
    notRestocked: 'لم يُعد للمخزون',
  },

  expenses: {
    title: 'المصروفات',
    subtitle: 'الأموال التي أنفقها المحل.',
    add: 'تسجيل مصروف',
    addTitle: 'تسجيل مصروف',
    category: 'الفئة',
    description: 'ما الغرض منه؟',
    amount: 'المبلغ',
    currency: 'العملة',
    method: 'طريقة الدفع',
    cashWarning:
      'المصروف النقدي يخرج مباشرة من الدرج، لذلك يجب أن تكون ورديتك مفتوحة وسينقص النقد المتوقع بهذا المبلغ.',
    submit: 'تسجيل',
    offlineBlocked:
      'تسجيل المصروفات يحتاج إلى اتصال بخادم نظام نقاط البيع.',
    submitting: 'جارٍ التسجيل…',
    created: 'تم تسجيل المصروف.',
    status: 'الحالة',
    statusPENDING: 'بانتظار الاعتماد',
    statusAPPROVED: 'معتمد',
    statusREJECTED: 'مرفوض',
    approve: 'اعتماد',
    reject: 'رفض',
    rejectTitle: 'رفض هذا المصروف؟',
    rejectReason: 'ما سبب الرفض؟',
    rejectNote: 'الرفض لا يعيد المال إلى الدرج. المال أُنفق فعلاً؛ هذا تسجيل بأنه ما كان يجب إنفاقه.',
    approved: 'تم اعتماد المصروف.',
    rejected: 'تم رفض المصروف.',
    recordedBy: 'سجّله',
    spentAt: 'التاريخ',
    total: 'الإجمالي',
    empty: 'لا توجد مصروفات بعد.',
    emptyHint: 'يُسجَّل هنا كل ما ينفقه المحل.',
    allStatuses: 'الكل',
    needsApproval: 'هذا المصروف فوق الحد، لذلك يجب أن يوافق مدير.',
    approvalTitle: 'اعتماد هذا المصروف',

    categoryDELIVERY: 'توصيل',
    categoryFUEL: 'محروقات',
    categoryPACKAGING: 'تغليف',
    categoryCLEANING: 'تنظيف',
    categoryMAINTENANCE: 'صيانة',
    categoryELECTRICITY: 'كهرباء',
    categoryEMPLOYEE_MEALS: 'وجبات الموظفين',
    categorySUPPLIER_RELATED: 'متعلق بالموردين',
    categoryMISCELLANEOUS: 'متفرقات',

    allCategories: 'كل الفئات',
    manageCategories: 'إدارة الفئات',
    categoriesTitle: 'إدارة فئات المصروفات',
    activeCategories: 'الفئات النشطة',
    archivedCategories: 'الفئات المؤرشفة',
    addCategory: 'إضافة فئة',
    editCategory: 'تعديل الفئة',
    categoryName: 'اسم الفئة',
    archive: 'أرشفة',
    restore: 'استعادة',
    categoryInUse: 'هذه الفئة مستخدمة بالفعل.',
    categoryAdded: 'تمت إضافة الفئة.',
    categoryRenamed: 'تم تغيير اسم الفئة.',
    categoryArchived: 'تمت أرشفة الفئة. تبقى على المصروفات السابقة.',
    categoryRestored: 'تمت استعادة الفئة.',
    categoryRemoved: 'تم حذف الفئة.',
    categoryUsedCount: 'مستخدمة في {count}',
    categoryNeverUsed: 'غير مستخدمة',
    categoryArchiveNote: 'الأرشفة تُبقي الفئة على كل مصروف يستخدمها بالفعل.',
    categoryDeleteUnused: 'حذف',
    categoryDeleteConfirm: 'حذف هذه الفئة؟ لم تُستخدم قط، فلن يضيع شيء.',
    noCategories: 'لا توجد فئات مصروفات متاحة.',
    noCategoriesHint: 'أضف واحدة لبدء تسجيل المصروفات.',
    categoryArchivedMark: 'مؤرشفة',
  },

  discount: {
    title: 'خصم',
    add: 'إضافة خصم',
    amount: 'قيمة الخصم',
    reason: 'السبب؟',
    reasonPlaceholder: 'مثال: تغليف تالف، زبون دائم',
    remove: 'إزالة الخصم',
    needsApproval: 'هذا الخصم فوق الحد، لذلك يجب أن يوافق مدير.',
    approvalTitle: 'اعتماد هذا الخصم',
    applied: 'تم تطبيق الخصم.',
    subtotal: 'قبل الخصم',
  },

  suppliers: {
    title: 'الموردون',
    subtitle: 'الجهات التي يشتري منها المحل.',
    add: 'إضافة مورد',
    edit: 'تعديل المورد',
    name: 'الاسم',
    contactPerson: 'الشخص المسؤول',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    notes: 'ملاحظات',
    paymentTerms: 'شروط الدفع',
    paymentTermsDays: 'مهلة السداد بالأيام',
    paymentTermsHint: 'تُستخدم لحساب تاريخ استحقاق الفاتورة عند عدم تحديده. صفر يعني الاستحقاق فوراً.',
    active: 'مُتعامل معه حالياً',
    searchPlaceholder: 'ابحث بالاسم أو الشخص المسؤول أو الهاتف',
    showInactive: 'إظهار غير النشطين',
    orders: 'الطلبات',
    invoices: 'الفواتير',
    balance: 'الرصيد',
    owed: 'المستحق لهم',
    overdue: 'متأخر',
    invoiced: 'إجمالي الفواتير',
    paid: 'المدفوع',
    settled: 'لا يوجد مستحق',
    balanceHidden: 'لا تملك صلاحية عرض أرصدة الموردين.',
    empty: 'لا يوجد موردون بعد.',
    emptyHint: 'أضف الجهات التي تشتري منها اللحوم.',
    created: 'تمت إضافة المورد.',
    saved: 'تم حفظ المورد.',
    deleted: 'تمت إزالة المورد.',
    deleteTitle: 'إزالة هذا المورد؟',
    deleteMessage: 'لا يمكن إزالة مورد إلا إذا لم تكن لديه طلبات مفتوحة ولا مبالغ مستحقة.',
    history: 'سجل المشتريات',
    noHistory: 'لم يُشترَ منه شيء بعد.',
    tabSuppliers: 'الموردون',
    tabInvoices: 'الفواتير',
    tabPayments: 'الدفعات',
    moneyHidden: 'لا تملك صلاحية الاطّلاع على فواتير الموردين أو دفعاتهم.',
  },

  purchases: {
    title: 'المشتريات',
    subtitle: 'ما تم طلبه، وما وصل فعلياً.',
    add: 'طلب جديد',
    addTitle: 'أمر شراء جديد',
    orderNumber: 'الطلب',
    supplier: 'المورد',
    status: 'الحالة',
    expectedDate: 'التاريخ المتوقع',
    expectedTotal: 'الإجمالي المتوقع',
    items: 'المنتجات',
    deliveries: 'التسليمات',
    createdBy: 'أنشأه',
    notes: 'ملاحظات',

    statusDRAFT: 'مسودة',
    statusORDERED: 'مُرسل',
    statusPARTIALLY_RECEIVED: 'مستلم جزئياً',
    statusRECEIVED: 'مستلم',
    statusCANCELLED: 'ملغى',

    noStockYet:
      'لم يُضف شيء إلى المخزون. لا يتغيّر المخزون إلا عند وصول البضاعة فعلياً وتسجيل التسليم.',

    addProduct: 'إضافة منتج',
    product: 'المنتج',
    quantity: 'الكمية',
    expectedCost: 'التكلفة المتوقعة',
    lineTotal: 'إجمالي السطر',
    removeLine: 'حذف',
    createOrder: 'حفظ كمسودة',
    creating: 'جارٍ الحفظ…',
    created: 'تم حفظ الطلب {number} كمسودة.',
    place: 'إرسال إلى المورد',
    placing: 'جارٍ الإرسال…',
    placed: 'تم إرسال الطلب.',
    cancel: 'إلغاء الطلب',
    cancelTitle: 'إلغاء هذا الطلب؟',
    cancelMessage: 'يبقى ما استُلم فعلاً في المخزون. يُلغى الجزء غير المستلم فقط.',
    cancelReason: 'ما سبب الإلغاء؟',
    cancelled: 'تم إلغاء الطلب.',

    receive: 'تسجيل تسليم',
    receiveTitle: 'تسجيل ما وصل',
    receiveIntro:
      'أدخل ما وصل فعلياً. تُضاف الكمية السليمة فقط إلى المخزون؛ أما التالفة فتُسجَّل ولا توضع على الرف أبداً.',
    ordered: 'المطلوب',
    received: 'المستلم حتى الآن',
    outstanding: 'المتبقي',
    receivingNow: 'الواصل الآن',
    damaged: 'تالف',
    damagedHint: 'وصل لكنه غير صالح للبيع.',
    missing: 'ناقص',
    missingHint: 'مطلوب لكنه لم يصل مع الشحنة.',
    actualCost: 'التكلفة الفعلية',
    actualCostHint: 'ما تقاضاه المورد فعلاً. يحدّث هذا سعر التكلفة.',
    batchNumber: 'رقم الدفعة',
    batchNumberHint: 'اتركه فارغاً ليُولَّد تلقائياً.',
    productionDate: 'تاريخ الإنتاج',
    expiryDate: 'تاريخ الانتهاء',
    storageLocation: 'مكان التخزين',
    deliveryNote: 'إشعار التسليم من المورد',
    confirmReceive: 'إضافة إلى المخزون',
    confirmPendingReceipt: 'لم يتم تأكيد الاستلام السابق بعد. أعد المحاولة للتحقق من الشحنة نفسها.',
    receiving: 'جارٍ التسجيل…',
    receivedMessage: 'تم تسجيل التسليم {number} وتحديث المخزون.',
    nothingEntered: 'أدخل كمية واحدة على الأقل.',
    tooMuch: 'لم يتبقَّ من {product} سوى {outstanding} على هذا الطلب.',
    costHidden: 'تُسجَّل التكاليف من الطلب. لا يُسمح لك بالاطّلاع عليها أو تغييرها.',
    empty: 'لا توجد أوامر شراء بعد.',
    emptyHint: 'أنشئ واحداً عند الطلب من مورد.',
    allStatuses: 'الكل',
    fullyReceived: 'وصل كل ما في هذا الطلب.',
  },

  invoices: {
    title: 'فواتير الموردين',
    add: 'تسجيل فاتورة',
    addTitle: 'تسجيل فاتورة مورد',
    invoiceNumber: 'رقم فاتورة المورد',
    invoiceDate: 'تاريخ الفاتورة',
    dueDate: 'تاريخ الاستحقاق',
    dueDateHint: 'اتركه فارغاً ليُحسب من شروط الدفع المتفق عليها مع المورد.',
    amount: 'المبلغ',
    outstanding: 'المتبقي',
    paid: 'المدفوع',
    linkedOrder: 'مقابل الطلب',
    statusUNPAID: 'غير مدفوعة',
    statusPARTIALLY_PAID: 'مدفوعة جزئياً',
    statusPAID: 'مدفوعة',
    statusOVERDUE: 'متأخرة',
    empty: 'لا توجد فواتير مسجّلة.',
    emptyHint: 'سجّل فاتورة المورد عند وصولها.',
    created: 'تم تسجيل الفاتورة.',
    allStatuses: 'الكل',
    overdueWarning: '{count} متأخرة',
  },

  supplierPayments: {
    title: 'الدفعات للموردين',
    add: 'تسجيل دفعة',
    addTitle: 'دفع لمورد',
    paymentNumber: 'الدفعة',
    againstInvoice: 'مقابل فاتورة',
    onAccount: 'على الحساب',
    amount: 'المبلغ',
    method: 'طريقة الدفع',
    reference: 'المرجع',
    note: 'ملاحظة',
    paidAt: 'التاريخ',
    paidBy: 'دفعها',
    cashWarning:
      'الدفع النقدي للمورد يخرج من الدرج، لذلك يجب أن تكون ورديتك مفتوحة وسينقص النقد المتوقع بهذا المبلغ.',
    submit: 'تسجيل الدفعة',
    submitting: 'جارٍ التسجيل…',
    created: 'تم تسجيل الدفعة.',
    empty: 'لا توجد دفعات مسجّلة.',
  },

  batches: {
    title: 'الدفعات',
    subtitle: 'ما وصل، ومتى، ومتى ينتهي.',
    batchNumber: 'الدفعة',
    product: 'المنتج',
    supplier: 'المورد',
    received: 'تاريخ الاستلام',
    production: 'تاريخ الإنتاج',
    expiry: 'تاريخ الانتهاء',
    initial: 'الوارد',
    remaining: 'المتبقي',
    location: 'مكان التخزين',
    value: 'القيمة',
    state: 'الحالة',
    stateACTIVE: 'سليمة',
    stateNEAR_EXPIRY: 'قاربت الانتهاء',
    stateEXPIRED: 'منتهية',
    stateDEPLETED: 'نفدت',
    daysLeft: 'بقي {days} يوم',
    expiredDaysAgo: 'انتهت منذ {days} يوم',
    expiresToday: 'تنتهي اليوم',
    allStates: 'الكل',
    empty: 'لا توجد دفعات بعد.',
    emptyHint: 'تُنشأ الدفعات عند تسجيل تسليم مع تاريخ انتهاء.',
    alertsTitle: 'تنبيهات الانتهاء',
    nearExpiry: 'قاربت الانتهاء',
    expired: 'منتهية',
    noAlerts: 'لا يوجد ما قارب على الانتهاء.',
    warningWindow: 'التنبيه مضبوط على {days} يوم قبل الانتهاء.',
    deductionNote:
      'تسجّل الدفعات ما وصل ومتى ينتهي. حالياً يخصم البيع من إجمالي المنتج لا من دفعة بعينها — والخصم من الأقدم أولاً هو الخطوة التالية.',
  },

  recipes: {
    title: 'الوصفات',
    subtitle: 'مكوّنات ما يصنعه المحل.',
    add: 'وصفة جديدة',
    addTitle: 'وصفة جديدة',
    edit: 'تعديل الوصفة',
    name: 'الاسم',
    nameEn: 'الاسم بالإنجليزية',
    nameAr: 'الاسم بالعربية',
    makes: 'تنتج',
    output: 'المنتج النهائي',
    outputHint: 'المنتج الذي تصنعه هذه الوصفة. عند الإنتاج يُضاف هذا المنتج بالذات إلى المخزون.',
    batchSize: 'الدفعة الواحدة تنتج',
    batchSizeHint: 'كميات المكوّنات أدناه مكتوبة لدفعة واحدة بهذا الحجم.',
    shelfLife: 'مدة الصلاحية',
    shelfLifeDays: 'أيام',
    shelfLifeHint: 'تُستخدم لتحديد تاريخ انتهاء الدفعة عند إنهاء الإنتاج. اتركها فارغة إن كان لا ينتهي.',
    instructions: 'طريقة التحضير',
    notes: 'ملاحظات',
    ingredients: 'المكوّنات',
    addIngredient: 'إضافة مكوّن',
    ingredient: 'المكوّن',
    quantity: 'الكمية',
    optional: 'اختياري',
    optionalHint: 'المكوّن الاختياري غير المتوفر لا يمنع الإنتاج.',
    removeIngredient: 'حذف',
    inStock: 'المتوفر',
    needed: 'المطلوب',
    notEnough: 'غير كافٍ',
    estimatedCost: 'التكلفة التقديرية',
    estimatedUnitCost: 'تكلفة الوحدة',
    costHidden: 'لا تملك صلاحية الاطّلاع على التكاليف.',
    active: 'قيد الاستخدام',
    used: 'استُخدمت {count} مرة',
    empty: 'لا توجد وصفات بعد.',
    emptyHint: 'اكتب وصفة لكل ما يصنعه المحل من اللحم الخام.',
    created: 'تم حفظ الوصفة.',
    saved: 'تم حفظ الوصفة.',
    deleted: 'تم حذف الوصفة.',
    retired: 'لهذه الوصفة سجل إنتاج، لذلك تم إيقافها بدل حذفها.',
    deleteTitle: 'حذف هذه الوصفة؟',
    deleteMessage: 'الوصفة التي استُخدمت تُوقَف بدل حذفها، حتى يبقى الإنتاج السابق مفهوماً.',
    planOnly: 'الوصفة خطة. كتابتها لا تغيّر المخزون.',
    outputIsIngredient: 'لا يمكن أن تستخدم الوصفة المنتج الذي تصنعه كأحد مكوّناتها.',
    searchPlaceholder: 'ابحث في الوصفات',
    showInactive: 'إظهار الموقوفة',
  },

  production: {
    title: 'الإنتاج',
    subtitle: 'ما صُنع، ومن ماذا، وكيف كانت النتيجة.',
    add: 'تخطيط دفعة',
    addTitle: 'تخطيط دفعة إنتاج',
    productionNumber: 'الدفعة',
    recipe: 'الوصفة',
    status: 'الحالة',
    statusDRAFT: 'مخطّطة',
    statusCOMPLETED: 'منتهية',
    statusCANCELLED: 'ملغاة',
    allStatuses: 'الكل',
    planned: 'المخطّط',
    produced: 'المُنتَج',
    wanted: 'كم تريد أن تنتج؟',
    wantedHint: 'تُحسب كميات المكوّنات من الوصفة بما يوافق ذلك.',
    createdBy: 'خطّطها',
    completedBy: 'أنهاها',
    notes: 'ملاحظات',

    noStockYet:
      'لم يتحرك شيء بعد. تُخصم المكوّنات من المخزون ويُضاف المنتج النهائي فقط عند إنهاء الدفعة.',

    plan: 'حفظ الخطة',
    planning: 'جارٍ الحفظ…',
    plannedMessage: 'تم تخطيط الدفعة {number}. لم يتحرك أي مخزون بعد.',

    finalise: 'إنهاء الدفعة',
    finaliseTitle: 'إنهاء دفعة الإنتاج',
    finaliseIntro:
      'أدخل ما استُخدم فعلاً وما خرج فعلاً. تُخصم المكوّنات ويُضاف المنتج النهائي معاً في خطوة واحدة — وإن نقص أي شيء لا يتحرك شيء إطلاقاً.',
    actuallyUsed: 'المستخدَم فعلاً',
    plannedUse: 'المخطّط',
    available: 'المتوفر',
    producedQuantity: 'كم خرج؟',
    producedHint: 'زِنه. هذا ما يوضع على الرف، وعليه يُحسب المردود.',
    wasteDuring: 'الهدر أثناء الإنتاج',
    wasteDuringHint: 'القصاصات والفاقد. يُسجَّل ولا يوضع على الرف أبداً.',
    addWaste: 'تسجيل هدر',
    confirmFinalise: 'الخصم والإضافة إلى المخزون',
    finalising: 'جارٍ التسجيل…',
    finishedMessage: 'انتهت الدفعة {number}. أُضيف {produced} إلى المخزون.',
    shortOf: 'لا يوجد ما يكفي من {product}: المتوفر {available}، والمطلوب {needed}.',
    nothingUsed: 'أدخل كمية مكوّن واحد على الأقل.',

    cancel: 'إلغاء الدفعة',
    cancelTitle: 'إلغاء هذه الدفعة؟',
    cancelMessage: 'لم يُستهلك شيء، فلا يتغيّر المخزون. تُلغى الخطة فقط.',
    cancelReason: 'ما سبب الإلغاء؟',
    cancelled: 'تم إلغاء الدفعة.',

    ingredientsUsed: 'المكوّنات المستخدمة',
    fromBatches: 'مأخوذة من',
    yield: 'المردود',
    yieldHint: 'ما خرج نسبةً إلى ما دخل.',
    ingredientCost: 'تكلفة المكوّنات',
    unitCost: 'تكلفة الوحدة',
    outputBatch: 'الدفعة المُنتَجة',
    expires: 'تنتهي',
    empty: 'لا توجد دفعات إنتاج بعد.',
    emptyHint: 'خطّط دفعة عندما تكون على وشك تحضير شيء.',
    completedAt: 'انتهت',
  },

  waste: {
    title: 'الهدر',
    subtitle: 'مخزون خرج دون أن يُباع.',
    add: 'تسجيل هدر',
    addTitle: 'تسجيل هدر',
    wasteNumber: 'السجل',
    product: 'المنتج',
    quantity: 'الكمية',
    reason: 'السبب',
    note: 'ماذا حصل؟',
    batch: 'الدفعة',
    batchHint: 'اختر دفعة إن كنت ترمي كمية من دفعة بعينها.',
    value: 'قيمة الخسارة',
    recordedBy: 'سجّلها',
    when: 'التاريخ',
    fromProduction: 'خلال الدفعة',
    allReasons: 'كل الأسباب',

    reasonPRODUCTION_TRIM: 'قصاصات',
    reasonEXPIRED: 'منتهي الصلاحية',
    reasonSPOILED: 'فاسد',
    reasonDAMAGED: 'تالف',
    reasonCUSTOMER_RETURN: 'إرجاع من زبون',
    reasonPREPARATION_ERROR: 'خطأ في التحضير',
    reasonOTHER: 'سبب آخر',

    submit: 'تسجيل',
    submitting: 'جارٍ التسجيل…',
    created: 'تم تسجيل الهدر {number} وخصمه من المخزون.',
    needsApproval: 'قيمة هذا الهدر أعلى من الحد المسموح، لذا يجب أن يوافق عليه مدير.',
    approvalTitle: 'الموافقة على هذا الهدر',
    tooMuch: 'المتوفر في المخزون {available} فقط.',
    empty: 'لم يُرمَ شيء.',
    emptyHint: 'وهذا أمر جيد.',
    totalLost: 'إجمالي الخسارة',
    costHidden: 'لا تملك صلاحية الاطّلاع على قيمة الهدر.',
  },

  productionReport: {
    title: 'الإنتاج والهدر',
    subtitle: 'ما صُنع وما ضاع.',
    tabProduction: 'الإنتاج',
    tabWaste: 'الهدر',
    from: 'من',
    to: 'إلى',
    runCount: 'الدفعات',
    producedTotal: 'المُنتَج',
    consumedTotal: 'المواد الخام المستخدمة',
    wasteTotal: 'الهدر',
    overallYield: 'المردود العام',
    totalCost: 'تكلفة المكوّنات',
    byProduct: 'حسب المنتج',
    byReason: 'حسب السبب',
    recentRuns: 'الدفعات',
    noRuns: 'لم يُصنع شيء في هذه الفترة.',
    noWaste: 'لم يُرمَ شيء في هذه الفترة.',
    records: 'السجلات',
  },

  customers: {
    title: 'العملاء',
    subtitle: 'الزبائن الذين يتعامل معهم المحل بالاسم.',
    add: 'عميل جديد',
    addTitle: 'عميل جديد',
    edit: 'تعديل العميل',
    code: 'الرمز',
    name: 'الاسم',
    phone: 'الهاتف',
    altPhone: 'رقم ثانٍ',
    email: 'البريد الإلكتروني',
    type: 'النوع',
    notes: 'ملاحظات',
    active: 'عميل حالي',
    searchPlaceholder: 'ابحث بالاسم أو الهاتف أو الرمز',
    showInactive: 'إظهار العملاء السابقين',
    allTypes: 'كل الأنواع',

    typeRETAIL: 'مفرّق',
    typeWHOLESALE: 'جملة',
    typeRESTAURANT: 'مطعم',
    typeSTAFF: 'موظف',

    orders: 'الطلبات',
    purchases: 'المشتريات',
    empty: 'لا يوجد عملاء بعد.',
    emptyHint: 'أضف من يطلب بالاسم أو يشتري على الحساب.',
    created: 'تمت إضافة {name}.',
    saved: 'تم حفظ العميل.',
    deleted: 'تم حذف العميل.',
    deleteTitle: 'حذف هذا العميل؟',
    deleteMessage: 'لا يُحذف إلا عميل ليس عليه أي مبلغ. تبقى مبيعاته السابقة محفوظة.',

    creditHidden: 'لا تملك صلاحية الاطّلاع على أرصدة العملاء أو ذممهم.',

    addresses: 'العناوين',
    addAddress: 'إضافة عنوان',
    addressLabel: 'اسم العنوان',
    addressLabelHint: 'مثلاً: المنزل، المطعم، المستودع.',
    area: 'المنطقة',
    street: 'الشارع',
    addressDetails: 'البناية والطابق والشقة',
    deliveryInstructions: 'تعليمات التوصيل',
    deliveryInstructionsHint: 'مثلاً: اقرع الجرس مرتين، اترك الطلب مع الناطور، اتصل عند الوصول.',
    isDefault: 'استخدمه أولاً',
    noAddresses: 'لا توجد عناوين بعد.',
    addressSaved: 'تم حفظ العنوان.',
    addressRemoved: 'تم حذف العنوان.',

    tabDetails: 'التفاصيل',
    tabStatement: 'كشف الحساب',
    tabHistory: 'المشتريات',
  },

  credit: {
    title: 'الذمم',
    settings: 'إعدادات الذمم',
    allowed: 'يمكنه الشراء على الحساب',
    allowedHint: 'مغلق افتراضياً. تُمنح الذمة بقرار، لا بالصدفة.',
    limit: 'سقف الذمة',
    limitHint: 'أقصى مبلغ يمكن أن يترتب عليه في أي لحظة.',
    terms: 'مهلة السداد بالأيام',
    termsHint: 'كم يوماً بعد البيع على الحساب يُعتبر المبلغ متأخراً.',
    balance: 'المستحق عليه',
    available: 'المتبقي من الذمة',
    overdue: 'متأخر',
    overdueSince: 'أقدم مبلغ غير مسدّد منذ {date}',
    settled: 'لا يوجد مستحق',
    limitSaved: 'تم حفظ إعدادات الذمة.',

    blocked: 'الذمة موقوفة',
    block: 'إيقاف الشراء على الحساب',
    unblock: 'السماح بالشراء على الحساب',
    blockTitle: 'إيقاف الشراء على الحساب؟',
    blockMessage: 'يبقى السقف كما هو، لكن لا يمكن تسجيل أي بيع جديد على الحساب حتى يُرفع الإيقاف.',
    blockReason: 'السبب؟',
    blocked_: 'الذمة موقوفة: {reason}',
    blockedShort: 'موقوف',
    blockSaved: 'تم تحديث إيقاف الذمة.',

    statement: 'كشف الحساب',
    when: 'التاريخ',
    detail: 'البيان',
    charge: 'مدين',
    paid: 'دائن',
    runningBalance: 'الرصيد',
    due: 'الاستحقاق',
    typeSALE: 'بيع على الحساب',
    typePAYMENT: 'دفعة مستلمة',
    typeREFUND: 'إرجاع',
    typeADJUSTMENT: 'تصحيح',
    noTransactions: 'لا توجد حركات على الحساب بعد.',
    appendOnly:
      'كل سطر هنا دائم. يُصحَّح الخطأ بإضافة قيد معاكس، لا بتغيير ما هو مكتوب أصلاً.',

    adjust: 'تصحيح الرصيد',
    adjustTitle: 'تصحيح هذا الرصيد',
    adjustMessage: 'يضيف هذا قيداً تصحيحياً. تبقى القيود الأصلية كما هي تماماً.',
    adjustAmount: 'بكم؟',
    adjustAmountHint: 'الرقم الموجب يزيد المستحق عليه، والسالب ينقصه.',
    adjustReason: 'السبب؟',
    adjusted: 'تم تصحيح الرصيد.',
    approvalTitle: 'الموافقة على هذا التصحيح',

    outstanding: 'الذمم المستحقة',
    outstandingSubtitle: 'كل من عليه مبلغ، الأسوأ أولاً.',
    totalOwed: 'إجمالي المستحق',
    totalOverdue: 'إجمالي المتأخر',
    onlyOverdue: 'المتأخر فقط',
    noneOwing: 'لا يوجد أي مبلغ مستحق.',
  },

  customerPayments: {
    title: 'دفعات العملاء',
    add: 'استلام دفعة',
    addTitle: 'استلام دفعة',
    paymentNumber: 'الدفعة',
    customer: 'العميل',
    amount: 'المبلغ',
    method: 'الطريقة',
    reference: 'المرجع',
    note: 'ملاحظة',
    when: 'التاريخ',
    receivedBy: 'استلمها',
    owes: 'المستحق حالياً',
    payAll: 'سداد الكل',
    cashWarning:
      'المبلغ النقدي من العميل يدخل إلى الصندوق، لذا يجب أن تكون ورديتك مفتوحة وسيزيد النقد المتوقع بهذا المبلغ.',
    tooMuch: 'لا يمكن أن يدفع العميل أكثر مما عليه.',
    submit: 'تسجيل الدفعة',
    submitting: 'جارٍ التسجيل…',
    created: 'تم تسجيل الدفعة {number}. الرصيد الآن {balance}.',
    empty: 'لا توجد دفعات بعد.',
    totalTaken: 'إجمالي المستلم',
  },

  customerOrders: {
    title: 'الطلبات',
    subtitle: 'ما يجب تحضيره، وإلى أين يذهب.',
    add: 'تسجيل طلب',
    addTitle: 'تسجيل طلب',
    orderNumber: 'الطلب',
    customer: 'العميل',
    status: 'الحالة',
    fulfilment: 'الطريقة',
    fulfilmentPICKUP: 'استلام من المحل',
    fulfilmentDELIVERY: 'توصيل',
    address: 'العنوان',
    requestedFor: 'مطلوب في',
    estimate: 'التقدير',
    estimateHint: 'تقدير بأسعار اليوم. الرقم الفعلي يأتي من الفاتورة، لأن اللحم يُباع بالوزن.',
    items: 'الأصناف',
    addItem: 'إضافة صنف',
    quantity: 'الكمية',
    preparationNotes: 'طريقة التقطيع',
    preparationNotesHint: 'مثلاً: مكعبات، مفروم مرتين، بدون دهن.',
    orderNotes: 'ملاحظات على الطلب كله',
    deliveryNotes: 'ملاحظات للسائق',
    pickupOnlyHint: 'هذا النموذج لطلبات الاستلام فقط. يُنشأ طلب التوصيل من الطلب الحالي في نقطة البيع مع رسوم توصيل.',
    driver: 'السائق',
    takenBy: 'سجّله',
    billedAs: 'فوتر باسم',
    notBilledYet: 'لم يُفوتَر بعد',

    statusNEW: 'جديد',
    statusPREPARING: 'قيد التحضير',
    statusREADY: 'جاهز',
    statusOUT_FOR_DELIVERY: 'خرج للتوصيل',
    statusDELIVERED: 'تم التسليم',
    statusCANCELLED: 'ملغى',
    allStatuses: 'الكل',
    onlyOpen: 'قيد التنفيذ',

    noStockYet:
      'الطلب قائمة عمل. لا يحرّك مخزوناً ولا مالاً — الفاتورة على الصندوق هي التي تفعل الأمرين.',

    start: 'بدء التحضير',
    markReady: 'تحديده جاهزاً',
    markDelivered: 'تحديده مُسلَّماً',
    cancel: 'إلغاء الطلب',
    cancelTitle: 'إلغاء هذا الطلب؟',
    cancelMessage: 'لم يُبَع شيء، فلا يتغيّر مال ولا مخزون. يُلغى الطلب فقط.',
    cancelReason: 'السبب؟',
    cancelled: 'تم إلغاء الطلب.',
    statusChanged: 'تم تحديث الطلب.',
    created: 'تم تسجيل الطلب {number}.',

    prepared: 'جُهّز',
    notPrepared: 'لم يُجهّز بعد',
    allPrepared: 'تم تحضير كل ما في هذا الطلب.',
    ticket: 'بطاقة التحضير',
    printTicket: 'طباعة البطاقة',

    empty: 'لا توجد طلبات بعد.',
    emptyHint: 'سجّل طلباً عندما يتصل زبون أو يطلب تحضير شيء.',
    noOpen: 'لا يوجد شيء بالانتظار.',
  },

  // --- Phase 8 -------------------------------------------------------------

  reportSuite: {
    title: 'التقارير',
    subtitle: 'كل ما قام به المحل، بأي تقسيم تحتاجه.',

    tabs: {
      sales: 'المبيعات',
      profit: 'الأرباح',
      inventory: 'قيمة المخزون',
      purchases: 'المشتريات',
      expenses: 'المصروفات',
      cashiers: 'الكاشير',
      customers: 'العملاء',
      production: 'الإنتاج والهدر',
    },

    filters: {
      title: 'التصفية',
      from: 'من',
      to: 'إلى',
      product: 'المنتج',
      category: 'الفئة',
      cashier: 'الكاشير',
      customer: 'العميل',
      supplier: 'المورّد',
      paymentMethod: 'طريقة الدفع',
      status: 'الحالة',
      apply: 'تطبيق',
      clear: 'مسح التصفية',
      anyProduct: 'كل المنتجات',
      anyCategory: 'كل الفئات',
      anyCashier: 'كل الكاشير',
      anyCustomer: 'كل العملاء',
      anySupplier: 'كل المورّدين',
      anyMethod: 'كل الطرق',
      anyStatus: 'كل الحالات',
      today: 'اليوم',
      last7: 'آخر ٧ أيام',
      last30: 'آخر ٣٠ يوماً',
      thisMonth: 'هذا الشهر',
      showing: 'من {from} إلى {to} بتوقيت بيروت',
      backwards: 'تاريخ البداية بعد تاريخ النهاية.',
    },

    export: {
      title: 'تنزيل',
      csv: 'CSV',
      excel: 'إكسل',
      pdf: 'PDF',
      working: 'جارٍ التجهيز…',
      failed: 'لم ينجح التنزيل.',
      note: 'يحتوي الملف على ما تراه هنا تماماً — لا أكثر.',
    },

    summary: {
      sales: 'المبيعات',
      itemsSold: 'الأصناف المباعة',
      revenue: 'الإيرادات',
      netRevenue: 'صافي الإيرادات',
      discounts: 'الخصومات',
      refunded: 'المرتجعات',
      averageSale: 'متوسط الفاتورة',
      cost: 'تكلفة البضاعة',
      grossProfit: 'الربح الإجمالي',
      netProfit: 'صافي الربح',
      margin: 'الهامش',
      grossMargin: 'الهامش الإجمالي',
      netMargin: 'هامش الصافي',
      expenses: 'المصروفات',
      waste: 'الهدر',
      products: 'المنتجات',
      lowStock: 'مخزون منخفض',
      outOfStock: 'نفد المخزون',
      batches: 'الدفعات النشطة',
      stockAtCost: 'المخزون بالتكلفة',
      stockAtRetail: 'المخزون بسعر البيع',
      potentialMargin: 'الهامش المتوقع',
      orders: 'الطلبيات',
      deliveries: 'التسليمات',
      invoices: 'الفواتير',
      payments: 'الدفعات',
      received: 'البضاعة المستلمة',
      invoiced: 'المفوتر',
      paid: 'المدفوع',
      outstanding: 'المتبقي',
      cashiers: 'الكاشير',
      shifts: 'الورديات',
      shiftsOver: 'أدراج زائدة',
      shiftsShort: 'أدراج ناقصة',
      netDifference: 'صافي الفرق',
      customersWhoBought: 'عملاء اشتروا',
      totalSpend: 'إجمالي الشراء',
      customersOwing: 'عملاء عليهم دين',
      totalOwed: 'إجمالي المستحق',
      paymentsTaken: 'الدفعات المقبوضة',
      runs: 'دورات الإنتاج',
      produced: 'المُنتَج',
      consumed: 'المواد المستهلكة',
      yield: 'نسبة الإنتاج',
      wasteRecords: 'سجلات الهدر',
      wasted: 'المهدور',
      ingredientCost: 'تكلفة المكوّنات',
      wasteCost: 'تكلفة الهدر',
    },

    tables: {
      byDay: 'حسب اليوم',
      byHour: 'حسب الساعة',
      byProduct: 'حسب المنتج',
      byCategory: 'حسب الفئة',
      byMethod: 'حسب طريقة الدفع',
      bySupplier: 'حسب المورّد',
      byCashier: 'حسب الكاشير',
      byCustomer: 'حسب العميل',
      byStatus: 'حسب الحالة',
      byReason: 'حسب السبب',
      shifts: 'الورديات',
      overdue: 'الحسابات المتأخرة',
      stockOnHand: 'المخزون المتوفر',
      movements: 'حركات المخزون',
      runs: 'دورات الإنتاج',
      wasteByReason: 'الهدر حسب السبب',
      wasteByProduct: 'الهدر حسب المنتج',
      expensesByCategory: 'المصروفات حسب الفئة',
      howProfitIsWorkedOut: 'كيف يُحتسب الربح',
    },

    columns: {
      day: 'اليوم',
      hour: 'الساعة',
      count: 'العدد',
      sales: 'المبيعات',
      quantity: 'الكمية',
      revenue: 'الإيراد',
      cost: 'التكلفة',
      profit: 'الربح',
      margin: 'الهامش',
      amount: 'المبلغ',
      product: 'المنتج',
      category: 'الفئة',
      method: 'الطريقة',
      currency: 'العملة',
      supplier: 'المورّد',
      customer: 'العميل',
      code: 'الرمز',
      type: 'النوع',
      balance: 'الرصيد',
      spend: 'الشراء',
      phone: 'الهاتف',
      since: 'متأخر منذ',
      cashier: 'الكاشير',
      shift: 'الوردية',
      opened: 'الفتح',
      closed: 'الإغلاق',
      expected: 'المتوقع',
      counted: 'المعدود',
      difference: 'الفرق',
      refunds: 'المرتجعات',
      voids: 'الملغاة',
      discounts: 'الخصومات',
      average: 'المتوسط',
      status: 'الحالة',
      inStock: 'المتوفر',
      minimum: 'الحد الأدنى',
      unitCost: 'تكلفة الوحدة',
      valueAtCost: 'القيمة بالتكلفة',
      valueAtRetail: 'القيمة بسعر البيع',
      reason: 'السبب',
      netChange: 'صافي التغيّر',
      run: 'الدورة',
      finished: 'انتهت',
      made: 'المُنتَج',
      yield: 'نسبة الإنتاج',
      deliveries: 'التسليمات',
      invoiced: 'المفوتر',
      paid: 'المدفوع',
      outstanding: 'المتبقي',
      line: 'البند',
      records: 'السجلات',
      open: 'مفتوحة',
    },

    profitLines: {
      revenue: 'الإيرادات',
      lessCost: 'ناقص: تكلفة البضاعة المباعة',
      grossProfit: 'الربح الإجمالي',
      lessRefunds: 'ناقص: المرتجعات',
      lessWaste: 'ناقص: الهدر',
      lessExpenses: 'ناقص: المصروفات',
      netProfit: 'صافي الربح',
    },

    stockStatus: {
      OK: 'متوفر',
      LOW: 'منخفض',
      OUT: 'نفد',
    },

    empty: 'لم يحدث شيء في هذه الفترة.',
    emptyHint: 'جرّب فترة أوسع أو امسح التصفية.',
    profitHidden: 'التكلفة والربح غير معروضين لصلاحيتك.',
    valueHidden: 'قيم المخزون غير معروضة لصلاحيتك.',
    creditHidden: 'أرصدة العملاء غير معروضة لصلاحيتك.',
  },

  imports: {
    title: 'استيراد البيانات',
    subtitle: 'حمّل الفئات أو المنتجات أو الأسعار أو المخزون أو المورّدين أو العملاء من ملف.',

    kinds: {
      CATEGORIES: 'الفئات',
      PRODUCTS: 'المنتجات',
      PRICES: 'الأسعار',
      OPENING_INVENTORY: 'المخزون الافتتاحي',
      SUPPLIERS: 'المورّدون',
      CUSTOMERS: 'العملاء',
    },

    steps: {
      choose: 'اختر ما تريد استيراده',
      upload: 'ارفع الملف',
      map: 'تحقّق من الأعمدة',
      review: 'المراجعة',
      done: 'انتهى',
    },

    template: {
      title: 'غير متأكد من الشكل؟',
      body: 'نزّل نموذجاً بالعناوين الصحيحة وصف مثال واحد.',
      csv: 'نموذج (CSV)',
      excel: 'نموذج (إكسل)',
    },

    upload: {
      choose: 'اختر ملفاً',
      chooseAgain: 'اختر ملفاً آخر',
      accepted: 'CSV أو إكسل (.xlsx)، حتى ٨ ميغابايت.',
      preview: 'افحص الملف',
      previewing: 'جارٍ قراءة الملف…',
      noFile: 'اختر ملف CSV أو إكسل أولاً.',
    },

    mapping: {
      title: 'الأعمدة',
      body: 'كل حقل أدناه مرتبط بعمود في ملفك. صحّح ما هو خاطئ.',
      field: 'الحقل',
      column: 'العمود في ملفك',
      notMatched: 'غير مرتبط',
      required: 'مطلوب',
      example: 'مثال: {example}',
      unmatchedHeadings: 'أعمدة في ملفك لم تُستخدم:',
      missingRequired: 'الأعمدة المطلوبة التالية ما زالت ناقصة:',
      recheck: 'أعد الفحص بهذه الأعمدة',
    },

    review: {
      title: 'ماذا سيحدث',
      totalRows: 'الصفوف في الملف',
      validRows: 'صفوف جاهزة',
      rejectedRows: 'صفوف بها مشكلة',
      willCreate: 'ستُضاف',
      willUpdate: 'ستُحدَّث',
      matchedOn: 'كيف يتم التعرّف على السجلات الموجودة',
      sample: 'أول الصفوف كما ستُحفظ',
      action: 'الإجراء',
      create: 'إضافة',
      update: 'تحديث',
      line: 'السطر',
      problems: 'المشاكل المكتشفة',
      problemsBody: 'كل سطر أدناه مرقّم كما يظهر في ملفك.',
      noProblems: 'كل الصفوف سليمة.',
      allOrNothing: 'لن يتم استيراد أي شيء ما دامت هناك مشاكل.',
      allOrNothingBody:
        'الملف الذي فيه خطأ هو عادةً ملف يحتاج تصحيحاً، لا ملف يُحمّل نصفه. صحّح الأسطر أعلاه ثم افحص الملف مجدداً.',
      validRowsOnly: 'استورد الصفوف الجاهزة فقط وتجاهل الباقي',
      validRowsOnlyWarning:
        'لن يتم استيراد الصفوف المذكورة أعلاه وعددها {count}. عليك تصحيحها واستيرادها لاحقاً.',
      commit: 'استورد الآن',
      committing: 'جارٍ الاستيراد…',
      cancel: 'ابدأ من جديد',
    },

    outcome: {
      committed: 'تم الاستيراد بنجاح',
      rejected: 'لم يتم استيراد أي شيء',
      created: 'تمت إضافة {count}',
      updated: 'تم تحديث {count}',
      skipped: 'تم تجاهل {count}',
      again: 'استورد ملفاً آخر',
    },

    history: {
      title: 'سجل الاستيراد',
      empty: 'لم يتم استيراد أي شيء بعد.',
      file: 'الملف',
      kind: 'النوع',
      result: 'النتيجة',
      rows: 'الصفوف',
      by: 'بواسطة',
      when: 'التاريخ',
      showPreviews: 'إظهار عمليات الفحص',
      statusPREVIEWED: 'فحص فقط',
      statusCOMMITTED: 'تم الاستيراد',
      statusREJECTED: 'مرفوض',
      partial: 'الصفوف الصالحة فقط',
    },
  },

  notifications: {
    title: 'التنبيهات',
    open: 'التنبيهات',
    empty: 'لا شيء يحتاج انتباهك.',
    emptyHint: 'تظهر هنا تنبيهات المخزون والصلاحية والمبالغ المستحقة وفروقات الصندوق.',
    acknowledge: 'تمّت المشاهدة',
    acknowledgeAll: 'تعليم الكل كمشاهد',
    refresh: 'تحقّق الآن',
    refreshing: 'جارٍ التحقق…',
    showAcknowledged: 'إظهار ما تمت مشاهدته',
    seen: 'شوهد',
    seenBy: 'شاهده {name}',
    resolved: 'انتهى',
    viewAll: 'عرض الكل',

    severity: {
      INFO: 'للعلم',
      WARNING: 'يحتاج انتباهاً',
      CRITICAL: 'عاجل',
    },

    kinds: {
      LOW_STOCK: 'مخزون منخفض',
      NEAR_EXPIRY: 'قارب على الانتهاء',
      EXPIRED_STOCK: 'منتهي الصلاحية',
      SUPPLIER_INVOICE_OVERDUE: 'فاتورة مورّد متأخرة',
      CUSTOMER_CREDIT_OVERDUE: 'رصيد عميل متأخر',
      CUSTOMER_CREDIT_LIMIT: 'سقف الدين',
      SHIFT_DIFFERENCE: 'فرق في الصندوق',
      LARGE_WASTE: 'هدر كبير',
      BACKUP_FAILED: 'فشل النسخ الاحتياطي',
    },
  },

  backups: {
    title: 'النسخ الاحتياطي',
    subtitle: 'هل المحل محمي، وكيف تبقيه كذلك.',

    status: {
      protected: 'توجد نسخة احتياطية',
      overdue: 'لا توجد نسخة حديثة',
      never: 'لم يتم النسخ أبداً',
      lastSuccess: 'آخر نسخة ناجحة',
      hoursAgo: 'قبل {hours} ساعة',
      justNow: 'قبل أقل من ساعة',
      warnAfter: 'يظهر تحذير بعد {hours} ساعة.',
      failures: '{count} محاولات فاشلة مسجّلة',
      lastAttemptFailed: 'فشلت المحاولة الأخيرة.',
      neverBody: 'لم يتم أخذ أي نسخة احتياطية بعد. إذا تعطّل هذا الجهاز اليوم، سيضيع كل ما فيه.',
    },

    run: {
      title: 'انسخ الآن',
      body: 'سيكتب الخادم ملف نسخة احتياطية في مجلد النسخ.',
      button: 'انسخ الآن',
      working: 'جارٍ النسخ…',
      disabled: 'النسخ التلقائي معطّل على هذا الخادم.',
      disabledBody:
        'خذ نسخة احتياطية بالأمر أدناه ثم سجّلها هنا. هذا الإعداد أكثر أماناً: الخادم لا يملك القدرة على إخراج قاعدة البيانات كاملة إلى ملف عند الطلب.',
    },

    manual: {
      title: 'خذ نسخة احتياطية يدوياً',
      body: 'تُأخذ النسخ تلقائياً كل 15 دقيقة. لأخذ نسخة من الطرفية أو للاستعادة، اتبع الإجراء المكتوب.',
      seeDocs: 'الأوامر الدقيقة في docs/backup-and-restore.md. تُحفظ هناك لا على هذه الشاشة لتبقى الصفحة عن حماية المحل، ولتبقى الاستعادة — التي تستبدل كل ما سُجّل منذ أخذ النسخة — فعلاً مقصوداً.',
      record: 'سجّل نسخة أخذتها',
      recordBody: 'لكي تتوقف الشاشة عن تحذيرك بينما أنت تنسخ بانتظام.',
      fileName: 'اسم الملف',
      fileNamePlaceholder: 'yaghi-backup-2026-07-30.dump',
      note: 'ملاحظة',
      save: 'سجّلها',
      saving: 'جارٍ التسجيل…',
    },

    restore: {
      title: 'الاستعادة',
      body: 'الاستعادة تستبدل كل شيء ولا يمكن التراجع عنها، لذلك تتم من سطر الأوامر بقرار من شخص — لا بزر.',
      link: 'اقرأ تعليمات الاستعادة',
    },

    history: {
      title: 'السجل',
      empty: 'لا توجد نسخ مسجّلة بعد.',
      outcome: 'النتيجة',
      file: 'الملف',
      size: 'الحجم',
      duration: 'استغرقت',
      by: 'بواسطة',
      when: 'التاريخ',
      succeeded: 'نجحت',
      failed: 'فشلت',
      seconds: '{count} ثانية',
      automatic: 'مجدولة',
    },

    security: {
      title: 'ملاحظة أمنية',
      body: 'ملفات النسخ الاحتياطي لا يمكن تنزيلها أبداً من هذا الموقع، وكلمة مرور قاعدة البيانات لا تُعرض. احتفظ بنسخك على قرص غير القرص الذي تعمل عليه PostgreSQL.',
    },
  },

  ownerDashboard: {
    title: 'اليوم',
    subtitle: 'كيف يسير المحل.',
    ranges: {
      today: 'اليوم',
      week: 'آخر ٧ أيام',
      month: 'آخر ٣٠ يوماً',
    },
    takings: 'المقبوضات',
    attention: 'يحتاج انتباهاً',
    topProducts: 'الأكثر مبيعاً',
    noSales: 'لا مبيعات في هذه الفترة.',
    lowStockCount: '{count} منتجات منخفضة أو نافدة',
    expiringCount: '{count} دفعات قاربت على الانتهاء',
    supplierOverdue: 'مستحق للمورّدين',
    customerOverdue: 'مستحق على العملاء',
    allClear: 'لا شيء يحتاج انتباهاً.',
    openReports: 'افتح التقارير الكاملة',
    takingsHidden: 'أرقام المبيعات غير معروضة لصلاحيتك.',
  },

  // --- Phase 9 -------------------------------------------------------------


  // --- operational changes -------------------------------------------------

  held: {
    hold: 'علّق الطلب',
    holdTitle: 'تعليق هذا الطلب',
    holdBody: 'تُحفظ السلة وتُفرَّغ الشاشة، لتتمكن من خدمة الزبون التالي.',
    heldOrders: 'الطلبات المعلّقة',
    heldCount: 'الطلبات المعلّقة ({count})',
    label: 'التسمية',
    labelHint: 'أي شيء تتذكره به: أحمد، بالانتظار.',
    customerName: 'اسم الزبون',
    phone: 'الهاتف',
    note: 'ملاحظة',
    chooseCustomer: 'اختر عميلاً',
    walkIn: 'زبون عابر',
    confirmHold: 'علّقه',
    holding: 'جارٍ التعليق…',
    heldState: 'معلّق',

    nothingHeld: 'لا توجد طلبات معلّقة.',
    nothingHeldHint: 'علّق السلة عندما لا يكون الزبون جاهزاً، وستنتظر هنا.',
    restore: 'استعادة',
    restoring: 'جارٍ الاستعادة…',
    cancel: 'إلغاء السلة',
    cancelTitle: 'إلغاء هذه السلة المعلّقة؟',
    cancelBody: 'لم يُبَع شيء، فلا شيء يُعكس. تُزال السلة فقط.',
    cancelReason: 'لماذا يتم الإلغاء؟',
    cancelConfirm: 'ألغِها',
    items: '{count} أصناف',
    heldBy: 'علّقها {name}',
    heldAt: 'عُلّقت {when}',
    mine: 'الخاصة بي',
    everyone: 'الجميع',
    cartNotEmpty: 'أنهِ الطلب الحالي أو امسحه قبل استعادة طلب آخر.',
    restored: 'تمت استعادة السلة',
    restoredBody: 'الطلب عاد إلى الشاشة.',

    warningsTitle: 'تغيّرت هذه السلة منذ تعليقها',
    warningsBody: 'تحقق من هذه النقاط قبل قبض الثمن.',
    priceChanged: '{name}: كان {was}، والآن {now}',
    unavailable: '{name} لم يعد متوفراً ولم يُضف.',
    insufficient: '{name}: المتبقي {available} فقط، والمطلوب {requested}.',
    heldTotal: 'الإجمالي عند التعليق',
    currentTotal: 'إجمالي اليوم',
    acknowledge: 'أبلغتُ الزبون',
    acknowledgeHint: 'ضع علامة للمتابعة بأسعار اليوم.',
  },

  delivery: {
    receivingForTitle: 'استلام عن {name}',
    receivingForBody: 'أرسل {name} هذا التوصيل، لذلك يدخل المبلغ إلى درجه ({shift})، وليس درجك.',
    receiveFor: 'استلام عن {name}',
    accountsAllCashiers: 'كل الكاشيرية ({count})',
    accountsScopeHint: 'عرض المالك والمدير. يرى الكاشير فقط التوصيلات التي أرسلها.',
    fee: 'رسوم التوصيل',
    feeNoRate: 'لم يُحدد سعر صرف، لذلك لا يمكن تحويل رسم بالليرة.',
    amountConverted: '{name}: {amount} تصبح {quantity} على بطاقة التحضير.',
    itemsSubtotal: 'الأصناف',
    finalTotal: 'الإجمالي المطلوب',

    stockReserved: 'المخزون محجوز وليس مخصوماً. يُخصم عند دفع الطلب.',
    newOrder: 'الطلب التالي',

    openInPos: 'افتح نقطة البيع بوضع التوصيل',
    collectPayment: 'اقبض الثمن',
    reopened: 'أُعيد فتح الطلب {number}',
    reopenedBody: 'اقبض الثمن لإتمامه. الأصناف موجودة على الشاشة.',
    alreadyPaid: 'هذا الطلب مدفوع مسبقاً.',
    unpaid: 'غير مدفوع',
    paid: 'مدفوع',

    dispatchTitle: 'إنشاء طلب التوصيل',
    dispatchConfirm: 'إنشاء طلب التوصيل',
    dispatching: 'جارٍ الإنشاء…',
    dispatchHint: 'ستُطبع فاتورة الزبون الآن ويأخذها السائق مع الطلب. لا يُسجَّل أي دفع حتى استلام الحساب.',
    dispatchFailed: 'تعذّر إنشاء طلب التوصيل',
    dispatched: 'أُنشئ طلب التوصيل',
    dispatchedTitle: 'خرج طلب التوصيل',
    dispatchedBody: 'أُنشئ الطلب {number} وطُبعت فاتورته ليأخذها السائق.',
    awaitingAccountHint: 'لم يُسجَّل أي دفع. يبقى الطلب في «حسابات التوصيل» داخل نقطة البيع بانتظار استلام الحساب.',
    viewDeliveries: 'الذهاب إلى حسابات التوصيل',
    feeMakesDelivery: 'رسوم التوصيل تجعل هذا الطلب طلب توصيل.',
    useStandingFee: 'الرسوم المعتادة: {amount}',
    noPermission: 'لا تملك صلاحية إنشاء طلبات التوصيل.',
    offlineBlocked: 'لا يمكن إنشاء طلب توصيل بدون اتصال بالخادم.',
    collectInView: 'يُحصَّل حساب هذا التوصيل من حسابات التوصيل داخل نقطة البيع.',

    awaitingAccount: 'بانتظار الحساب',
    accountReceived: 'تم استلام الحساب',

    posViewLabel: 'عرض عمود الطلب',
    currentOrderTab: 'الطلب الحالي',
    accountsTab: 'حسابات التوصيل',
    pendingCountLine: '{count} بانتظار الحساب',
    viewAllOrders: 'عرض كل الطلبات',
    collectAccount: 'استلام الحساب',
    leftAt: 'خرج:',
    orderRef: 'طلب التوصيل',
    receiptRef: 'الإيصال:',
    noneAwaiting: 'لا حسابات معلّقة',
    noneAwaitingHint: 'كل توصيلة خرجت عاد حسابها.',
  },

  kitchen: {
    ticket: 'بطاقة التحضير',
    ticketNumber: 'البطاقة {number}',
    print: 'اطبع البطاقة',
    reprint: 'اطبع نسخة أخرى',
    reprinting: 'جارٍ الطباعة…',
    copyNumber: 'النسخة {number}',
    noTicket: 'لا شيء في هذا الطلب يحتاج تقطيعاً.',
    noTicketHint: 'تُصدر بطاقة التحضير فقط للفئات الموجّهة إلى المطبخ.',
    forPreparation: 'للتحضير',
    // Deliberately English, in every language. It is the word shouted across a
    // shop, and the one everybody recognises on a printed ticket whichever
    // language they read.
    deliveryHeading: 'DELIVERY',
    urgentHeading: 'عاجل',
    printedAt: 'وقت الطباعة',
    notABill: 'هذه ليست فاتورة، ولا تظهر فيها أي أسعار',
    orderTypeLabel: 'النوع',
    wantedBy: 'الوقت المطلوب',

    customerLabel: 'الزبون',
    onlineOrderLabel: 'طلب المنيو',
    phoneLabel: 'الهاتف',
    addressLabel: 'العنوان',
    cashierLabel: 'الكاشير',
    orderNumberLabel: 'رقم الطلب',
    saleNumberLabel: 'رقم الفاتورة',
    preparationStatusLabel: 'حالة التحضير',
    categoryLabel: 'الصنف',
    weightLabel: 'الوزن',
    quantityLabel: 'الكمية',
    notesLabel: 'ملاحظات',
    cuttingInstructionsLabel: 'تعليمات التقطيع',
    orderNotesLabel: 'ملاحظات الطلب',

    typeCOUNTER: 'المحل',
    typeDELIVERY: 'توصيل',
    typePICKUP: 'استلام من المحل',
    typeON_TABLE: 'على الطاولة',
    fulfilmentDelivery: 'توصيل',
    fulfilmentPickup: 'استلام من المحل',
    fulfilmentOnTable: 'على الطاولة',
    tableWord: 'طاولة',

    sampleCustomer: 'زبون تجريبي',
    sampleCashier: 'كاشير تجريبي',
    sampleOrderNote: 'دقّ الجرس مرتين',
    printedBy: 'طباعة: {name}',
    reprintDenied: 'لا تملك صلاحية طباعة نسخة أخرى. اطلب من مدير.',
  },

  printRoutingSection: {
    title: 'وجهات الطباعة',
    intro: 'أي طابعة تستقبل كل نوع من الأوراق.',
    receiptExplain: 'تُطبع فاتورة البيع الكاملة على هذه الطابعة.',
    destination: 'طابعة الوجهة',
    choose: 'اختر طابعة',
    notSet: 'لم تُحدَّد بعد طابعة لإيصال الزبون.',
    prepExplain: 'يتم تحديد طابعة التحضير لكل فئة من إعدادات الفئات.',
    manageCategories: 'إدارة الفئات',
  },

  printerManager: {
    title: 'الطابعات',
    intro: 'أضف طابعات المحل وحدّد طريقة اتصال كل طابعة ومكان استخدامها.',
    refreshPrinters: 'تحديث القائمة',
    noPrintersFound: 'لا توجد طابعات مثبتة في ويندوز على هذا الجهاز.',
    shopPcNotReported: 'لم يرسل كمبيوتر المحل قائمة طابعاته بعد. تأكد أن برنامج الطباعة يعمل على كمبيوتر الكاشير، ثم اضغط تحديث القائمة.',
    shopPcReported: 'الطابعات المثبتة على كمبيوتر المحل · آخر تحديث {time}',
    add: 'إضافة طابعة',
    edit: 'تعديل الطابعة',
    empty: 'لم تتم إضافة أي طابعة بعد.',
    name: 'اسم الطابعة',
    namePlaceholder: 'مطبخ المشاوي',
    nameHint: 'الاسم الذي تعرف به هذه الطابعة في المحل. يمكنك تغييره في أي وقت.',
    connection: 'نوع الاتصال',
    usb: 'USB / ويندوز',
    usbHint: 'موصولة بهذا الكمبيوتر ومثبتة في ويندوز.',
    network: 'شبكة / IP',
    networkHint: 'على شبكة المحل، بعنوان خاص بها.',
    windowsPrinter: 'اختر الطابعة المثبتة',
    choosePrinter: 'اختر طابعة مثبتة',
    windowsUnavailable: 'لا يمكن عرض طابعات ويندوز على هذا الجهاز.',
    address: 'عنوان الطابعة',
    port: 'المنفذ',
    receiptPrinter: 'الإيصالات',
    receiptSet: 'ستُطبع الإيصالات هنا.',
    off: 'متوقفة',
    switchOn: 'تشغيل',
    switchOff: 'إيقاف',
    switchedOn: 'تم تشغيل الطابعة.',
    switchedOff: 'تم إيقاف الطابعة.',
    usage: 'الاستخدام:',
    usageReceipt: 'إيصال الزبون',
    usagePrepOne: 'التحضير: فئة واحدة',
    usagePrepTwo: 'التحضير: فئتان',
    usagePrepFew: 'التحضير: {count} فئات',
    usagePrepMany: 'التحضير: {count} فئة',
    unused: 'غير مستخدمة حالياً',
    retire: 'إزالة',
    retired: 'لم تعد الطابعة قيد الاستخدام.',
    retireTitle: 'إيقاف استخدام هذه الطابعة؟',
    retireMessage:
      'تبقى على كل ما طبعته سابقًا. إذا كانت هناك فئات ترسل التحضير إليها، انقلها أولًا.',
    saved: 'تم حفظ الطابعة.',
    added: 'تمت إضافة الطابعة.',
    testConnection: 'اختبار الاتصال',
    testing: 'جارٍ الاختبار…',
    testConnected: 'تم الاتصال بنجاح.',
    testAvailable: 'متوفرة في ويندوز.',
    testFailed: 'فشل الاتصال.',
    testNotFound: 'لا توجد طابعة بهذا الاسم في ويندوز.',
    testNotConfigured: 'لم يتم إعداد هذه الطابعة بعد.',
    testDisabled: 'هذه الطابعة متوقفة.',
    testUnsupported: 'الطباعة عبر ويندوز متاحة على جهاز المحل فقط.',
    testAgentRunning: 'خدمة الطباعة في المحل تعمل. اطبع فاتورة للتأكد من الطابعة نفسها.',
    testAgentSilent: 'خدمة الطباعة في المحل غير مشغّلة، لذلك لا يمكن الطباعة.',
    preparationPrinter: 'طابعة التحضير',
    preparationPrinterHint: 'الطابعة التي تستقبل بطاقات تحضير هذه الفئة.',
    choosePreparation: 'اختر طابعة',
    preparationRequired: 'اختر الطابعة التي تُحضّر أصناف هذه الفئة.',
    notAssigned: 'طابعة التحضير غير محددة',
    printerUnavailable: 'الطابعة المحددة غير متاحة',
    printerUnavailableFix: 'الطابعة المحددة غير متاحة. اختر طابعة أخرى.',
    unavailableSuffix: 'غير متاحة',
    preparationGoesTo: 'التحضير: {name}',
    noReceiptPrinter: 'لم يتم اختيار طابعة إيصالات بعد',
  },

  printRouting: {
    label: 'وجهة الطباعة',
    hint: 'أين تُطبع منتجات هذه الفئة عند بيعها.',
    CASHIER_ONLY: 'الإيصال فقط',
    KITCHEN_AND_CASHIER: 'التحضير + الإيصال',
    cashierOnlyHint: 'الإيصال فقط. للمشروبات والسلع المعبأة.',
    kitchenHint: 'الإيصال وبطاقة تحضير للملحم.',
    cannotChange: 'المدير فقط يستطيع تغيير وجهة طباعة الفئة.',
    currentStatus: 'الحالة الحالية',
    active: 'مفعّل',
    unset: 'غير محدد',
    unsetHint: 'لا توجد وجهة طباعة لهذه الفئة بعد. اختر واحدة بالأسفل.',
    required: 'يرجى تحديد وجهة الطباعة لهذه الفئة.',
    receiptInfo: 'إيصال الزبون: يُطبع على {name} (إعداد عام للمحل)',
    receiptInfoUnset: 'إيصال الزبون: لم تُحدَّد طابعة بعد (إعداد عام للمحل)',
  },

  reservations: {
    title: 'المخزون المحجوز',
    subtitle: 'لحم موعود لطلبات لم تُدفع بعد.',
    empty: 'لا يوجد شيء محجوز.',
    emptyHint: 'يُحجز المخزون عند إرسال الطلب للتحضير، ويُحرَّر عند الدفع أو الإلغاء.',
    product: 'المنتج',
    quantity: 'المحجوز',
    order: 'الطلب',
    customer: 'العميل',
    since: 'منذ',
    onHand: 'المتوفر فعلياً',
    available: 'القابل للبيع',
    reservedShort: 'محجوز',
  },

  designer: {
    receiptTitle: 'مصمم الإيصال',
    receiptBody:
      'رتّب إيصال الزبون وشاهده يتغيّر أمامك. ما تراه هو الإيصال نفسه لا صورة عنه.',

    whichDesign: 'التصميم',
    designName: 'الاسم',
    duplicate: 'إنشاء نسخة',
    copied: 'تم النسخ. النسخة غير مُفعّلة حتى تفعّلها.',
    activeMark: 'قيد الاستخدام',
    thisIsActive: 'هذا هو التصميم الذي يطبع به الصندوق.',
    notActive: 'هذا التصميم غير مستخدم. الصندوق ما زال يطبع بالتصميم المفعّل.',
    makeActive: 'استخدم هذا التصميم',
    nowActive: 'سيطبع الصندوق بهذا التصميم من الآن.',
    saveFirst: 'احفظ تغييراتك أولاً.',

    livePreview: 'معاينة حيّة',
    zoomLabel: 'حجم المعاينة',
    zoomFit: 'ملائم',
    actualSize: 'معروض بـ {zoom}. عرض الورق {width} ملم مهما كان التكبير.',
    previewIsReal:
      'هذا هو الإيصال نفسه الذي يطبعه الصندوق، مملوءاً ببيع تجريبي. رتّبه هنا ويطابقه الورق.',
    printSample: 'اطبع هذه العيّنة',
    printA4: 'اطبع ورقة اختبار A4',
    printThermal: 'اطبع اختباراً حرارياً ({width} ملم)',
    printTestNote:
      'كلاهما يطبع ما تراه أعلاه تماماً، بما في ذلك التغييرات غير المحفوظة. ورقة A4 تضع الإيصال بعرضه الحقيقي {width} ملم على صفحة عادية، ولا تمدّده ليملأها. للاختبار الحراري، اختر مقاس الرول المطابق في نافذة الطابعة.',

    unsaved: 'تغييرات غير محفوظة على التصميم',
    saveVersion: 'احفظ كنسخة جديدة',
    savedVersion: 'تم الحفظ. الإيصالات المطبوعة سابقاً تحتفظ بتصميمها.',
    resetDefaults: 'العودة إلى التصميم القياسي',

    versionsTitle: 'النسخ',
    versionsBody:
      'كل حفظ يُبقي التصميم القديم. الإيصال المُعاد طبعه بعد سنة يخرج تماماً كما استلمه الزبون أول مرة.',
    versionN: 'النسخة {n}',
    inUse: 'قيد الطباعة',

    groupPaper: 'الورق والنص',
    groupHeader: 'أعلى الإيصال',
    groupIdentity: 'تعريف البيع',
    groupLines: 'الأصناف',
    groupMoney: 'المبالغ',
    groupFooter: 'أسفل الإيصال',
    groupPrinting: 'الطباعة',

    template: 'التصميم الأساسي',
    templateClassic: 'كلاسيكي',
    templateModern: 'حديث',
    templateCompact: 'مضغوط (يوفّر الورق)',
    templateBilingual: 'عربي وإنكليزي',
    paperWidth: 'عرض الورق',
    language: 'لغة الطباعة',
    bothLanguages: 'الاثنتان',
    fontSize: 'حجم الخط',
    sizeSmall: 'صغير',
    sizeMedium: 'متوسط',
    sizeLarge: 'كبير',
    density: 'التباعد',
    densityComfortable: 'مريح',
    densityCompact: 'ضيّق',
    divider: 'خطوط الفصل',
    dividerSolid: 'متصل',
    dividerDashed: 'متقطّع',
    dividerDotted: 'منقّط',
    dividerNone: 'بدون',
    alignLeft: 'يسار',
    alignCenter: 'وسط',
    alignRight: 'يمين',

    showLogo: 'طباعة الشعار',
    logoSize: 'حجم الشعار',
    logoAlignment: 'موضع الشعار',
    showNameEn: 'اسم المحل بالإنكليزية',
    showNameAr: 'اسم المحل بالعربية',
    showAddress: 'العنوان',
    showPhone: 'رقم الهاتف',
    headerText: 'سطر فوق البيع',
    headerTextHint: 'يُطبع تحت بيانات المحل. اتركه فارغاً لإخفائه.',

    showReceiptNumber: 'رقم الإيصال',
    showReceiptNumberHint:
      'به يجد الزبون هذا البيع لاحقاً. إخفاؤه يصعّب تتبّع أي استرجاع.',
    showDateTime: 'التاريخ والوقت',
    showCashierName: 'من خدم الزبون',
    showCustomerName: 'اسم الزبون إن وُجد',
    showShiftNumber: 'رقم الوردية',
    showOrderNumber: 'رقم طلب التوصيل',

    showWeight: 'الوزن',
    showQuantity: 'الكمية',
    showUnitPrice: 'سعر الكيلو أو القطعة',
    showSku: 'رمز الصنف',
    showItemNotes: 'ملاحظات على السطر',
    showItemNotesHint: 'مثل «مكعّبات».',

    showSubtotal: 'المجموع الفرعي',
    showDiscount: 'الخصم',
    showDeliveryFee: 'رسوم التوصيل',
    showTotalUsd: 'الإجمالي بالدولار',
    noTotalWarning: 'لن يُظهر الإيصال ما دفعه الزبون.',
    showTotalLbp: 'الإجمالي بالليرة',
    showExchangeRate: 'سعر الصرف المستعمل',
    showExchangeRateHint:
      'يتيح للزبون التحقق من الحساب، ولا يجعل تغيّر السعر لاحقاً يُظهر الإيصال خاطئاً.',
    showPaymentMethods: 'طريقة الدفع',
    showChange: 'الباقي',

    thankYouText: 'سطر الشكر',
    footerText: 'التذييل',
    footerTextHint: 'أوقات الدوام، رقم هاتف، ملاحظة عن الاسترجاع.',
    footerAlignment: 'موضع التذييل',
    showReceiptBarcode: 'رقم الإيصال في الأسفل',

    copies: 'عدد النسخ',
    copiesHint: 'كم مرة يُطلب من نافذة الطباعة أن تطبع.',
    autoPrint: 'فتح نافذة الطباعة تلقائياً',
    autoPrintHint:
      'لا تستطيع صفحة الويب الطباعة بلا نافذة. هذا يفتحها لك ولا يتخطاها.',
  },

  kitchenDesigner: {
    title: 'مصمم ورقة التحضير',
    body: 'رتّب الورقة التي يعمل منها اللحّام. لا تحمل أي مبلغ، وهذا مقصود.',

    noMoneyPromise:
      'ورقة التحضير لا تُظهر أبداً سعراً أو إجمالياً أو عملة. لا يوجد مفتاح لذلك في هذه الشاشة لأنّ الحقل نفسه غير موجود على الورقة: إنّها تعليمة تقطيع، ووجود سعر عليها يغري بتسليمها للزبون كفاتورة.',

    thisIsActive: 'هذا هو التصميم الذي تُطبع به ورقات التحضير.',
    notActive: 'هذا التصميم غير مستخدم. ما زالت الورقات تُطبع بالتصميم المفعّل.',
    nowActive: 'ستُطبع ورقات التحضير بهذا التصميم من الآن.',
    savedVersion: 'تم الحفظ. الورقات المطبوعة سابقاً تحتفظ بتصميمها.',
    previewIsReal:
      'هذه هي الورقة نفسها التي تنتجها الطابعة، مملوءة بطلب تجريبي. رتّبها هنا ويطابقها الورق.',
    versionsBody:
      'كل حفظ يُبقي التصميم القديم، فالورقة المُعاد طبعها لطلب متنازع عليه تُظهر ما استلمه اللحّام فعلاً.',

    templateLarge: 'كبير، للطاولة',

    groupReadability: 'قراءتها من بعيد',
    groupWho: 'صاحب الطلب',
    groupWhatToCut: 'ما يجب تقطيعه',
    groupShouted: 'ما يجب إبرازه',

    productNameSize: 'حجم اسم الصنف',
    boldProductName: 'اسم الصنف عريض',
    boldQuantity: 'الكمية عريضة',
    boldQuantityHint: 'الرقم الذي يُتحقق منه مرتين قبل التقطيع.',

    showOrderNumber: 'رقم الطلب',
    showSaleNumber: 'رقم البيع',
    showOrderType: 'على الطاولة أو توصيل أو استلام',
    showCustomerName: 'اسم الزبون',
    showRequestedTime: 'موعد الاستلام',
    showRequestedTimeHint: 'الفرق بين تحضيره الآن وتحضيره عند الرابعة.',
    showCashierName: 'من استلم الطلب',

    showWeightOrQuantity: 'الوزن أو الكمية',
    noQuantityWarning: 'لن يعرف اللحّام كم يقطّع.',
    showCuttingInstructions: 'تعليمات التقطيع',
    showCuttingInstructionsHint: 'مثل «مكعّبات ۳ سم، مع تصفية الدهن».',
    showOrderNotes: 'ملاحظات على الطلب ككل',
    showCategory: 'القسم الذي ينتمي إليه',

    showDeliveryHeading: 'إظهار نوع الطلب',
    showDeliveryHeadingHint: 'يعرض نوع الطلب بشكل بارز: توصيل، استلام من المحل، أو على الطاولة.',
    showUrgentHeading: 'عنوان «عاجل» كبير',
    handwritingSpace: 'أسطر فارغة للكتابة',
    handwritingSpaceHint:
      'مساحة ليكتب اللحّام الوزن النهائي. صفر لإلغائها.',

    copiesHint: 'واحدة للطاولة وواحدة للكيس، إن كان هذا أسلوب المحل.',
  },

  printers: {
    limitTitle: 'لا تستطيع صفحة الويب اختيار الطابعة عنك.',
    limitBody:
      'مهما ضُبط هنا، تفتح الطباعة نافذة المتصفح ويختار شخص. هكذا تعمل كل المتصفحات عن قصد. الإعدادات أدناه تحدد أي طابعة تُختار وما الورق فيها، ولا ترسل شيئاً بنفسها.',

    roleReceipt: 'إيصالات الكاشير',
    roleKitchen: 'ورقات التحضير',
    roleLabel: 'ملصقات المنتجات',

    name: 'الاسم الذي تعرفها به',
    connection: 'طريقة الوصول إليها',
    connectionBrowser: 'نافذة طباعة المتصفح',
    connectionConnector: 'برنامج صغير على هذا الجهاز',
    connectionKiosk: 'متصفح يعمل بوضع الكشك',

    printerName: 'اسم الطابعة',
    printerNameNote: 'الطابعة التي تُختار في النافذة (ملاحظة لا توجيه)',
    printerNamePlaceholder: 'مثل EPSON TM-T20',
    paperWidth: 'الورق',
    copies: 'النسخ',

    explainBrowser:
      'تعمل اليوم بلا تثبيت شيء. تفتح النافذة ويضغط شخص طباعة. الاسم أعلاه تذكير بأيّها يختار لا أكثر.',
    explainConnector:
      'هذا النظام لا يتضمّن ذلك البرنامج. اختيار هذا يسجّل ما تنوي إعداده فقط؛ لا يُثبّت شيء ولا يتغيّر شيء حتى تُبلّغ طباعة حقيقية.',
    explainKiosk:
      'يحتاج إلى تشغيل المتصفح مع موافقة مسبقة على الطباعة، وهذا تغيير في طريقة تشغيل الصندوق لا إعداد هنا. إلى أن يتمّ ذلك، تبقى النافذة تظهر.',

    statusUntested: 'لم تُجرّب',
    statusWorked: 'طبعت',
    statusFailed: 'لم تطبع',

    test: 'اطبع صفحة اختبار',
    saveFirst: 'احفظ هذه الطابعة أولاً.',
    didItPrint: 'هل خرجت صفحة الاختبار من الطابعة؟',
    testPassed: 'سُجّل: هذه الطابعة تعمل.',
    testRecorded: 'سُجّل: لم تخرج الصفحة.',
    reportedNotPrinted: 'أفاد من أجرى الاختبار أنّ شيئاً لم يخرج من الطابعة.',
    lastSuccess: 'آخر طباعة ناجحة في {when}.',
    lastFailure: 'آخر إخفاق في {when}: {note}',

    saved: 'حُفظت الطابعة.',
  },

  users: {
    intro:
      'كل من يسجّل دخولاً، وما يحقّ لكل واحد فعله. الأدوار تغطّي الحالات المعتادة؛ والصلاحية الفردية استثناء من الدور وتحتاج إلى سبب.',
    listLabel: 'الموظّفون',
    addPerson: 'إضافة شخص',
    createAccount: 'إنشاء الحساب',
    created: 'أُنشئ الحساب. أخبره باسم المستخدم وكلمة السر.',

    fullName: 'الاسم الكامل',
    username: 'اسم المستخدم',
    usernameHint: 'حروف إنكليزية صغيرة وأرقام ونقطة أو شرطة. هذا ما يكتبه ليدخل.',
    firstPassword: 'كلمة السر الأولى',
    firstPasswordHint:
      'أنت من يختارها، فأخبره بها. يستطيع تغييرها بنفسه لاحقاً.',

    role: 'الدور',
    roleChanged: 'تغيّر الدور.',
    status: 'الحساب',
    statusActive: 'يستطيع الدخول',
    statusSuspended: 'لا يستطيع الدخول',
    statusChanged: 'تم تحديث الحساب.',
    suspendedMark: 'موقوف',

    deleteUser: 'حذف المستخدم',
    deleteTitle: 'حذف المستخدم؟',
    deleteMessage:
      'هل أنت متأكد أنك تريد حذف هذا المستخدم نهائيًا؟',
    deleteConfirm: 'نعم، احذف',
    deleteDone: 'تم حذف الحساب.',
    deleteHasHistory:
      'هذا الحساب جزء من سجلات المحل ولا يمكن حذفه. علّق الحساب بدلاً من ذلك - لن يتمكن من تسجيل الدخول، ويبقى كل ما قام به منسوباً إليه.',
    deleteLastOwner:
      'هذا آخر مالك نشط. لا يمكن حذف هذا الحساب لأن المحل سيبقى بلا مالك.',

    noDeleteBody:
      'لا تُحذف الحسابات أبداً. من ترك العمل يُوقف بدل ذلك: لا يستطيع الدخول، وتبقى كل مبيعاته ووردياته وموافقاته منسوبة إليه. حذف الحساب يأخذ ذلك التاريخ معه.',

    cannotChangeSelf:
      'هذا حسابك أنت. لا يمكنك تغيير دورك أو صلاحياتك — راجع مالكاً آخر. ما عدا ذلك يمكنك تعديله.',

    lastSignedIn: 'آخر دخول {when}.',
    neverSignedIn: 'لم يسجّل دخولاً قط.',

    signInTitle: 'تسجيل الدخول',
    newPassword: 'كلمة سر جديدة',
    setPassword: 'تعيين كلمة السر',
    passwordChanged: 'تغيّرت كلمة السر. أخبره بها.',
    passwordNote:
      'لا يوجد بريد إلكتروني في هذا النظام، فلا يوجد رابط استعادة. أنت تضبط كلمة السر وتخبر الشخص.',
    pinSet: 'رمز المدير (مضبوط)',
    pinNotSet: 'رمز المدير (غير مضبوط)',
    setPinButton: 'تعيين الرمز',
    removePin: 'إزالة الرمز',
    pinChanged: 'ضُبط الرمز.',
    pinRemoved: 'أُزيل الرمز.',
    pinNote:
      'يوافق الرمز على خصم أو إلغاء عند الصندوق بلا تسجيل خروج. لا تعطه إلا لمن ينبغي أن يوافق عليها.',

    exceptionsTitle: 'صلاحيات فردية',
    exceptionsBody:
      'هذه استثناءات من دور {role}. استعملها بقلّة: دور للجميع استثناءات عليه لم يعد يصف شيئاً.',
    noExceptions: 'لا استثناءات. يفعل هذا الشخص ما يسمح به دور {role} تماماً.',
    exceptionCount: '{count} استثناء',
    addException: 'إضافة استثناء',
    whichPermission: 'أي صلاحية',
    pickPermission: 'اختر واحدة',
    alreadyInRole: 'موجودة في الدور',
    whatKind: 'إضافة أم سحب',
    effectAllow: 'مضافة',
    effectDeny: 'مسحوبة',
    effectAllowLong: 'اسمح له بهذا وإن لم يسمح الدور',
    effectDenyLong: 'امنعه من هذا وإن سمح الدور',
    reason: 'السبب',
    reasonPlaceholder: 'مثلاً: يغطّي المحل وحده أيام الأحد',
    reasonWhy:
      'سيسأل أحدهم عن هذا بعد سنة. اكتب ما تودّ قراءته حينها.',
    exceptionSaved: 'حُفظ الاستثناء.',
    exceptionRemoved: 'عاد إلى ما يسمح به الدور.',
    backToRole: 'عودة إلى الدور',
    denyWins:
      'إذا كانت الصلاحية مسموحة في الدور ومسحوبة هنا، تبقى مسحوبة. السحب يغلب دائماً.',
  },

  backupTargets: {
    intro:
      'نسخة كاملة عن بيانات المحل تُؤخذ كل 15 دقيقة. اختيار مجلّد لا يثبت أنّه يعمل، فاختبره.',
    add: 'إضافة وجهة',
    noneYet:
      'لا توجد وجهة محددة. لا مكان للنسخ الاحتياطية، وستكتشف ذلك يوم تحتاج إلى واحدة.',

    name: 'اسم الوجهة',
    kind: 'وجهة النسخ الاحتياطي',
    kindLocal: 'هذا الكمبيوتر',
    kindSynced: 'مزامنة OneDrive',

    explainLocal:
      'مجلّد على هذا الكمبيوتر أو على قرص موصول به. يفيد ضدّ الخطأ، ولا يفيد ضدّ الحريق — فهو في الغرفة نفسها.',
    explainSynced:
      'مجلّد داخل OneDrive الذي يزامنه Windows أصلاً مع السحابة. يكتب النظام الملف، ويرفعه تطبيق OneDrive. لهذا لا يحتاج إلى حساب مايكروسوفت ولا كلمة سر ولا أي إعداد سوى تسجيل الدخول إلى OneDrive.',

    folder: 'المجلّد',
    folderPlaceholder: 'D:\\Yaghi Backups',
    folderHint: 'ابدأ من حرف القرص. يُنشأ المجلّد إن لم يكن موجوداً.',

    oneDriveDetected: 'تم اكتشاف OneDrive',
    oneDriveChooseRoot: 'أي OneDrive؟',
    oneDriveMissing:
      'OneDrive غير مُعدّ على هذا الكمبيوتر. سجّل الدخول إلى تطبيق OneDrive في Windows ثم أعد المحاولة.',
    oneDriveUseSuggested: 'استخدم {folder}',
    oneDriveOutside:
      'هذا المجلّد ليس داخل OneDrive، فلن يُرفع أي شيء يُكتب فيه.',
    oneDriveSyncNote:
      'يرفع OneDrive في Windows هذه الملفات عندما يكون متصلاً. النظام يكتبها ولا يتخاطب مع مايكروسوفت، لذلك لا يمكنه معرفة متى انتهى الرفع — ولا يدّعي ذلك.',
    oneDriveDeletionNote:
      'عند إزالة نسخة قديمة هنا، قد يزيلها OneDrive من السحابة أيضاً. هكذا يعمل المجلّد المتزامن.',

    automatic: 'النسخ الاحتياطي التلقائي',
    frequency: 'التكرار',
    everyFifteen: 'كل 15 دقيقة',
    keep: 'النسخ المحفوظة',
    keepLatestFour: 'آخر 4 نسخ احتياطية ناجحة',
    keepHint:
      'لا تُحذف النسخة القديمة إلا بعد كتابة نسخة جديدة والتحقق منها. النسخة الفاشلة لا تحذف شيئاً.',
    makeActive: 'اكتب النسخ هنا من الآن',

    inUse: 'قيد الاستخدام',
    useThis: 'استخدم هذه',
    nowActive: 'ستُكتب النسخ هنا.',
    saved: 'حُفظت الوجهة.',
    removed: 'أُزيلت. النسخ المكتوبة سابقاً لم تُمس.',

    check: 'اختبار الوجهة',
    checkRecorded: 'تمّ الاختبار. النتيجة مسجّلة في الحالتين.',
    checkReadyOneDrive:
      'مجلد النسخ الاحتياطي جاهز. سيقوم OneDrive بمزامنة ملفات النسخ الاحتياطي عندما يكون تطبيق OneDrive في Windows متصلًا.',
    statusUnchecked: 'لم تُختبر',
    statusOk: 'جاهزة',
    statusFailed: 'لا يمكن الكتابة فيها',
    lastCheck: 'اختُبرت {when}: {message}',

    summary: 'كل 15 دقيقة. تحتفظ بآخر 4 نسخ احتياطية ناجحة.',
    sameDiskWarning:
      'هذا على الجهاز نفسه الذي فيه قاعدة البيانات. إن تعطّل القرص تعطّلت النسخة معه.',

    noRestoreHere:
      'لا يوجد زر استعادة هنا، عن قصد. الاستعادة تستبدل كل ما سجّله المحل منذ أخذ النسخة، لذلك يقوم بها شخص أمام الجهاز وفق الإجراء المكتوب — لا زر يُضغط بالخطأ.',
  },

shiftReport: {
    title: 'تقرير إغلاق الوردية',
    settingsTitle: 'تقرير إغلاق الوردية',
    settingsBody: 'ما يُطبع عند إغلاق الوردية، وهل يُطبع تلقائيًا.',

    shift: 'الوردية',
    cashier: 'الكاشير',
    opened: 'وقت الفتح',
    closed: 'وقت الإغلاق',
    duration: 'المدة',

    salesSummary: 'ملخص المبيعات',
    grossSales: 'إجمالي المبيعات',
    discounts: 'الحسومات',
    netSales: 'صافي المبيعات',
    saleCount: 'عدد المبيعات',
    averageSale: 'متوسط البيع',

    payments: 'طرق الدفع',
    cash: 'نقدي',
    credit: 'على الحساب',
    transactions: 'العمليات',
    totalPayments: 'إجمالي المدفوعات',

    cashDrawer: 'صندوق النقد',
    openingCash: 'رصيد الفتح',
    expectedCash: 'الرصيد المتوقع',
    countedCash: 'الرصيد الفعلي',
    difference: 'الفرق',
    result: 'الحالة',
    resultExact: 'مطابق',
    resultOver: 'زيادة',
    resultShort: 'نقص',

    voids: 'مبيعات ملغاة',
    expenses: 'المصروفات',
    expenseCount: 'عدد المصروفات',
    closingNote: 'ملاحظة الإغلاق',
    deliveryHeading: 'التوصيل',
    deliveriesDispatched: 'عدد طلبات التوصيل التي خرجت اليوم',
    deliveriesCollected: 'تم استلام حسابها',
    deliveriesPending: 'بانتظار الحساب',
    deliveryFees: 'إجمالي رسوم التوصيل',

    closedBy: 'أغلقها',
    printedAt: 'طُبع',
    reprintMark: 'نسخة',

    viewReport: 'عرض تقرير الوردية',
    reprintReport: 'إعادة طباعة تقرير الوردية',
    reportForShift: 'تقرير الوردية',
    livePreviewNote: 'الوردية ما تزال مفتوحة — هذه الأرقام معاينة حية وليست التقرير النهائي.',
    frozenNote: 'جُمّدت هذه الأرقام عند إغلاق الوردية ولا تتغير أبدًا.',

    autoPrint: 'طباعة تقرير الوردية تلقائيًا عند الإغلاق',
    copies: 'عدد النسخ',
    copiesHint: 'من واحدة إلى ثلاث. التقرير للصندوق وربما للمكتب.',
    sectionHeader: 'الترويسة',
    sectionSales: 'المبيعات',
    sectionPayments: 'طرق الدفع',
    sectionDrawer: 'صندوق النقد',
    sectionOptional: 'أقسام اختيارية',
    sectionFooter: 'التذييل',
    footerTextLabel: 'نص التذييل',

    printQueued: 'أُرسل تقرير الوردية إلى طابعة الإيصالات.',
    printFailed: 'تم إغلاق الوردية بنجاح، ولكن تعذرت طباعة التقرير وسيتم الاحتفاظ به لإعادة المحاولة.',
    reprintQueued: 'أُرسلت نسخة التقرير إلى طابعة الإيصالات.',
    reprintFailed: 'تعذرت طباعة نسخة التقرير. النسخة محفوظة ويمكن إعادة المحاولة.',
  },


  networkPrint: {
    toCashier: 'اطبع هذه المسودة على طابعة الإيصالات',
    toKitchen: 'اطبع هذه المسودة على إحدى طابعات التحضير',
    testPrinterLabel: 'طابعة التجربة',
    chooseTestPrinter: 'اختر طابعة لطباعة التجربة',
    preparing: 'جارٍ تجهيز المستند…',
    queued: 'في انتظار الطابعة.',
    sending: 'جارٍ الإرسال إلى الطابعة…',
    sent: 'أُرسل إلى الطابعة.',
    sentMeaning: 'وصلت البيانات إلى الطابعة. تأكّد من خروج الورق.',
    failed: 'فشل الإرسال.',
  },

  heldSheet: {
    banner: 'طلب محفوظ',
    notPaid: 'غير مدفوع',
    reference: 'رقم الحفظ',
    printed: 'وقت الطباعة',
    cashier: 'الكاشير',
    customer: 'الزبون',
    subtotal: 'المجموع الفرعي',
    deliveryFee: 'رسوم التوصيل',
    amountDue: 'المبلغ المطلوب',
    notes: 'ملاحظات',
    notASale: 'هذا ليس بيعاً وليس فاتورة.',
    stockNotDeducted:
      'لم يُدفع أي مبلغ ولم يُخصم أي مخزون.',

    print: 'طباعة الطلب المحفوظ',
    printing: 'جارٍ الإرسال',
    sent: 'أُرسل الطلب المحفوظ إلى الطابعة.',
    failed: 'لم يُرسل الطلب المحفوظ إلى الطابعة.',
    retry: 'أعد المحاولة',
    noPrinter: 'لم تُحدَّد بعد طابعة الإيصالات.',
    unchanged:
      'لم يتغير الطلب المحفوظ، وما زال بانتظاره.',
  },

  comingSoon: {
    badge: 'قريبًا',
    dataSafe: 'كل ما تم تسجيله محفوظ وسيبقى كما هو.',
    backToPos: 'العودة إلى نقطة البيع',
    backToDashboard: 'العودة إلى لوحة التحكم',

    production: 'ستتوفر إدارة الإنتاج في تحديث قادم.',
    recipes: 'ستتوفر إدارة الوصفات في تحديث قادم.',
    labels: 'ستتوفر طباعة ملصقات المنتجات في تحديث قادم.',
    customers: 'شاشة الزبائن متوقفة مؤقتاً. سجلات الزبائن والحسابات والتاريخ كلها محفوظة، وما تزال نقطة البيع تختار زبوناً للبيع على الحساب.',
  },

  connectivity: {
    title: 'الاتصال',
    openDetails: 'تفاصيل الاتصال',
    serverFine: 'الصندوق متّصل بخادم المحل.',

    syncing: 'جارٍ المزامنة',
    attention: 'يحتاج متابعة',

    backend: 'خادم المحل',
    reachable: 'متاح',
    unreachable: 'غير متاح',
    checking: 'جارٍ الفحص',

    device: 'هذا الجهاز',
    hasNetwork: 'الشبكة متصلة',
    noNetwork: 'لا توجد شبكة',

    printerConnected: 'متصلة',
    printerDisconnected: 'غير متصلة',
    printerNotSetUp: 'غير مضبوطة',

    pending: 'بانتظار المزامنة',
    failed: 'مرفوضة',
    lastSync: 'آخر مزامنة',
    never: 'لم تحدث بعد',
  },

  salePrint: {
    cashierLabel: 'إيصال الزبون',
    kitchenLabel: 'ورقة التحضير',

    preparing: 'قيد التجهيز',
    sending: 'جارٍ الإرسال إلى الطابعة',
    queuedForRetry: 'في انتظار إعادة المحاولة',
    onItsWay: 'أُرسلت إلى طابعة المحل - ستخرج بعد لحظات.',
    sent: 'أُرسل إلى الطابعة',

    cashierFailed:
      'لم يُرسل إلى طابعة الإيصالات. البيع مكتمل ومسجّل.',
    kitchenFailed:
      'لم تُرسل. لا أحد عند الطاولة يعلم بهذا الطلب — أخبرهم أو أعد المحاولة.',

    retry: 'أعد المحاولة',
    retryStarted: 'بدأت إعادة المحاولة.',
    retryFailed: 'فشلت إعادة المحاولة. ما زال الوصول إلى الطابعة متعذّراً.',

    browserFallback:
      'يمكنك طباعة الإيصال من المتصفح عبر الزر الموجود على الإيصال نفسه.',

    notConfiguredCashier: 'لم تُحدَّد بعد طابعة الإيصالات.',

    cashierSent: 'تم إرسال فاتورة الزبون إلى الطابعة.',
    kitchenSent: 'أُرسل طلب التحضير.',
    kitchenSentAt: 'أُرسل طلب التحضير إلى {name}.',
    cashierQueued: 'تم وضع فاتورة الزبون في قائمة إعادة المحاولة.',
    agentSilentToast: 'لن تتم الطباعة: خدمة الطباعة في المحل غير مشغّلة.',
    agentSilentDetail: 'المستند محفوظ وسيُطبع فور تشغيل الخدمة.',
    kitchenQueued: 'تم وضع طلب التحضير في قائمة إعادة المحاولة.',
    kitchenQueuedAt: 'تم وضع طلب التحضير إلى {name} في قائمة إعادة المحاولة.',
    cashierFailedToast: 'تعذّر إرسال فاتورة الزبون إلى الطابعة.',
    kitchenFailedToast: 'تعذّر إرسال طلب التحضير.',
    kitchenFailedAt: 'تعذّر إرسال طلب التحضير إلى {name}.',
    uncertainToast:
      'نتيجة الطباعة غير مؤكدة. تحقّق من الطابعة قبل إعادة الطباعة.',

    sendAgain: 'أرسل إلى الطابعة',
    sendingAgain: 'جارٍ إرسال نسخة أخرى',
    sentAgain: 'أُرسلت نسخة أخرى إلى الطابعة.',
    sendAgainFailed: 'لم تُرسل النسخة. تعذّر الوصول إلى الطابعة.',
  },

  settings: {
    title: 'الإعدادات',
    subtitle: 'كيف يعمل هذا المحل. كل خيار هنا يقرأه شيء فعلي.',
    noPermission: 'الإعدادات متاحة للمالك أو المدير فقط. اطلب منهما إجراء أي تغيير.',

    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saved: 'تم الحفظ.',
    revert: 'تراجع عن التغييرات',
    unsavedMark: 'غير محفوظ',
    unsavedCount: '{count} غير محفوظ',
    unsavedBanner: 'لديك {count} تغيير غير محفوظ. لا يُطبَّق حتى تحفظ القسم الموجود فيه.',
    mustBeNumber: 'يجب أن يكون رقماً.',
    tooLow: 'لا يمكن أن يقل عن {min}.',
    tooHigh: 'لا يمكن أن يزيد عن {max}.',
    tooLong: 'طويل جداً. الحد الأقصى {max} حرفاً.',
    range: 'من {min} إلى {max}',
    updatedNote: 'تُعرض هذه الصفحة بـ {locale}. الإعدادات تخص المحل لا شخصاً بعينه.',

    navLabel: 'أقسام الإعدادات',
    saveChanges: 'حفظ التغييرات',
    lastSaved: 'آخر تعديل {when}',
    savedBy: 'بواسطة {name}',
    destinationsTitle: 'مكان كتابة النسخ الاحتياطية',
    approvalsTitle: 'ما يحتاج إلى موافقة',
    approvalsBody:
      'الحدود التي يعمل ضمنها الكاشير، ومتى يجب أن يوافق المدير. هذه لك أن تضبطها.',
    signInPolicyTitle: 'تسجيل الدخول',
    signInPolicyBody:
      'طول كلمة السر والرمز والإيقاف ومدة الجلسة. للقراءة فقط هنا: مكانها backend/.env وتُقرأ مرّة عند إقلاع الخادم.',
    ownerOnlyMark: 'للمالك فقط',
    ownerOnlyNote: 'لا يستطيع تغييره إلا المالك. راجعه إن كان يحتاج تعديلاً.',
    noDefaultCategory: 'كل المنتجات',
    categoryGone:
      'الفئة المحددة لم تعد موجودة. اختر غيرها أو اتركها على كل المنتجات.',
    usersTitle: 'الموظّفون والصلاحيات',
    usersBody: 'من يستطيع الدخول، وما يسمح به دوره، وأي استثناءات عليه.',
    designerReceiptTitle: 'مصمم الإيصال',
    designerKitchenTitle: 'مصمم ورقة التحضير',
    designerKitchenBody:
      'رتّب الورقة التي يعمل منها اللحّام. لا تحمل أي مبلغ، وهذا مقصود.',
    designerReceiptBody:
      'رتّب إيصال الزبون وشاهده يتغيّر أمامك.',
    searchPlaceholder: 'ابحث في الإعدادات',
    searchClear: 'مسح البحث',
    searchNothing: 'لا يوجد ما يطابق «{query}».',

    scaleComingSoonTitle: 'قريباً',
    scaleNeedsHardwareTitle: 'ما ينتظره',
    scaleNeedsHardwareBody:
      'ميزان مركّب على هذه الطاولة ومجرّب عليها. القارئ مكتوب والإعدادات جاهزة؛ ولا يُفعّل أي منهما قبل أن يمرّ عتاد حقيقي عليه، لأن وزناً لم يتحقق منه أحد أسوأ من لا وزن إطلاقاً.',
    scaleComingSoonBody:
      'قراءة الوزن من الميزان مباشرة إلى السلة ليست قيد الخدمة في هذا المحل بعد. تحتاج إلى تركيب الميزان على الطاولة وتجربته، وهذا لم يحصل بعد.',
    scaleComingSoonWhat:
      'ما يعمل فعلاً: الإعدادات أدناه تُحفظ وتُقرأ، واختبار الاتصال في الأسفل يفتح منفذاً تسلسلياً حقيقياً ويعرض ما يرسله الميزان. غير المكتمل هو ربط الميزان بالصندوق كي لا يكتب أحد الوزن يدوياً.',
    scaleTryTitle: 'جرّب الاتصال بميزان الآن',
    scaleTryBody:
      'اختبار حقيقي لا عرض توضيحي. يحتاج إلى كروم أو إيدج وميزان على منفذ تسلسلي أو USB تسلسلي.',

    businessTitle: 'بيانات المحل',
    businessBody: 'الاسم والعنوان والعبارات المطبوعة على كل إيصال.',
    logoTitle: 'شعار المحل',
    logoBody: 'يُطبع على الإيصالات وبطاقات التحضير ويظهر في القائمة. استبداله تغيير ملف لا إعداد.',

    currencyTitle: 'العملة وسعر الصرف',
    currencyBody: 'أي عملة تظهر أولاً، وكم يساوي الدولار اليوم.',
    rateTitle: 'سعر صرف الدولار مقابل الليرة',
    rateBody: 'يُستخدم لكل مبلغ بالليرة منذ لحظة الحفظ.',
    rateCurrent: 'الآن',
    ratePerUsd: 'ليرة لكل دولار',
    rateNew: 'السعر الجديد',
    rateSave: 'تثبيت السعر',
    rateSaved: 'تم تحديث سعر الصرف.',
    rateFrozenNote: 'الفواتير المسجّلة تحتفظ بالسعر الذي بيعت به. هذا التغيير لا يعيد كتابتها.',
    rateNoPermission: 'لا يمكنك تغيير السعر. اطلب من المالك أو المدير.',
    rateHistory: 'الأسعار الأخيرة',
    rateCurrentBadge: 'الحالي',

    posTitle: 'نقطة البيع',
    posBody: 'كيف تتصرف الشاشة قبل أن يلمسها أحد.',
    categoryHint: 'تُنشأ الفئات من شاشة المنتجات.',
    categoryLink: 'إدارة الفئات',

    printingTitle: 'الطباعة',
    printingBody: 'مقاسات الورق، وما يستطيع المتصفح فعله بصدق مع الطابعة.',
    printRealityTitle: 'ما يستطيع المتصفح وما لا يستطيع',
    printRealityBody:
      'يهيّئ هذا النظام الإيصال لمقاس ورقك ويفتح نافذة الطباعة. لا يستطيع اختيار الطابعة عنك ولا الطباعة بصمت في الخلفية؛ لا صفحة ويب تستطيع ذلك. للحصول على بطاقات تخرج في الخلف دون ضغط أحد، تحتاج موصل طباعة محلياً على هذا الجهاز أو تشغيل المتصفح بوضع الكشك. أسماء الطابعات أدناه ملاحظات تذكّر من يقف على الصندوق بأي خيار يختار.',
    printTestTitle: 'طباعة تجريبية',
    printTestBody:
      'كل زر يفتح التخطيط الحقيقي بالمقاس المحدد أعلاه، لتتحقق من الورق قبل أن ينتظر زبون.',
    printTestReceipt: 'تجربة إيصال الكاشير',
    printTestTicket: 'تجربة بطاقة المطبخ',
    printTestLabel: 'تجربة ملصق منتج',
    printAgain: 'طباعة',
    testReceiptTitle: 'إيصال تجريبي',
    testItem: 'سطر تجريبي',
    testSample: 'طباعة تجريبية، ليست عملية بيع',
    testLabelProduct: 'منتج تجريبي',

    scaleTitle: 'الميزان',
    scaleBody: 'وسيلة راحة اختيارية. لوحة المفاتيح هي الضمانة.',
    scaleManualTitle: 'إدخال الوزن يدوياً يعمل دائماً',
    scaleManualBody:
      'الإدخال اليدوي ليس إعداداً ولا يمكن إيقافه. مهما حدث للميزان أو المتصفح أو الكابل، يستطيع الكاشير قراءة الوزن من الشاشة وإدخاله.',
    scaleStatus: 'الاتصال',
    scaleConnected: 'متصل',
    scaleConnecting: 'جارٍ الاتصال…',
    scaleDisconnected: 'غير متصل',
    scaleError: 'فشل الاتصال',
    scaleUnsupported: 'هذا المتصفح لا يستطيع فتح منفذ تسلسلي',
    scaleConnect: 'توصيل ميزان',
    scaleDisconnect: 'قطع الاتصال',
    scaleReading: 'القراءة الحية',
    scaleStable: 'مستقر',
    scaleReadingHint: 'إذا ظهر وزن مطابق لشاشة الميزان فالاتصال يعمل.',
    scaleHonesty:
      'يفهم القارئ سطر نص ينتهي بـ kg أو g، وهو ما ترسله معظم الموازين التسلسلية. لم نختبر مجموعة من الطرازات ولا ندّعي أن طرازاً بعينه يعمل؛ وصّل ميزانك وراقب القراءة أعلاه. ما لا يستطيع قراءته يظهر بلا قراءة، لا بوزن خاطئ.',

    inventoryTitle: 'قواعد المخزون',
    inventoryBody: 'التنبيهات، وحجم التصحيح الذي يحتاج نظرة ثانية.',
    reservationRule:
      'المخزون الموعود لطلب توصيل غير مدفوع محجوز لا مخصوم: المتاح هو الموجود ناقص المحجوز. السلة المعلّقة لا تحجز شيئاً. هكذا تعمل ميزة التوصيل وهي غير قابلة للتعديل.',

    securityTitle: 'الأمان والموافقات',
    securityBody: 'ما يحتاج موافقة مدير، وسياسة الدخول التي يعمل بها الخادم.',
    policyTitle: 'سياسة تسجيل الدخول',
    policyPassword: 'أقصر كلمة مرور',
    policyPin: 'طول الرمز',
    policyLockout: 'الإيقاف المؤقت',
    policyLockoutValue: '{attempts} محاولات فاشلة ثم {seconds} ثانية',
    policySession: 'مدة الجلسة',
    policyCharacters: 'حرفاً',
    policyDigits: 'أرقام',
    policyMinutes: 'دقيقة',
    policyWhere:
      'تُقرأ مرة واحدة عند تشغيل الخادم من {file}. تُعرض هنا لتعرف السياسة؛ تغييرها يعني تعديل ذلك الملف وإعادة التشغيل.',
    selfApprovalRule: 'لا يستطيع المدير الموافقة على طلبه هو. الموافقة تطلب رمزاً وترفض صاحب الطلب.',

    shiftReportDesignerTitle: 'تقرير إغلاق الوردية',
    shiftReportDesignerBody: 'ما يُطبع عند إغلاق الوردية، وهل يُطبع تلقائيًا.',
    backupTitle: 'النسخ الاحتياطي والنظام',

    installTitle: 'تثبيت نظام ملحمة ياغي على هذا الجهاز',
    installBody:
      'التثبيت يضع شعار ملحمة ياغي على سطح المكتب وقائمة ابدأ، ويفتح النظام في نافذة مستقلة بدون تبويبات المتصفح.',
    installButton: 'تثبيت التطبيق',
    installAlready: 'أنت تستخدم التطبيق المثبّت بالفعل.',
    installManually:
      'للتثبيت: افتح قائمة المتصفح (⋮ أو …)، اختر «التطبيقات»، ثم «تثبيت نظام ملحمة ياغي».',
    backupBody: 'هل المحل محمي، وهل كل شيء يعمل.',
    backupAutomatic: 'النسخ الاحتياطي التلقائي',
    backupFrequency: 'التكرار',
    backupEveryFifteen: 'كل 15 دقيقة',
    backupDestination: 'الوجهة',
    backupKept: 'النسخ المحفوظة',
    backupKeptValue: 'آخر 4 نسخ احتياطية ناجحة',
    backupLast: 'آخر نسخة احتياطية ناجحة',
    backupNext: 'النسخة الاحتياطية التالية',
    backupDirectory: 'المجلّد',
    backupWritable: 'جاهز',
    backupNotWritable: 'غير قابل للكتابة، سيفشل النسخ',
    backupUntested: 'لم يُختبر بعد',
    backupFailures: 'النسخ الفاشلة',
    backupNone: 'لا توجد وجهة معدّة بعد، فلا يُنسخ أي شيء.',
    backupNow: 'إنشاء نسخة احتياطية الآن',
    backupStarting: 'جارٍ البدء…',
    backupRunning: 'جارٍ النسخ…',
    backupInProgress: 'يوجد نسخ احتياطي قيد التنفيذ حاليًا',
    backupStarted: 'بدأ النسخ الاحتياطي.',
    backupSucceeded: 'انتهى النسخ الاحتياطي.',
    backupSavedOneDrive: 'تم حفظ النسخة الاحتياطية بنجاح في مجلد مزامنة OneDrive.',
    backupFailed: 'لم يكتمل النسخ الاحتياطي. النسخ السابقة لم تُمس.',
    backupSkipped: 'يوجد نسخ احتياطي قيد التنفيذ. لم تبدأ هذه النسخة.',
    backupCleanupWarning:
      'النسخة الاحتياطية سليمة، لكن تعذّر حذف نسخة أقدم. هي تشغل مساحة فقط.',
    backupHistory: 'سجل النسخ وتعليمات الاستعادة',
    backupNoPermission: 'لا يمكنك عرض حالة النسخ. اطلب من المالك.',
    backupNoSecrets:
      'لا تُعرض هنا كلمة مرور قاعدة البيانات ولا سلسلة الاتصال، ولا تُرسل إلى هذه الشاشة.',
    backupHealthTitle: 'حالة النظام',
    backupHealthNote:
      'سلامة قاعدة البيانات لا تعني أن النسخ الاحتياطي يعمل. هذان سؤالان مختلفان ويُجاب عنهما بشكل منفصل.',
    backupFolderStatus: 'مجلّد النسخ الاحتياطي',
    healthApi: 'الخادم',
    healthDatabase: 'قاعدة البيانات',
    version: 'الإصدار',
    restoreDocs: 'استعادة نسخة',
    restoreDocsValue: 'راجع docs/backup-and-restore.md',

    notificationsTitle: 'التنبيهات',
    notificationsBody: 'أي تنبيهات يرفعها الجرس. إيقاف أحدها يمسح أيضاً ما رفعه سابقاً.',
  },

  offline: {
    online: 'متصل',
    offline: 'غير متصل',
    offlineDetail: 'لا يوجد اتصال — يتم حفظ المبيعات على هذا الجهاز',
    syncing: 'جارٍ إرسال المبيعات…',
    syncNow: 'أرسل الآن',
    waiting: '{count} بانتظار الإرسال',
    waitingItem: 'بانتظار الإرسال',
    someFailed: 'تعذّر إرسال {count}',
    openQueue: 'عرض',

    queueTitle: 'مبيعات بانتظار الإرسال',
    queueSubtitle: 'مبيعات سُجّلت بينما كان هذا الجهاز بلا اتصال.',
    emptyQueue: 'لا شيء بالانتظار.',
    emptyQueueHint: 'كل بيع سُجّل على هذا الجهاز وصل إلى الخادم.',
    allHandedOver: 'وصل كل شيء إلى الخادم.',
    safetyNote:
      'يُحفظ البيع على هذا الجهاز فور إتمامه. إرساله إلى الخادم خطوة منفصلة، ولا يُحذف أي بيع من هذه القائمة قبل أن يؤكد الخادم استلامه.',

    itemCount: '{count} أصناف',
    attempts: 'المحاولات: {count}',
    retry: 'أعد المحاولة',
    retryAll: 'أعد محاولة الـ {count} جميعاً',
    lastSync: 'آخر إرسال {when} — سُجّل {synced}، و{duplicates} كان موجوداً',

    discard: 'إزالة من القائمة',
    discardConfirm: 'إزالة هذا البيع دون تسجيله؟',
    discardWarning:
      'الزبون دفع فعلاً. إزالته هنا تعني ألا يبقى للمحل أي سجل لهذا البيع. لا تفعل هذا إلا إذا كان تسجيله مستحيلاً.',
    discardYes: 'أزله على أي حال',

    receiptPrinted: 'أُرسلت الفاتورة {number} إلى الطابعة.',
    receiptNotPrinted: 'تعذّر إرسال الفاتورة إلى الطابعة. يمكن طباعتها من المبيعات بعد عودة الاتصال.',
    noReceiptNumber: 'لا يحمل هذا الجهاز رقم فاتورة محجوزاً، فلم تُطبع فاتورة. يمكن طباعتها من المبيعات بعد عودة الاتصال.',

    updateTitle: 'يوجد إصدار جديد',
    updateBody: 'سيُثبَّت عند إعادة التحميل. أنهِ ما تعمل عليه أولاً.',
    updateNow: 'أعد التحميل الآن',
    updateLater: 'لاحقاً',

    catalogueAge: 'آخر تحديث للأسعار {when}',
    catalogueMissing: 'لا توجد قائمة منتجات محفوظة على هذا الجهاز، لذا لا يمكن البيع دون اتصال.',
    catalogueSaved: 'تم حفظ قائمة المنتجات للاستخدام دون اتصال.',
    savingCatalogue: 'جارٍ حفظ قائمة المنتجات…',
    saveCatalogue: 'احفظ للاستخدام دون اتصال',

    newOrder: 'الطلب التالي',
    soldOffline: 'محفوظ على هذا الجهاز',
    soldOfflineBody: 'البيع مسجّل هنا وسيُرسل إلى الخادم فور عودة الاتصال.',
    noShiftOffline: 'افتح وردية قبل البيع. لا يمكن فتح وردية بدون اتصال.',
    cannotOffline: 'هذا المتصفح لا يستطيع حفظ المبيعات دون اتصال، لذا يحتاج البيع إلى اتصال.',
  },

  scale: {
    title: 'الميزان',
    manual: 'أدخل الوزن',
    manualHint: 'متاح دائماً. اكتب الوزن كما يظهر على شاشة الميزان.',
    connect: 'وصّل ميزاناً',
    connected: 'الميزان متصل',
    disconnect: 'فصل',
    reading: 'جارٍ القراءة…',
    stable: 'ثابت',
    unstable: 'لم يستقر بعد',
    lastReading: 'آخر قراءة',
    use: 'استخدم هذا الوزن',
    notSupported: 'هذا المتصفح لا يستطيع التواصل مع الميزان مباشرة.',
    notSupportedBody:
      'اكتب الوزن من شاشة الميزان بدلاً من ذلك. هكذا تعمل معظم المحلات، ولا شيء يُفقد بذلك.',
    permissionNeeded: 'اختر منفذ الميزان عندما يسألك المتصفح.',
    failed: 'تعذّرت قراءة الميزان. اكتب الوزن يدوياً.',
    noScale: 'لا يوجد ميزان متصل.',
    methods: 'طرق توصيل الموازين',
  },

  labels: {
    title: 'ملصقات المنتجات',
    subtitle: 'اطبع ملصقاً يحمل باركود وسعراً وتواريخ.',
    product: 'المنتج',
    choose: 'اختر منتجاً',
    weight: 'الوزن',
    packedOn: 'تاريخ التعبئة',
    expiresOn: 'تاريخ الانتهاء',
    shelfLifeDays: 'مدة الصلاحية (أيام)',
    price: 'السعر',
    total: 'الإجمالي',
    copies: 'عدد النسخ',
    codeType: 'الرمز',
    barcode: 'باركود',
    qr: 'رمز QR',
    size: 'حجم الملصق',
    preview: 'معاينة',
    print: 'اطبع الملصقات',
    noProduct: 'اختر منتجاً لعرض الملصق.',
    noBarcode: 'هذا المنتج بلا باركود، لذا يُستخدم رمز المنتج بدلاً منه.',
    printedBy: 'طباعة: {name}',
  },
};

export type Translations = typeof en;
export const dictionaries = { en, ar } as const;
